import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { isPublicTutorialRoute } from '../publicRoute';

// Self-contained i18n for the standalone NoticeIA app. Holds only the notice.*
// UI strings (plus the two theme toggle labels used by the shared sidebar),
// extracted from the former Lumna LanguageContext. FR/EN are wired to the
// LangToggle; ES exists for the Google sign-in button locale.
type Language = 'en' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

// EVERY STRING ONCE, in both languages at the same time.
//
// This was two dictionaries, `en` and `fr`, five hundred lines apart. Adding a string
// meant remembering to add it twice, in two places nothing connected, and forgetting the
// second half produced no error at all: `t()` fell through to the key, so a French page
// quietly showed `notice.parts.estimate` and only a French reader would ever notice.
//
// One entry, two values, and the TYPE is what makes it work: a tuple of exactly two
// strings, so a missing translation is a compile error at the line where it is missing.
// That is the whole argument for the shape. It also halves the file, which is the smaller
// half of the benefit.
//
// [English, French], in that order, everywhere.
const translations: Record<string, readonly [string, string]> = {
  'notice.nav.admin': ['Admin', 'Admin'],
  'notice.admin.costsQuotas': ['Costs & Quotas', 'Coûts & Quotas'],
  'notice.analytics.title': ['Codebase Analytics', 'Analytique du code'],
  'notice.analytics.sloc': ['Source lines of code', 'Lignes de code source'],
  'notice.analytics.loc': ['Physical lines', 'Lignes physiques'],
  'notice.analytics.cloc': ['Comments and docstrings', 'Commentaires et docstrings'],
  'notice.analytics.blankShort': ['Blank', 'Vides'],
  'notice.analytics.blank': ['Empty lines', 'Lignes vides'],
  'notice.analytics.byLang': ['by language', 'par langage'],
  'notice.analytics.loadError': [
    'Could not load analytics.',
    'Impossible de charger les analyses.',
  ],
  'notice.users.title': ['Authorized users', 'Utilisateurs autorisés'],
  // ONE HEADING PER APP. The demo and the sandbox are separate apps with separate
  // account pools, and these three tables sit one above the other: the heading is the
  // only thing that says which list a row is in, so it names the app rather than
  // repeating "authorized".
  'notice.users.titleProd': ['Authorized users on prod', 'Utilisateurs autorisés en prod'],
  'notice.users.titleDemo': ['Authorized users on demo', 'Utilisateurs autorisés sur la démo'],
  'notice.users.titleSandbox': [
    'Authorized users on sandbox',
    'Utilisateurs autorisés sur le bac à sable',
  ],
  'notice.users.envOffline': [
    'That database is not reachable — is the app installed on this server?',
    "Cette base est injoignable — l'application est-elle installée sur ce serveur ?",
  ],
  // Only prod administers the other two. On a copy this page is prod's build, so it
  // renders all three sections and two of them have nothing to say.
  'notice.users.envNotHere': [
    'Managed from prod, not from here.',
    'Géré depuis la prod, pas depuis ici.',
  ],
  'notice.users.loadError': [
    'Could not load the user list.',
    'Impossible de charger la liste des utilisateurs.',
  ],
  'notice.users.empty': [
    'No one is authorized yet.',
    'Aucun utilisateur autorisé pour l\'instant.',
  ],
  'notice.users.user': ['User', 'Utilisateur'],
  'notice.users.joined': ['Added', 'Ajouté le'],
  'notice.users.lastSeen': ['Last seen', 'Vu pour la dernière fois'],
  'notice.users.ownerBadge': ['Owner', 'Propriétaire'],
  'notice.users.noName': ['No name', 'Sans nom'],
  'notice.users.pending': ['Never signed in', 'Jamais connecté'],
  'notice.users.add': ['Add a user', 'Ajouter un utilisateur'],
  'notice.users.addTitle': ['Authorize a Google account', 'Autoriser un compte Google'],
  'notice.users.addHint': [
    'This address will be able to sign in to NoticeIA, with full admin access.',
    'Cette adresse pourra se connecter à NoticeIA, avec un accès admin complet.',
  ],
  'notice.users.email': ['Email', 'Adresse e-mail'],
  'notice.users.addSubmit': ['Authorize', 'Autoriser'],
  'notice.users.addError': [
    'Could not authorize this address.',
    'Impossible d\'autoriser cette adresse.',
  ],
  'notice.users.invalidEmail': [
    'Enter a valid email address.',
    'Saisissez une adresse e-mail valide.',
  ],
  'notice.users.duplicate': [
    'This address is already authorized.',
    'Cette adresse est déjà autorisée.',
  ],
  'notice.users.remove': ['Revoke access', 'Révoquer l\'accès'],
  // The sandbox's own switch: suspended, not revoked. Said as what pressing it does.
  'notice.users.disable': ['Suspend access', 'Suspendre l\'accès'],
  'notice.users.enable': ['Restore access', 'Rétablir l\'accès'],
  'notice.users.disabled': ['Suspended', 'Suspendu'],
  'notice.users.toggleError': [
    'That account could not be updated.',
    "Ce compte n'a pas pu être mis à jour.",
  ],
  'notice.users.removeConfirm.title': ['Revoke access?', 'Révoquer l\'accès ?'],
  'notice.users.removeConfirm.body': [
    '"{email}" will no longer be able to sign in to NoticeIA. Their manuals are kept.',
    '« {email} » ne pourra plus se connecter à NoticeIA. Ses notices sont conservées.',
  ],
  'notice.users.removeError': [
    'Could not revoke this user.',
    'Impossible de révoquer cet utilisateur.',
  ],
  // The Admin page's Transcriptions tab (panels/admin/TranscriptsSection.tsx).
  'notice.transcripts.tab': ['Transcriptions', 'Transcriptions'],
  'notice.transcripts.title': ['Meetings', 'Réunions'],
  'notice.transcripts.new': ['Transcribe a meeting', 'Transcrire une réunion'],
  'notice.transcripts.pick': ['Add a recording', 'Ajouter un enregistrement'],
  'notice.transcripts.titleField': ['Title', 'Titre'],
  'notice.transcripts.language': ['Language', 'Langue'],
  'notice.transcripts.languageAuto': ['Detect', 'Détecter'],
  'notice.transcripts.speakers': ['Number of speakers', "Nombre d'intervenants"],
  'notice.transcripts.speakersUnknown': ['Unknown', 'Inconnu'],
  'notice.transcripts.prompt': ['Names and terms to spell right', 'Noms et termes à bien orthographier'],
  'notice.transcripts.promptHint': ['e.g. NoticeIA, Lumna, Louis', 'ex. NoticeIA, Lumna, Louis'],
  'notice.transcripts.upload': ['Upload', 'Envoyer'],
  'notice.transcripts.uploadError': ['The upload failed.', "L'envoi a échoué."],
  'notice.transcripts.loadError': ['Could not load the meetings.', 'Impossible de charger les réunions.'],
  'notice.transcripts.saveError': ['Could not save the names.', "Impossible d'enregistrer les noms."],
  'notice.transcripts.requeueError': ['Could not queue it again.', 'Impossible de la relancer.'],
  'notice.transcripts.deleteError': ['Could not delete it.', 'Impossible de la supprimer.'],
  'notice.transcripts.workerOn': ['Transcriber on', 'Transcripteur actif'],
  'notice.transcripts.workerOff': ['Transcriber off', 'Transcripteur éteint'],
  'notice.transcripts.workerNever': ['No transcriber yet', 'Aucun transcripteur'],
  'notice.transcripts.empty': ['No meetings yet.', "Aucune réunion pour l'instant."],
  'notice.transcripts.meeting': ['Meeting', 'Réunion'],
  'notice.transcripts.added': ['Added', 'Ajoutée'],
  'notice.transcripts.length': ['Length', 'Durée'],
  'notice.transcripts.status': ['Status', 'Statut'],
  'notice.transcripts.status.uploading': ['Uploading', 'Envoi'],
  'notice.transcripts.status.queued': ['Waiting', 'En attente'],
  'notice.transcripts.status.running': ['Transcribing', 'Transcription'],
  'notice.transcripts.status.done': ['Done', 'Terminée'],
  'notice.transcripts.status.failed': ['Failed', 'Échec'],
  'notice.transcripts.requeue': ['Transcribe again', 'Relancer la transcription'],
  'notice.transcripts.delete': ['Delete', 'Supprimer'],
  'notice.transcripts.deleteConfirm.title': ['Delete this meeting?', 'Supprimer cette réunion ?'],
  'notice.transcripts.deleteConfirm.body': [
    '"{title}", its recording and its transcript will be deleted.',
    "« {title} », son enregistrement et sa transcription seront supprimés.",
  ],
  'notice.transcripts.speakerWord': ['Speaker', 'Intervenant'],
  'notice.transcripts.nameSpeakers': ['Who is who', 'Qui est qui'],
  'notice.transcripts.back': ['Back', 'Retour'],
  'notice.transcripts.copy': ['Copy', 'Copier'],
  'notice.transcripts.listen': ['Listen', 'Écouter'],
  'notice.transcripts.stop': ['Stop', 'Arrêter'],
  'notice.transcripts.keyGenerate': ['Generate a transcriber key', 'Générer une clé de transcripteur'],
  'notice.transcripts.keyHave': ['Key …{preview}', 'Clé …{preview}'],
  'notice.transcripts.keyReplace': ['Replace', 'Remplacer'],
  'notice.transcripts.keyRevoke': ['Revoke', 'Révoquer'],
  'notice.transcripts.keyOnce': [
    'Your transcriber key — copy it now, it will not be shown again.',
    "Votre clé de transcripteur — copiez-la maintenant, elle ne sera plus affichée.",
  ],
  'notice.transcripts.keyRun': [
    'On the machine that transcribes, in NoticeIA/transcriber:',
    'Sur la machine qui transcrit, dans NoticeIA/transcriber :',
  ],
  'notice.transcripts.keyError': ['Could not change the key.', 'Impossible de modifier la clé.'],
  'notice.transcripts.keyRevokeConfirm.title': ['Revoke the transcriber key?', 'Révoquer la clé du transcripteur ?'],
  'notice.transcripts.keyRevokeConfirm.body': [
    'A transcriber using it stops at its next call, until it is given a new key.',
    "Un transcripteur qui l'utilise s'arrête à son prochain appel, jusqu'à ce qu'on lui donne une nouvelle clé.",
  ],
  'notice.home.welcome': ['Welcome to NoticeIA!', 'Bienvenue sur NoticeIA !'],
  'notice.nav.viewer': ['Manuals', 'Notices'],
  'notice.nav.catalog': ['Database', 'Base de données'],
  // The sandbox's own tab (IS_SANDBOX): a screen per phase, kept across resets.
  'notice.nav.saves': ['Saves', 'Sauvegardes'],
  'notice.catalog.title': ['Parts database & Product photos', 'Base de pièces & Photos produit'],
  'notice.catalog.partsDb': ['Product & parts database', 'Base de produits & pièces'],
  'notice.catalog.filterSubmodel': ['Filter by submodel', 'Filtrer par sous-modèle'],
  'notice.catalog.allSubmodels': ['All submodels', 'Tous les sous-modèles'],
  'notice.catalog.productPhotos': ['Product photos', 'Photos produit'],
  'notice.catalog.allNotices': ['All manuals', 'Toutes les notices'],
  'notice.catalog.filterNotice': ['Filter by manual', 'Filtrer par notice'],
  'notice.catalog.back': ['Back to the manual', 'Retour à la notice'],
  'notice.catalog.empty': ['The database is empty.', 'La base est vide.'],
  // The database's 3D models could not be listed: a failure, not "no product" (see ProductModelsTable).
  'notice.catalog.modelsFailed': [
    'The 3D models could not be loaded. Reload the page to try again.',
    'Les modèles 3D n’ont pas pu être chargés. Rechargez la page pour réessayer.',
  ],
  'notice.catalog.id': ['ID', 'ID'],
  'notice.catalog.nameEn': ['Name (EN)', 'Nom (EN)'],
  'notice.catalog.nameFr': ['Name (FR)', 'Nom (FR)'],
  'notice.catalog.partName': ['Part name', 'Nom de la pièce'],
  'notice.catalog.category': ['Category', 'Catégorie'],
  'notice.catalog.categoryPlaceholder': ['e.g. Chairs, Hardware', 'ex. Chaises, Quincaillerie'],
  'notice.catalog.newCategory': ['+ New category...', '+ Nouvelle catégorie...'],
  'notice.catalog.addCategory': ['Add category', 'Ajouter une catégorie'],
  'notice.catalog.editCategory': ['Edit category', 'Modifier la catégorie'],
  'notice.catalog.editItem': ['Edit this part', 'Modifier cette pièce'],
  'notice.catalog.emptyDb': ['Empty the database', 'Vider la base'],
  'notice.catalog.emptyingDb': ['Emptying…', 'Suppression…'],
  'notice.catalog.emptyDbConfirm': ['Empty the parts database?', 'Vider la base de pièces ?'],
  'notice.catalog.emptyDbBody': [
    'All {n} part(s) will be deleted, with their drawings. The categories are kept. This cannot be undone.',
    'Les {n} pièce(s) seront supprimées, avec leurs schémas. Les catégories sont conservées. Cette action est irréversible.',
  ],
  'notice.catalog.emptyPhotosConfirm': ['Empty the photo database?', 'Vider la base de photos ?'],
  'notice.catalog.emptyPhotosBody': [
    'All {n} product photo(s), across every manual, will be deleted. This cannot be undone.',
    'Les {n} photo(s) produit, toutes notices confondues, seront supprimées. Cette action est irréversible.',
  ],
  'notice.catalog.schematicUnknownSource': ['Source unknown', 'Source inconnue'],
  'notice.catalog.deleteSchematic': ['Delete this drawing', 'Supprimer ce schéma'],
  'notice.catalog.deletePhoto': ['Delete this photo', 'Supprimer cette photo'],
  'notice.catalog.photoModel': ['Generated by:', 'Généré par :'],
  'notice.catalog.contextTitle': ['Context given', 'Contexte fourni'],
  'notice.catalog.deleteItem': ['Delete', 'Supprimer'],
  'notice.catalog.deleteItemConfirm': ['Delete this part?', 'Supprimer cette pièce ?'],
  'notice.catalog.catNameEn': ['English name', 'Nom anglais'],
  'notice.catalog.catNameFr': ['French name', 'Nom français'],
  'notice.catalog.duplicateName': ['That category already exists.', 'Cette catégorie existe déjà.'],
  'notice.catalog.definition': ['Definition (for the AI)', 'Définition (pour l’IA)'],
  'notice.catalog.catParent': ['Inside', 'Dans'],
  'notice.catalog.catNoParent': ['— top level —', '— premier niveau —'],
  'notice.catalog.definitionHint': [
    'How to tell a part of this category from the others. Only the classifier reads it.',
    'Ce qui distingue une pièce de cette catégorie des autres. Seul le classifieur la lit.',
  ],
  'notice.catalog.deleteCategory': ['Delete category', 'Supprimer la catégorie'],
  'notice.catalog.deleteCategoryConfirm': ['Delete?', 'Supprimer ?'],
  'notice.catalog.deleteCategoryItems': [
    'Delete? {n} item(s) move to Uncategorized',
    'Supprimer ? {n} article(s) iront dans Sans catégorie',
  ],
  'notice.catalog.uncategorized': ['Uncategorized', 'Sans catégorie'],
  'notice.catalog.schematic': ['Schematic', 'Schéma'],
  'notice.catalog.photosReal': ['Real images', 'Images réelles'],
  'notice.catalog.models3d': ['3D models', 'Modèles 3D'],
  'notice.catalog.product': ['Product', 'Produit'],
  'notice.catalog.submodel': ['Submodel', 'Sous-modèle'],
  'notice.catalog.dbImagesTitle': ['Generated images in the database', 'Images générées en base'],
  'notice.parts.addToDb': ['Add to database', 'Ajouter à la base'],
  'notice.catalog.addTitle': ['Add to parts database', 'Ajouter à la base de pièces'],
  'notice.catalog.type': ['Type', 'Type'],
  'notice.catalog.save': ['Save', 'Enregistrer'],
  'notice.catalog.cancel': ['Cancel', 'Annuler'],
  'notice.catalog.saving': ['Saving...', 'Enregistrement...'],
  'notice.catalog.saveError': ['Could not save.', "Échec de l'enregistrement."],
  'notice.pricing.serper': [
    'Real-image search runs on Serper (Google Images), billed in credits, not Gemini tokens.',
    'La recherche d\'images réelles utilise Serper (Google Images), facturé en crédits et non en tokens Gemini.',
  ],
  'notice.pricing.genImage': [
    'Image generation (the main option) bills per generated 1K image — about $0.067 (Nano Banana 2) or $0.134 (Nano Banana Pro), per the model chosen in Phase 2.',
    'La génération d\'images (l\'option principale) est facturée par image 1K générée — environ 0,067 $ (Nano Banana 2) ou 0,134 $ (Nano Banana Pro), selon le modèle choisi à l\'étape 4.',
  ],
  'notice.pricing.serperCredits': ['Serper credits', 'Crédits Serper'],
  'notice.pricing.serperCost': [
    'Each image search costs 1 Serper credit. Requests returning 20+ results cost 2.',
    'Chaque recherche d\'images coûte 1 crédit Serper. Les requêtes renvoyant plus de 20 résultats en coûtent 2.',
  ],
  'notice.pricing.totalSpent': ['Total spent', 'Total dépensé'],
  'notice.pricing.tarifs': ['Costs', 'Coûts'],
  'notice.pricing.quotas': ['Quotas', 'Quotas'],
  'notice.pricing.rateLimitLink': ['Gemini rate limits & usage', 'Limites et usage Gemini'],
  'notice.pricing.billingLink': ['Google Cloud billing', 'Facturation Google Cloud'],
  'notice.pricing.quotaNote': [
    'Gemini quota is per project and shared with anything else using the key, so the real numbers live at Google.',
    'Le quota Gemini est par projet et partagé avec tout ce qui utilise la clé : les vrais chiffres sont chez Google.',
  ],
  'notice.upload': ['Upload PDF', 'Importer un PDF'],
  'notice.viewer.gone': [
    'This document is no longer in your library. Click to upload a new one.',
    "Ce document n'est plus dans ta bibliothèque. Clique pour en importer un nouveau.",
  ],
  'notice.viewer.onlyPdf': [
    'Only PDF files are supported for now.',
    'Seuls les fichiers PDF sont pris en charge pour le moment.',
  ],
  'notice.pdf.page': ['Page', 'Page'],
  'notice.pdf.of': ['of', 'sur'],
  'notice.pdf.openError': ['Could not open this PDF.', "Impossible d'ouvrir ce PDF."],
  'notice.pdf.zoom': ['Click to zoom', 'Cliquer pour agrandir'],
  'notice.pdf.prev': ['Previous page', 'Page précédente'],
  'notice.pdf.next': ['Next page', 'Page suivante'],
  'notice.pdf.close': ['Close', 'Fermer'],
  // Asked for by the library panel while it waits, and never defined — so a reader saw the
  // key itself. `t` answers with the key when it has no string, which is the right way
  // round for finding one missing and the wrong thing to leave on screen.
  'common.loading': ['Loading…', 'Chargement…'],
  'notice.step': ['Step', 'Étape'],
  'notice.phase': ['Phase', 'Phase'],
  'notice.step.sep': [': ', ' : '],
  // What each phase does, shown in the "i" beside its title. One key per PHASE and never
  // a number in the text: the heading is numbered from the phase's position, so a number
  // written into the copy is a second answer to the same question and the one that goes
  // stale the first time a phase moves.
  'notice.phase.info.sections': [
    'Upload the assembly manual as a PDF, with the product’s web listings if there are any. Then extract the product information (brand, name, category, assembly time, etc.) and classify each page of the manual into sections: summary, required tools, supplied parts, assembly step N, safety, miscellaneous links.',
    'Charger la notice de montage en PDF, avec les annonces du produit s’il y en a. Puis extraire les informations du produit (marque, nom, catégorie, temps de montage…) et classer chaque page de la notice par section : sommaire, outils nécessaires, matériel fourni, assemblage étape N, sécurité, liens divers.',
  ],
  'notice.phase.info.partsModels': [
    'List every part supplied in the manual. They are often grouped in bags, and each part has a reference number. Then obtain a real image of each one, either by searching for a reference image (brand + reference number) or by generating it from the manual’s drawing, and reconstruct a 3D mesh of the part from those images.',
    'Lister l’ensemble des pièces fournies dans la notice. Elles sont souvent groupées par sachets, et chaque pièce a un numéro de référence. Obtenir ensuite une vraie image de chacune, soit par recherche d’une image de référence (marque + numéro de référence), soit en la générant à partir du croquis de la notice, puis reconstruire un maillage 3D de la pièce à partir de ces images.',
  ],
  // The mesh half of what used to be one phase — see viewerPipelines, where the split is
  // argued. The words are the old paragraph's last clause, given the phase it now is.
  // THE PHASE, WHICH IS TWO SUBPHASES — what it is made of, then the meshes made of it.
  'notice.phase.info.materials': [
    'Judge how the listing\u2019s materials look in its photographs \u2014 the colour as it appears, how rough, whether bare metal \u2014 and which one each component part is made of, with close-ups of the parts. Then measure each material off the parts made of it, inside their outlines from Phase 2 \u2014 how coarse the grain is, how directional, how often it repeats \u2014 which is what every 3D model of Phase 5 is built and grained with. Read once per colourway, not once per model. The hardware\u2019s materials, which no photograph shows, are set by hand and their surfaces asked for separately.',
    'Juger l\u2019aspect des mat\u00e9riaux de l\u2019annonce sur ses photos \u2014 la couleur telle qu\u2019elle appara\u00eet, la rugosit\u00e9, s\u2019il s\u2019agit de m\u00e9tal nu \u2014 et de quel mat\u00e9riau chaque pi\u00e8ce est faite, avec des gros plans des pi\u00e8ces. Puis mesurer chaque mat\u00e9riau sur les pi\u00e8ces qui en sont faites, \u00e0 l\u2019int\u00e9rieur de leurs contours de la phase 2 \u2014 finesse du grain, orientation, p\u00e9riode \u2014 ce avec quoi chaque mod\u00e8le 3D de la phase 5 est construit et vein\u00e9. Lu une fois par coloris, pas une fois par mod\u00e8le. Les mat\u00e9riaux de la quincaillerie, qu\u2019aucune photo ne montre, sont fix\u00e9s \u00e0 la main et leurs surfaces demand\u00e9es \u00e0 part.',
  ],
  'notice.phase.info.partModels': [
    'Reconstruct a 3D mesh of each part, from the photographs obtained in the parts phase — generated from them, uploaded as a supplier’s CAD file, or turned from a profile. Read off the sub-steps which pieces of hardware work together, which the hardware is built from and the holes and covers are placed from. The mesh switched on here is the one the assembly draws.',
    'Reconstruire un maillage 3D de chaque pièce, à partir des photos obtenues dans la phase des pièces — généré depuis celles-ci, importé comme fichier CAO du fabricant, ou tourné à partir d’un profil. Lire dans les sous-étapes quelles pièces de quincaillerie fonctionnent ensemble : la quincaillerie est construite d’après cela, et les trous et les caches en découlent. Le maillage activé ici est celui que l’assemblage dessine.',
  ],
  'notice.phase.info.steps': [
    'Each assembly step as a whole: which supplied parts it adds and how many, the tools it calls for, what it does, and the text it prints — one reading of the whole manual, since four legs supplied means the steps between them use four. Then each step broken into sub-steps, one gesture each: what it joins, with which parts and tools, and the piece it leaves.',
    'Chaque étape de montage dans son ensemble : quelles pièces fournies elle ajoute et en quelle quantité, les outils qu’elle demande, ce qu’elle fait, et le texte qu’elle imprime — une lecture de toute la notice, puisque quatre pieds fournis signifient que les étapes en utilisent quatre. Puis chaque étape décomposée en sous-étapes, un geste chacune : ce qu’elle assemble, avec quelles pièces et quels outils, et la pièce qu’elle laisse.',
  ],
  'notice.phase.info.assembly': [
    'Say how the parts actually go together: pick the part that stays still and the part that goes onto it, then the direction it arrives from, how far, and — for a nut or a screw — the thread pitch, so the turn follows the travel instead of being animated beside it. Nothing is keyframed: the movement is computed from these numbers, which is why it can be scrubbed in both directions.',
    "Décrire comment les pièces s'assemblent : choisir la pièce fixe et celle qui vient dessus, puis la direction d'arrivée, la distance, et — pour un écrou ou une vis — le pas de filetage, afin que la rotation découle du déplacement au lieu d'être animée à côté. Rien n'est en images clés : le mouvement est calculé à partir de ces valeurs, ce qui permet de le parcourir dans les deux sens.",
  ],
  // The same phase in the 3D pipeline, which has no assembly render to publish yet. It
  // says so: the page is real and the screens are empty, and a description that promised
  // an animated assembly would be describing the phase that comes after this one.
  'notice.phase.info.publishModels': [
    'Publish the manual as a public tutorial page, reachable by a QR code. The assembly is not rendered yet, so every step shows its instructions above an empty frame.',
    "Publier la notice sur une page tutoriel publique, accessible par un QR code. L'assemblage n'est pas encore rendu : chaque étape affiche ses instructions au-dessus d'un cadre vide.",
  ],
  'notice.runAll': ['New run', 'Nouveau run'],
  'notice.pause': ['Pause', 'Pause'],
  'notice.pausing': ['Finishing the calls in flight…', 'Fin des appels en cours…'],
  'notice.pauseHint': [
    'Stop launching new calls; keep the answers already paid for',
    'Ne plus lancer d\'appels ; garder les réponses déjà payées',
  ],
  'notice.resume': ['Resume', 'Reprendre'],
  'notice.step1.title': ['Upload, info & sections', 'Chargement, infos & sections'],
  'notice.parts.phaseTitle': ['Product & parts', 'Produit & pièces'],
  'notice.parts.tableTitle': ['Parts', 'Pièces'],
  'notice.parts.allParts': ['All parts', 'Toutes les pièces'],
  // The header of the scale table under a zoomed photograph: every part's known dimension
  // divided by the pixels its box spans, which on one picture should agree.
  'notice.parts.mmPerPx': ['mm per pixel', 'mm par pixel'],
  // The mean of the rows above it: this photograph's own scale, per axis.
  'notice.parts.mmPerPxMean': ['mean', 'moyenne'],
  // HOW FAR FROM THE CAMERA a box is, said in its label on the picture. The finding call's
  // own word — see ListingCrop.depth — and the reason a scale read off a 'far' box is not
  // comparable with the rest of them.
  'notice.parts.depth.near': ['near', 'proche'],
  'notice.parts.depth.far': ['far', 'loin'],
  'notice.parts.depth.spans': ['front to back', "d'avant en arrière"],
  'notice.parts.estimateFailed': [
    'The sizes could not be estimated.',
    "Les tailles n'ont pas pu être estimées.",
  ],
  'notice.parts.estimatePrompt': ['The estimate prompt', "Le prompt de l'estimation"],
  // A component's estimate asks nobody: it is the picture's scale times the part's box. So its
  // first panel is the working, not a prompt.
  'notice.parts.estimateHow': ['How it is worked out', "Le calcul"],
  // Shared by every prompt badge (see PromptBadge): what a call would ask, on hover.
  'notice.parts.promptLoading': ['Building the prompt…', 'Construction du prompt…'],
  'notice.parts.promptFailed': [
    'The prompt could not be built.',
    "Le prompt n'a pas pu être construit.",
  ],
  'notice.parts.estimateThinking': [
    'What the model thought when estimating',
    "Le raisonnement du modèle pendant l'estimation",
  ],
  'notice.merged.substeps': ['Substeps', 'Sous-étapes'],
  'notice.instr.run': ['Extract the instructions', 'Extraire les instructions'],
  'notice.merged.title': ['Steps & substeps', 'Étapes & sous-étapes'],
  // The steps badge's Run, and the three things it can run (see StepsBadge).
  'notice.steps.runSteps': ['Run steps & substeps', 'Lancer les étapes & sous-étapes'],
  // See refileStep: the fittings moved with their gestures, the pieces did not.
  'notice.steps.refileFailed': [
    'The sub-steps were cut again, and their Phase 6 pieces could not be moved with them. Run this step again to retry.',
    'Les sous-étapes ont été redécoupées, et leurs pièces de la Phase 6 n’ont pas pu suivre. Relancez cette étape pour réessayer.',
  ],
  'notice.steps.runEverything': ['Everything', 'Tout'],
  'notice.steps.runOverview': ['Steps only', 'Étapes seulement'],
  'notice.steps.runAnalysis': ['Sub-steps only', 'Sous-étapes seulement'],
  // Not "wait" and not an error: the work this button offers is already being done, by
  // another tab or another admin of this shared library, and the count beside it is theirs.
  'notice.steps.runElsewhere': ['Being analyzed elsewhere', 'Analyse en cours ailleurs'],
  // On the badge beside that button. It names the STEP because the badge can only ever be
  // one step's — the button runs them all, and a prompt is written per step — so a label
  // saying "the prompt" would be a claim about the run rather than about what is on screen.
  'notice.steps.promptLabel': [
    'Prompt & images sent to analyze step {n}',
    'Prompt & images envoyés pour analyser l’étape {n}',
  ],
  // PHASE 4'S SECOND CALL. "Find" and not "infer": the holes are printed in the manual, in the
  // sense that every fastener going into a part is one — the call reads them off the gestures
  // rather than working them out from a mesh, which is where they used to be looked for.
  // PHASE 4'S TWO SUBPHASES, and the hardware table's call — see HardwareTable.
  'notice.steps.subOverview': ['Steps', 'Étapes'],
  'notice.steps.subSteps': ['Sub-steps', 'Sous-étapes'],
  'notice.steps.subRules': ['Expert rules', 'Règles expertes'],
  'notice.rules.none': ['No rule yet.', 'Aucune règle pour l\u2019instant.'],
  'notice.rules.add': ['Add a rule', 'Ajouter une règle'],
  'notice.rules.on': ['On', 'Active'],
  'notice.rules.edit': ['Edit', 'Modifier'],
  'notice.rules.delete': ['Delete', 'Supprimer'],
  'notice.rules.titlePh': ['Name (e.g. Cross tightening)', 'Nom (ex. Serrage en croix)'],
  'notice.rules.bodyPh': ['What the rule says, and when it applies', 'Ce que dit la règle, et quand elle s\u2019applique'],
  'notice.rules.cancel': ['Cancel', 'Annuler'],
  'notice.rules.save': ['Save', 'Enregistrer'],
  'notice.steps.subHardware': ['Joints table', 'Table des joints'],
  'notice.hardwareTable.run': ['Find hardware interactions', 'Trouver les interactions de quincaillerie'],
  'notice.hardwareTable.promptLabel': [
    'Prompt & images sent to find the hardware interactions',
    'Prompt & images envoyés pour trouver les interactions de quincaillerie',
  ],
  'notice.hardwareTable.needSteps': [
    'Analyze the steps first: this reads their sub-steps.',
    'Analysez d’abord les étapes : cette lecture part de leurs sous-étapes.',
  ],
  'notice.hardwareTable.none': [
    'No two pieces of hardware work together in this manual.',
    'Aucune pièce de quincaillerie n’interagit avec une autre dans cette notice.',
  ],
  'notice.hardwareTable.notYet': [
    'Not looked for yet.',
    'Pas encore recherché.',
  ],
  // Beside a piece of hardware that caps another's hole, in the hardware table.
  'notice.hardwareTable.covers': ['covers', 'couvre'],
  'notice.hardwareTable.colHardware': ['Hardware', 'Quincaillerie'],
  'notice.hardwareTable.colHoles': ['Component holes', 'Trous des pièces'],
  'notice.hardwareTable.holeHardware': ['Hardware', 'Quincaillerie'],
  // HOW THE HARDWARE GOES INTO ITS HOLE, as the insertion table's call answers it — see insertionRun.
  'notice.hardwareAxis.title': ['Insertion table', 'Table d\'insertion'],
  'notice.drilling.title': ['Drilling table', 'Table de perçage'],
  'notice.drilling.diameter': ['Diameter (mm)', 'Diamètre (mm)'],
  'notice.drilling.depth': ['Depth (mm)', 'Profondeur (mm)'],
  'notice.drilling.crosses': ['crosses {id}', 'croise {id}'],
  'notice.drilling.parts': ['Part(s) & Face(s)', 'Pièce(s) & Face(s)'],
  'notice.hardwareAxis.part': ['Part', 'Pièce'],
  'notice.hardwareAxis.run': ['Fill the insertion table', 'Remplir la table d\'insertion'],
  'notice.hardwareAxis.noHardware': ['This manual lists no hardware part.', 'Cette notice ne liste aucune quincaillerie.'],
  'notice.hardwareAxis.unanswered': ['Not answered yet', 'Pas encore de réponse'],
  'notice.hardwareAxis.noHole': ['no hole', 'pas de trou'],
  'notice.hardwareAxis.inside': ['inside the parts', 'dans les pièces'],
  'notice.hardwareAxis.outside': ['outside the parts', 'hors des pièces'],
  'notice.hardwareAxis.dims': ['Dimensions (mm)', 'Dimensions (mm)'],
  'notice.hardwareAxis.axis': ['Goes in along', 'Entre selon'],
  'notice.hardwareAxis.axis.x': ['width', 'largeur'],
  'notice.hardwareAxis.axis.y': ['depth', 'profondeur'],
  'notice.hardwareAxis.axis.z': ['height', 'hauteur'],
  'notice.hardwareAxis.width': ['Hole diameter (mm)', 'Diamètre du trou (mm)'],
  'notice.hardwareAxis.length': ['Length (mm)', 'Longueur (mm)'],
  'notice.hardwareTable.holeIds': ['Ids', 'Ids'],
  'notice.hardwareTable.hole.through': ['through', 'traversant'],
  'notice.hardwareTable.hole.blind': ['blind', 'borgne'],
  'notice.hardwareTable.hole.cross': ['cross', 'transversal'],
  // A cross hole whose answer names no hole it meets (see TableHole.crosses).
  'notice.hardwareTable.crossUnlinkedTip': [
    'The table does not say which hole this crosses — run it again.',
    'Le tableau ne dit pas quel trou celui-ci croise — relancez-le.',
  ],
  // Marks a component the hardware goes all the way through.
  'notice.hardwareTable.colSubsteps': ['Substeps', 'Sous étapes'],
  'notice.hardwareTable.colHow': ['How they interact', 'Comment elles interagissent'],
  'notice.hardwareTable.noInteraction': ['No interaction', 'Aucune interaction'],
  // A HARDWARE SET: a row of that table built as one model — see HardwareSetStep.
  'notice.hardwareSet.title': ['Built together', 'Construites ensemble'],
  'notice.hardwareSet.none': ['Not built together yet.', 'Pas encore construites ensemble.'],
  // The set's runs could not be listed: a failure, not "not built" (see useSetRuns).
  'notice.hardwareSet.runsFailed': [
    'This set’s models could not be loaded. Reload the page to try again.',
    'Les modèles de cet ensemble n’ont pas pu être chargés. Rechargez la page pour réessayer.',
  ],
  'notice.hardwareSet.pick': ['Which 3D model', 'Quel mod\u00e8le 3D'],
  'notice.hardwareSet.models': ['3D models', 'Mod\u00e8les 3D'],
  'notice.hardwareSet.noColour': [
    'No material colour in Phase 3 for {names}: built unpainted.',
    'Aucune couleur de matériau en phase 3 pour {names} : construit sans peinture.',
  ],
  // A joint's set starts with what a tool drives, built in that tool's set (see HardwareSetStep).
  'notice.hardwareSet.toolFirst': [
    'Build these with their tool first, under Tools, and keep that model: {names}.',
    'Construisez d\u2019abord ces pièces avec leur outil, dans Outils, et retenez ce modèle : {names}.',
  ],
  // A piece of hardware a tool drives: its model is made in that tool's set (see ThreeDStep).
  'notice.3d.madeWithTool': [
    'This part is driven by a tool, so its model is made with that tool, under Tools.',
    'Cette pièce est entraînée par un outil : son modèle se fait avec cet outil, dans Outils.',
  ],
  'notice.hardwareSet.missingDrawing': [
    'Some of these parts have no drawing: the build sees only the others.',
    'Certaines de ces pièces n\u2019ont pas de schéma : la construction ne voit que les autres.',
  ],
  'notice.models.setPieceFixed': [
    'Cut out of a model of several pieces of hardware built together: keep or un-keep that one, below.',
    'Découpé d\u2019un modèle de plusieurs pièces de quincaillerie construites ensemble : gardez ou retirez celui-ci, plus bas.',
  ],
  // THE HOLES FOUND, one row each — see HolesTable.
  'notice.holesTable.title': ['Holes', 'Trous'],
  'notice.holesTable.hole': ['Hole', 'Trou'],
  'notice.holesTable.part': ['Part', 'Pièce'],
  'notice.holesTable.face': ['Face', 'Face'],
  'notice.holesTable.position': ['Position', 'Position'],
  'notice.holesTable.size': ['Size', 'Taille'],
  'notice.holesTable.via': ['Takes', 'Reçoit'],
  'notice.holes.diameter': ['diameter', 'diamètre'],
  // A position in the sub-step navigator: the same table, cut to the gestures a bore can be
  // in. Named for the PARTS rather than for the gestures, because that is what a reader comes
  // to it holding — which planks does this manual drill, and where.
  // THE HARDWARE TABLE IS THE AUTHORITY on which hardware goes into what: no table, or one built
  // from other sub-steps, and no hole is looked for.
  'notice.holes.sunk': ['head sunk {n} mm', 'tête enfoncée de {n} mm'],
  'notice.joints.washerNoModel': ['{w} not drawn: no 3D model kept for it', '{w} non dessinée : aucun modèle 3D gardé pour elle'],
  'notice.joints.unthreaded': ['no thread could be measured on its screw’s 3D model: it goes in without turning, and validating the joint leaves its sub-step out', 'aucun filetage n’a pu être mesuré sur le modèle 3D de sa vis : elle entre sans tourner, et valider la jonction laisse sa sous-étape de côté'],
  'notice.holes.noSurface': ['not drawn: where its wood begins was not measured — run Drilling holes again', 'non dessiné : l’endroit où commence le bois n’a pas été mesuré — relancez le perçage des trous'],
  'notice.holes.needTable': [
    'Run Joints table first: it says which hardware goes into which parts.',
    'Lancer d\u2019abord Table des joints : elle dit quelle quincaillerie va dans quelles pi\u00e8ces.',
  ],
  'notice.holes.tableOutdated': [
    'Re-run Joints table: the sub-steps have changed since it was made.',
    'Relancer Table des joints : les sous-\u00e9tapes ont chang\u00e9 depuis.',
  ],
  'notice.holes.notInTable': [
    'Not in the joints table, so no hole was looked for: {list}.',
    'Absent de la table des joints, donc aucun trou cherch\u00e9 : {list}.',
  ],
  'notice.holes.needSteps': [
    'Analyze the steps in Phase 4 first — the holes are read off their gestures',
    'Analysez d’abord les étapes en phase 4 — les trous se lisent sur leurs gestes',
  ],
  // Why a Find holes call has no reasoning to show, from the thinking tokens OpenAI counted.
  'notice.holes.noThinking': [
    'The model did not reason on this call (0 thinking tokens): it answered straight away.',
    'Le modèle n’a pas raisonné sur cet appel (0 jeton de réflexion) : il a répondu directement.',
  ],
  'notice.holes.thoughtUnsummarised': [
    'The model reasoned for {n} tokens, and OpenAI returned no summary of it.',
    'Le modèle a raisonné sur {n} jetons, et OpenAI n’en a renvoyé aucun résumé.',
  ],
  'notice.holes.centred': ['centred', 'centré'],
  // "40 mm from the Left". The word "edge" used to close it, on the argument that the six face
  // words are sides of a box and "from Left" reads as a direction of travel. That argument was
  // answered elsewhere: the line now opens with the axis it measures — "Width: 40 mm from the
  // Left" — and a distance along a named axis cannot be read as a direction. What was left was
  // a noun repeated on every line of every row, and the six words carry it themselves.
  //
  // THE FRENCH KEEPS ITS "bord", which is not the same redundancy: "du bord Arrière" contracts
  // to "de l'Arrière", and an article that changes with the word after it is a thing this
  // string cannot do. The noun is what makes it grammatical rather than what pads it.
  'notice.holes.fromEdge': ['{mm} mm from the {edge}', '{mm} mm du bord {edge}'],
  // WHERE THAT LANDS, AND FROM WHICH END. The resolved number is counted from the face's own
  // origin corner, and which corner that is turns on the face — the right face counts its
  // depth from the front and the left face from the back. Unnamed it is a figure nobody can
  // check; named, the two halves of the line are one distance said from either end.
  //
  // No trailing "edge" on this half, where the stated half has one: the line carries both and
  // the second reading is the shorter of the two by design.
  //
  // AND IT IS DRAWN IN BRACKETS, which is what makes the line read as one fact rather than
  // two. The stated distance is the answer; this is the same distance from the other end, and
  // an aside is exactly what that is.
  'notice.holes.resolvedFrom': ['{mm} from the {edge}', '{mm} depuis {edge}'],
  // How far in it goes — or that it does not stop.
  'notice.holes.through': ['through', 'traversant'],
  'notice.joints.histories': ['Each part\'s history', 'Historique de chaque pièce'],
  'notice.holes.ownHole': ['no pre-drilling', 'sans pré-perçage'],
  'notice.steps.tools': ['Tools needed', 'Outils nécessaires'],
  'notice.steps.reused': ['From previous steps', 'Des étapes précédentes'],
  // The same message where the button ISN'T: this phase shares the parts out among the
  // steps, and the list itself is made in another phase. So it names that phase by its own
  // title ({p}) rather than by a number, which is what went stale here.
  'notice.steps.noParts': [
    'The supplied parts have not been listed yet. That happens in “{p}”.',
    'Le matériel fourni n’a pas encore été listé. Cela se fait dans « {p} ».',
  ],
  'notice.steps.prev': ['Previous step', 'Étape précédente'],
  'notice.steps.next': ['Next step', 'Étape suivante'],
  'notice.step6.title': ['Videos generation', 'Génération des vidéos'],
  'notice.step7.title': ['Publish', 'Publication'],
  // What the phase makes: the models of the phase before it, fitted together.
  'notice.assembly.title': ['Assembly', 'Assemblage'],
  // The assembly phase's subphases: its sub-steps, the hardware joints, the covers.
  'notice.assembly.subCovers': ['Placing covers', 'Pose des caches'],
  // NOT "3D parts models" any more: the phase's first segment reconstructs the WHOLE
  // PRODUCT out of the listing's photographs, which is not a part and is the point of the
  // rename — a title naming only the parts would be describing the segments after it.
  'notice.models.phaseTitle': ['3D models', 'Modèles 3D'],
  // Phase 6's three subphases, in the order the work depends on itself.
  'notice.models.subProduct': ['Whole product', 'Produit entier'],
  'notice.models.subHardware': ['Hardware & covers', 'Quincaillerie et caches'],
  'notice.models.subComponents': ['Drilling holes', 'Perçage des trous'],
  'notice.models.subTools': ['Tools', 'Outils'],
  'notice.models.subSingle': ['Single models', 'Modèles seuls'],
  'notice.models.pickModel': ['Model shown', 'Modèle affiché'],
  // The tools subphase's table: which tool turns which hardware, and by which drive.
  'notice.toolFit.title': ['Tools & the hardware they drive', 'Outils et quincaillerie entraînée'],
  'notice.toolFit.tool': ['Tool', 'Outil'],
  'notice.toolFit.materials': ['Materials', 'Matériaux'],
  'notice.toolFit.hardware': ['Hardware', 'Quincaillerie'],
  'notice.toolFit.recess': ['Its drive (the tool\'s tip)', 'Son empreinte (l\'embout de l\'outil)'],
  'notice.toolFit.subSteps': ['Sub-steps', 'Sous-steps'],
  'notice.toolFit.unsaid': ['not given', 'non renseigné'],
  'notice.toolFit.noHardware': ['none named in the sub-step', 'aucune nommée dans le sous-step'],
  'notice.toolFit.none': ['No sub-step of this manual uses a tool.', 'Aucun sous-step de cette notice n’utilise d’outil.'],
  // The two materials badges, by which parts they are about — the hardware's default materials,
  // and (matBadge) the listing's, which the component parts are made of.
  'notice.defaultMaterials.texture': ['Hardware', 'Quincaillerie'],
  'notice.covers.onHole': ['hole {id}', 'trou {id}'],
  'notice.covers.onFoot': ['a foot', 'un pied'],
  'notice.covers.contactFace': ['touches the part', 'touche la pi\u00e8ce'],
  // Place the covers — see CoversRun.
  // Whether a cover goes into the part it sits on — see coverCollisions.
  // The question before a cut of the whole product that would lose drilled holes.
  'notice.models.cutLoses': ['{name}: {n} hole(s)', '{name} : {n} trou(s)'],
  'notice.models.cutAskTitle': ['Lose the drilled holes?', 'Perdre les trous percés ?'],
  'notice.models.cutAskBody': [
    'This replaces the components cut from the product, and these have been drilled. Their holes will have to be drilled again:',
    'Cela remplace les pièces découpées dans le produit, et celles-ci ont été percées. Leurs trous seront à repercer :',
  ],
  'notice.models.cutAskGo': ['Replace them', 'Les remplacer'],
  'notice.models.cutAskCancel': ['Cancel', 'Annuler'],
  'notice.models.noHardware': ['This manual lists no hardware part.', 'Cette notice ne liste aucune quincaillerie.'],
  'notice.models.noComponents': ['This manual lists no component part.', 'Cette notice ne liste aucune pièce.'],
  'notice.models.noTools': ['This manual lists no tool.', 'Cette notice ne liste aucun outil.'],
  // A component part's mesh is cut out of the whole product and made nowhere else.
  'notice.models.pieceFixed': [
    'Follows the whole product: keep or un-keep the product in Whole product',
    'Suit le produit entier : garder ou retirer le produit dans Produit entier',
  ],
  'notice.models.noPiece': [
    'No model yet: a component part\u2019s model is cut out of the whole product when a GPT-6 Astra product is kept in the Parts DB, in Whole product.',
    'Pas encore de modèle : le modèle d\u2019une pièce est découpé dans le produit entier quand un produit GPT-6 Astra est gardé en base, dans Produit entier.',
  ],
  'notice.3d.subMaterials': [
    'Materials & colours', 'Mat\u00e9riaux & couleurs',
  ],
  // The whole-product segment, when the manual has told us neither its category nor its
  // product name. A segment with no label is a segment nobody presses.
  'notice.models.wholeProduct': ['Whole product', 'Produit entier'],
  // The product's photographs could not be listed: a failure, not "none" (see useSubmodelPhotos).
  'notice.models.photosFailed': [
    'The product’s photographs could not be loaded. Reload the page to try again.',
    'Les photos du produit n’ont pas pu être chargées. Rechargez la page pour réessayer.',
  ],
  'notice.models.noProductPhotos': [
    'No product photo is stored for this submodel yet. They are found and kept in Phase 1.',
    "Aucune photo du produit n'est enregistrée pour ce sous-modèle. Elles sont trouvées et conservées en phase 1.",
  ],
  // What the mark on the whole-product segment means. The strip's own pair says "in the
  // Parts DB", which is a fact about a part: the assembled chair has no row there, and the
  // glyph on its segment is answering the question the database was only ever the registry
  // of. Read out to a screen reader — see SelectorPart.markLabels.
  'notice.models.hasModel': ['Has a 3D model', 'A un modèle 3D'],
  'notice.models.noModel': ['No 3D model yet', 'Pas encore de modèle 3D'],
  // The product's models could not be listed: a failure, not "none" (see useProductRuns).
  'notice.models.runsFailed': [
    'The whole product’s models could not be loaded. Reload the page to try again.',
    'Les modèles du produit entier n’ont pas pu être chargés. Rechargez la page pour réessayer.',
  ],
  'notice.models.noProductMesh': [
    'No 3D model of the whole product yet.',
    "Pas encore de modèle 3D du produit entier.",
  ],
  // A PART MODELLED FROM THE MANUAL'S DRAWING, with no drawing kept for it. Its own sentence
  // and not the photographs' one: what is missing is a different thing, and it is found in a
  // different place — the part has to be saved to the Parts DB from the manual's parts list.
  'notice.3d.noSchematics': [
    'No drawing of this part has been kept — save it to the parts database from the manual.',
    'Aucun schéma de cette pièce n’a été conservé — enregistrez-la dans la base de pièces '
    + 'depuis la notice.',
  ],
  'notice.models.needsParts': [
    'The parts have not been listed yet — see the parts phase.',
    'Les pièces ne sont pas encore listées — voir la phase des pièces.',
  ],
  'notice.assembly.target': ['Fitted onto', 'Pièce fixe'],
  'notice.assembly.moving': ['Part that moves', 'Pièce qui se monte'],
  'notice.assembly.tool': ['Tool used', 'Outil utilisé'],
  // A PLACING'S OWN WORD FOR ITS PART: not "the part that moves" — nothing stands still for
  // it to move against — and there is no part it is fitted onto to name either. It had a
  // second word, the gesture, back when a turn was chosen off a list of five; the turn is the
  // rotation between two typed orientations now, and a placing asks for one thing.
  'notice.assembly.part': ['Part placed', 'Pièce placée'],
  // THE SIX FACES OF THE MEASURED BOX. Directions of the ASSEMBLY's frame and not claims about
  // the part — see FaceWords in meshAnnotations, where which face is which is decided and where
  // that distinction is spelled out. Short on purpose: they are drawn on the faces themselves,
  // at a size that has to work on a barrel nut.
  'notice.assembly.faceTop': ['Top', 'Dessus'],
  'notice.assembly.faceBottom': ['Bottom', 'Dessous'],
  'notice.assembly.faceFront': ['Front', 'Avant'],
  'notice.assembly.faceBack': ['Back', 'Arrière'],
  'notice.assembly.faceLeft': ['Left', 'Gauche'],
  'notice.assembly.listHide': ['Fold the list away', 'Replier la liste'],
  'notice.assembly.listShow': ['Show the list', 'Afficher la liste'],
  'notice.assembly.faceRight': ['Right', 'Droite'],
  // The copy button every hover panel carries (see HoverTip).
  'notice.tip.copy': ['Copy', 'Copier'],
  'notice.tip.copied': ['Copied', 'Copié'],
  // THE TWO HALVES OF A CALL, on the menu one badge opens (see CallBadge). Short and generic
  // where the panels' own headings are specific — "Prompt & images sent to analyze step 1" is
  // what the modal is titled, and a menu row saying that much is a menu nobody scans.
  // The word under a picture in a prompt is also the switch that takes it out of one, where
  // the call allows it. Said as an instruction rather than as a state — what pressing it
  // does — because the state is already drawn: an excluded picture is grey and struck.
  'notice.tip.picInclude': [
    'Include this picture in the question',
    'Inclure cette image dans la question',
  ],
  'notice.tip.picExclude': [
    'Leave this picture out of the question',
    'Exclure cette image de la question',
  ],
  'notice.tip.menuPrompt': ['Prompt', 'Prompt'],
  'notice.tip.menuPromptAll': ['Prompt, reasoning & output', 'Prompt, raisonnement & réponse'],
  'notice.tip.menuAnswer': ['Reasoning & output', 'Raisonnement & réponse'],
  // Making the call again on its own, from the badge that shows what it asks.
  'notice.tip.menuRun': ['Run', 'Lancer'],
  'notice.chain.runs': ['Runs these calls, in order:', 'Lance ces appels, dans l\'ordre :'],
  // THE TWO ENDS OF THE MOVE THE BOX ABOVE DEFINES. "Seat" was the trade's word for the
  // second of them — a bolt seats in its hole — and the only word on the page that had to be
  // explained before it could be read.
  'notice.assembly.startPos': ['Starting position', 'Position de départ'],
  'notice.assembly.finalPos': ['Final position', 'Position finale'],
  // And the column between them: where the part is at the t the scrubber is standing on. Not
  // a third setting — a reading, and the only one on the bar that moves while the clip plays.
  'notice.assembly.nowPos': ['Current position', 'Position actuelle'],
  // The kind chip's own heading, now that it has a box rather than floating over the
  // manual's sentence.
  // "Movement type" WAS HERE, naming the two-answer question Phase 5's chip used to show. What
  // it shows now is which of the five kinds the sub-step is, which is the sub-step table's KIND
  // column — so the phase reads notice.steps.colKind and this pair is gone. One word for one
  // fact, on both screens, and no second copy to be renamed on one of them.
  // The two readings each end is given. Short, because the column above them already says
  // which end it is — all these have to add is the unit.
  'notice.assembly.posRow': ['Position (cm)', 'Position (cm)'],
  'notice.assembly.rotRow': ['Orientation (°)', 'Orientation (°)'],
  // HOW THE PART GOES IN — the switch that decides whether the two threading fields are on
  // the form at all. Answered by the analysis and corrected here.
  'notice.assembly.drive': ['Goes in', 'Mise en place'],
  'notice.assembly.driveStraight': ['Straight', 'Tout droit'],
  'notice.assembly.driveTurned': ['Turned in', 'En vissant'],
  // HOW FAR THE PART GOES, over the two fields that divide that distance up.
  'notice.assembly.travelled': [
    'Distance travelled: {mm} mm',
    'Distance parcourue : {mm} mm',
  ],
  // And why the clip will not play: the thread claims more of the movement than there is.
  'notice.assembly.overThread': [
    'The thread is deeper than the distance the part travels — the two ends of the movement say how far it goes.',
    'Le filetage est plus profond que la distance parcourue par la pièce — ce sont les deux extrémités du mouvement qui disent la distance.',
  ],
  'notice.assembly.pitch': ['Pitch (mm/turn)', 'Pas (mm/tour)'],
  'notice.assembly.depth': ['Thread (mm)', 'Filetage (mm)'],
  // WHY THERE IS NO MOVEMENT ON SCREEN. It is not a prompt to do anything — this phase has no
  // control for it any more — so it names the two states it can be: Phase 4 never said which
  // part moves, or the parts it named have no mesh yet.
  // A part that does not move has one pose, and the column over it says so rather than
  // borrowing "final", which would imply it arrived from somewhere.
  // How close the shot stands, as a multiple of the one that fits everything — the camera's
  // own line over the parts' tables.
  'notice.assembly.camZoom': ['Camera zoom', 'Zoom caméra'],
  'notice.assembly.stillPos': ['Position', 'Position'],
  // A tightening shows nothing: the hardware is already put in that way — see stillSubSteps.
  // The collisions card's switch for the red painted on the parts — see ClashBar.
  'notice.clash.marks': ['Red marks', 'Marques rouges'],
  'notice.clash.showMarks': ['Paint what overlaps red on the parts', 'Peindre en rouge sur les pièces ce qui se chevauche'],
  'notice.clash.hideMarks': ['Hide the red painted on the parts', 'Masquer le rouge peint sur les pièces'],
  'notice.assembly.pressToPlace': [
    'Not placed: it is worked out on arrival, nothing is asked. If it stays this way, the reason is said on this page — or press the Movement badge\u2019s Place row.',
    'Pas placé : c\u2019est calculé à l\u2019arrivée, rien n\u2019est demandé. Si ça reste ainsi, la raison est indiquée sur cette page — ou appuyez sur la ligne Placer du badge Mouvement.',
  ],
  'notice.assembly.unfitted': [
    'Nothing to draw here yet: Phase 4 has not said which part moves here.',
    'Rien à dessiner ici : la phase 4 n’a pas dit quelle pièce bouge ici.',
  ],
  // AND THE OTHER REASON, which used to be the second half of the line above, joined to it by
  // an "or". Two states, two fixes — re-run Phase 4, or reconstruct a part in Phase 5 — and a
  // reader shown both at once believes the first. The parts are named between these two:
  // "No 3D model for JCBC screw (E). Reconstruct it in Phase 5."
  'notice.assembly.noMesh': ['No 3D model for', 'Pas de modèle 3D pour'],
  // A piece an earlier sub-step leaves is not reconstructed: it is saved when that sub-step is signed off.
  'notice.assembly.noPiece': ['No saved model for', 'Pas de modèle enregistré pour'],
  'notice.assembly.noPieceFix': [
    'It is what an earlier sub-step leaves: sign that sub-step off (again, if it already is) and this one draws itself.',
    'C\u2019est ce que laisse une sous-étape précédente : validez-la (de nouveau, si elle l\u2019est déjà) et celle-ci se dessinera.',
  ],
  // WHY A PLACING RULE COULD NOT PLACE — see ruleError, which is what throws these keys.
  'notice.rule.noCoverPlace': [
    'No hole a capped screw enters by, and no foot, was found for this cover.',
    'Aucun trou par lequel entre une vis à cacher, et aucun pied, n\u2019a été trouvé pour ce cache.',
  ],
  'notice.rule.markNotHeld': [
    '{id} is on a part the piece this sub-step receives does not hold.',
    '{id} est sur une pièce que l\u2019ensemble reçu par cette sous-étape ne contient pas.',
  ],
  'notice.rule.partNotCut': [
    '{id} was not cut from the product, so where it stands is unknown.',
    '{id} n\u2019a pas été découpé dans le produit : sa position est inconnue.',
  ],
  'notice.rule.madeOnNotCut': [
    'What {id} was made on was not cut from the product, so where it stands is unknown.',
    'Ce sur quoi {id} a été fait n\u2019a pas été découpé dans le produit : sa position est inconnue.',
  ],
  'notice.rule.noPieceUnder': [
    'The sub-step that makes {id} is not signed off, so there is no piece to place this on.',
    'La sous-étape qui fabrique {id} n\u2019est pas validée : il n\u2019y a pas de pièce sur laquelle placer ceci.',
  ],
  'notice.rule.madeOnTurned': [
    '{id} was made on a part that is turned, so where it stands is unknown.',
    '{id} a été fait sur une pièce tournée : sa position est inconnue.',
  ],
  'notice.rule.modelOfPiece': [
    'The model of {id} (#{job}) would not load.',
    'Le modèle de {id} (#{job}) n\u2019a pas pu être chargé.',
  ],
  'notice.rule.noModelUnder': [
    '{id}: no model to place it on.',
    '{id} : aucun modèle sur lequel le placer.',
  ],
  'notice.rule.noSurface': [
    '{id}: where the wood begins under it is not measured.',
    '{id} : l\u2019endroit où le bois commence dessous n\u2019est pas mesuré.',
  ],
  'notice.rule.hardwareModel': [
    '{id}: the hardware\u2019s model would not load.',
    '{id} : le modèle de la quincaillerie n\u2019a pas pu être chargé.',
  ],
  'notice.rule.noThread': [
    '{id}: no thread could be measured on the screw\u2019s model, so it would not turn.',
    '{id} : aucun filetage n\u2019a pu être mesuré sur le modèle de la vis, elle ne tournerait donc pas.',
  ],
  'notice.rule.noMiddle': [
    '{id}: it is in the other hand of a pair, and the product has no middle to reflect it across.',
    '{id} : il est dans l\u2019autre main d\u2019une paire, et le produit n\u2019a pas de milieu par rapport auquel le refléter.',
  ],
  // WHY NO RULE PLACES A SUB-STEP, before anything is loaded (see Declined in ruleError).
  'notice.rule.noRoles': [
    'No rule places this sub-step: Phase 4 does not say which part moves and which part receives it.',
    'Aucune règle ne place cette sous-étape : la phase 4 ne dit pas quelle pièce bouge ni laquelle la reçoit.',
  ],
  'notice.rule.joint.pieceMany': [
    '{part} holds several component parts, and the joint rule places a piece by the one part it carries.',
    '{part} contient plusieurs pièces, et la règle des jonctions place un assemblage par la seule pièce qu\u2019il porte.',
  ],
  'notice.rule.joint.nothingHeld': [
    '{onto} holds no component part to place against.',
    '{onto} ne contient aucune pièce contre laquelle placer.',
  ],
  'notice.rule.joint.notFromProduct': [
    '{part} has no model cut from the product, so the joint rule cannot stand it where the product built it.',
    '{part} n\u2019a pas de modèle découpé dans le produit : la règle des jonctions ne peut pas le placer là où le produit l\u2019a construit.',
  ],
  'notice.rule.joint.noLine': [
    'No hardware line connects a hole of {part} to a hole of {onto}{side}: Drilling holes found none.',
    'Aucune ligne de quincaillerie ne relie un trou de {part} à un trou de {onto}{side} : le perçage des trous n\u2019en a trouvé aucune.',
  ],
  'notice.rule.joint.onSide.left': [' on the left', ' du côté gauche'],
  'notice.rule.joint.onSide.right': [' on the right', ' du côté droit'],
  'notice.rule.joint.lineNoModel': [
    'Line {line} joins them, but its hardware {part} has no model to measure.',
    'La ligne {line} les relie, mais sa quincaillerie {part} n\u2019a pas de modèle à mesurer.',
  ],
  'notice.rule.joint.notSameRun': [
    '{part} and {onto} were not cut from the same product.',
    '{part} et {onto} n\u2019ont pas été découpés dans le même produit.',
  ],
  'notice.rule.joint.noMiddle': [
    'The joining hole is in one hand of a pair, and the product has no middle to reflect it across.',
    'Le trou de jonction est dans une main d\u2019une paire, et le produit n\u2019a pas de milieu par rapport auquel le refléter.',
  ],
  'notice.rule.joint.otherHandAlone': [
    '{onto} is the other hand of a pair on its own, and nothing draws it reflected.',
    '{onto} est l\u2019autre main d\u2019une paire, seule, et rien ne la dessine reflétée.',
  ],
  'notice.rule.hole.notCovered': [
    '{part} goes into no hole, and no covers reading places it.',
    '{part} n\u2019entre dans aucun trou, et aucune lecture des caches ne le place.',
  ],
  // Why a hole rule found no hole: nothing filed for the hardware, nothing in this part, or too few left.
  'notice.rule.hole.noneFiled': [
    'Drilling holes filed no hole for {part}.',
    'Le perçage des trous n\u2019a classé aucun trou pour {part}.',
  ],
  'notice.rule.hole.noneInPart': [
    'Drilling holes filed {n} holes for {part}, none of them in {onto}.',
    'Le perçage des trous a classé {n} trous pour {part}, aucun dans {onto}.',
  ],
  'notice.rule.hole.noneLeft': [
    'Drilling holes filed {n} holes for {part} in {onto}. The sub-steps before this one take them, and this one needs {qty}.',
    'Le perçage des trous a classé {n} trous pour {part} dans {onto}. Les sous-étapes d\u2019avant les prennent, et celle-ci en demande {qty}.',
  ],
  'notice.rule.set.noModel': [
    '{part} has no model to place it by its set.',
    '{part} n\u2019a pas de modèle pour le placer par son ensemble.',
  ],
  'notice.rule.set.notCut': [
    '{part} was not cut from a hardware set. Build the pair\u2019s set in Hardware & covers to place it as built.',
    '{part} n\u2019a pas été découpé dans un ensemble de quincaillerie. Construisez l\u2019ensemble de la paire dans Quincaillerie et caches pour le placer tel que construit.',
  ],
  'notice.rule.set.edited': [
    '{part} was edited after it was cut from its set, so it no longer stands in the set\u2019s frame.',
    '{part} a été modifié après avoir été découpé dans son ensemble : il ne se trouve plus dans le repère de l\u2019ensemble.',
  ],
  'notice.rule.set.noBox': [
    '{part} has no measured box.',
    '{part} n\u2019a pas de boîte mesurée.',
  ],
  'notice.rule.set.notInSet': [
    'The set {part} was cut from does not hold {onto}.',
    'L\u2019ensemble dont {part} a été découpé ne contient pas {onto}.',
  ],
  'notice.rule.set.otherSize': [
    '{onto} is not the same size in the set {part} was cut from.',
    '{onto} n\u2019a pas la même taille dans l\u2019ensemble dont {part} a été découpé.',
  ],
  'notice.rule.noneForKind': [
    'No rule places this kind of sub-step yet: it moves neither a component part onto another, nor hardware into a hole, nor one piece of hardware onto another.',
    'Aucune règle ne place encore ce genre de sous-étape : elle ne pose ni une pièce sur une autre, ni une quincaillerie dans un trou, ni une quincaillerie sur une autre.',
  ],
  'notice.rule.oldAnswer': [
    'The movement drawn is an old model answer, which no rule has replaced.',
    'Le mouvement affiché est une ancienne réponse du modèle, qu\u2019aucune règle n\u2019a remplacée.',
  ],
  'notice.rule.offFace': [
    '{id}: its place is not on the face.',
    '{id} : son emplacement n\u2019est pas sur la face.',
  ],
  'notice.rule.noPieceMoving': [
    'The sub-step that makes {id} is not signed off, so there is no piece to move.',
    'La sous-étape qui fabrique {id} n\u2019est pas validée : il n\u2019y a pas de pièce à déplacer.',
  ],
  'notice.rule.setLacks': [
    'The set {part} was cut from holds no {hw}, so where it sits on {hw} is unknown.',
    'L\u2019ensemble dont {part} a été découpé ne contient pas {hw} : sa position sur {hw} est inconnue.',
  ],
  'notice.rule.setOtherHw': [
    '{hw} is not the same size in the set {part} was cut from as the {hw} kept, so where {part} sits on it is unknown.',
    '{hw} n\u2019a pas la même taille dans l\u2019ensemble dont {part} a été découpé que celle gardée : la position de {part} dessus est inconnue.',
  ],
  'notice.rule.notOneSet': [
    '{id}: one of its parts ({parts}) was not cut from a hardware set, or was edited since, so where {hw} stands inside it is unknown. Build its set in Hardware & covers.',
    '{id} : une de ses pièces ({parts}) n\u2019a pas été découpée dans un ensemble de quincaillerie, ou a été modifiée depuis, donc la position de {hw} à l\u2019intérieur est inconnue. Construisez son ensemble dans Quincaillerie et caches.',
  ],
  'notice.rule.nothingUnderCover': [
    '{id}: nothing was found under the cover, so where it rests is unknown.',
    '{id} : rien n\u2019a été trouvé sous le cache, l\u2019endroit où il repose est inconnu.',
  ],
  'notice.rule.modelNoLength': [
    'The model of #{job} would not load, so its length is unknown.',
    'Le modèle #{job} n\u2019a pas pu être chargé : sa longueur est inconnue.',
  ],
  'notice.rule.modelNoAxis': [
    'The model of #{job} would not load, so its axis is unknown.',
    'Le modèle #{job} n\u2019a pas pu être chargé : son axe est inconnu.',
  ],
  'notice.assembly.bakeFailed': [
    'The piece this sub-step leaves could not be saved, so the sub-steps after it have nothing to be drawn on:',
    'La pièce que laisse cette sous-étape n\u2019a pas pu être enregistrée, donc les sous-étapes suivantes n\u2019ont rien sur quoi se dessiner :',
  ],
  'notice.assembly.noMeshFix': [
    'Reconstruct in Phase 5 and this sub-step draws itself.',
    'À reconstruire en phase 5, et cette sous-étape se dessinera.',
  ],
  'notice.assembly.pick': [
    'Pick the part that moves, and the part it is fitted onto.',
    'Choisir la pièce qui se monte, et la pièce sur laquelle elle vient.',
  ],
  'notice.assembly.pickOne': [
    'Pick the part that is placed.',
    'Choisir la pièce qui est placée.',
  ],
  // THE TOOL THE SUB-STEP CALLS FOR AND THE VIEWER HAS NOT GOT. Two facts, not one: nobody
  // picked a tool, or the picked one has no reconstruction to draw. Said plainly and left
  // there — a manual whose tools have no meshes still assembles.
  'notice.assembly.toolNotChosen': [
    'This sub-step calls for a tool and none is chosen, so none is drawn.',
    'Cette sous-étape demande un outil et aucun n’est choisi : aucun n’est dessiné.',
  ],
  'notice.assembly.toolNoMesh': [
    'No 3D model of {tool}, so it is not drawn.',
    'Aucun modèle 3D de {tool} : il n’est pas dessiné.',
  ],
  // Two pieces of hardware built together are placed as built, not asked about — see fromSet.
  'notice.assembly.badgeMovement': ['Movement', 'Mouvement'],
  'notice.assembly.placedByRule': ['placed by rule', 'placée par règle'],
  'notice.assembly.placedByHand': ['adjusted by hand', 'ajustée à la main'],
  'notice.assembly.placedByNobody': ['to place', 'à placer'],
  'notice.assembly.placeFromSet': ['Place as built together', 'Placer comme construites ensemble'],
  'notice.assembly.setHowRow': ['How it works & result', 'Fonctionnement & résultat'],
  'notice.assembly.setHowTitle': ['How it works', 'Fonctionnement'],
  'notice.assembly.setResultRow': ['Result', 'Résultat'],
  'notice.assembly.setHow': [
    'No call is made: these two pieces were built together in #{run}.\n\nWhere the moving piece ends up is read off that model — its centre, relative to the fixed piece\'s, as Blender measured both when the set was built.\n\nThe way in is the shaft\'s axis — the longer piece, a screw — read off its own mesh, from its tip: a washer goes on over the tip, a screw goes through tip first. It starts 3 mm clear of the other piece. Only a shaft alike at both ends (a dowel) has its two ends played against the meshes, the one along which nothing passes through anything kept. A shaft whose model will not load is an error: nothing else is tried.',
    'Aucun appel n\u2019est fait : ces deux pièces ont été construites ensemble dans #{run}.\n\nLa position finale de la pièce qui bouge est lue sur ce modèle — son centre, par rapport à celui de la pièce fixe, tel que Blender les a mesurés à la construction de l\u2019ensemble.\n\nLe chemin d\u2019entrée est l\u2019axe de la tige — la pièce la plus longue, une vis — lu sur son propre maillage, depuis sa pointe : une rondelle s\u2019enfile par la pointe, une vis traverse pointe la première. Elle part à 3 mm de l\u2019autre pièce. Seule une tige identique à ses deux bouts (un tourillon) voit ses deux bouts joués contre les maillages, celui où rien ne traverse rien étant gardé. Une tige dont le modèle ne se charge pas est une erreur : rien d’autre n’est essayé.',
  ],
  // A component part set against another is placed as their joint has them — see fromJoint.
  'notice.assembly.placeFromJoint': ['Place as the joint has them', 'Placer comme dans le joint'],
  'notice.assembly.jointHow': [
    'No call is made: the joint these two parts make already says it.\n\nWhere the moving part ends up is where the joint view stands it — its centre, relative to the fixed part\'s, as both were cut from the product #{run}.\n\nThe way in is the line of the hardware that will join them ({line}, the first driven after this sub-step): the moving part comes onto the fixed one along it, from the side the hardware runs on to — through the part for a screw, out of the face for a dowel.\n\nIt starts as far out as the joint view stands the two apart, exploded: that hardware\'s length, off its model, plus 13 mm. A model that will not load is an error: nothing else is tried.',
    'Aucun appel n\u2019est fait : le joint que forment ces deux pièces le dit déjà.\n\nLa position finale de la pièce qui bouge est celle de la vue joint — son centre, par rapport à celui de la pièce fixe, telles que le produit #{run} les a découpées.\n\nLe chemin d\u2019entrée est la ligne de la quincaillerie qui les joindra ({line}, la première posée après cette sous-étape) : la pièce qui bouge vient sur la pièce fixe le long de cette ligne, du côté où la quincaillerie continue — à travers la pièce pour une vis, hors de la face pour un tourillon.\n\nElle part aussi loin que la vue joint écarte les deux pièces, éclatée : la longueur de cette quincaillerie, lue sur son modèle, plus 13 mm. Un modèle qui ne se charge pas est une erreur : rien d\u2019autre n\u2019est essayé.',
  ],
  // A piece of hardware put into its hole is placed as the hole has it — see fromHole.
  'notice.assembly.placeFromHole': ['Place in its hole', 'Placer dans son trou'],
  'notice.assembly.holeHow': [
    'No call is made: the hole this sub-step fills ({hole}) already says it.\n\nWhere the hardware rests is worked out from the hole — the part it is on, the face it goes in by, its place on that face, where the wood begins and how deep the hole goes — and from the hardware\'s own length, off its 3D model. A barrel nut is turned so its threaded hole faces its screw.\n\nIt is then placed on what this sub-step receives: the piece the sub-steps before have made, whose parts stand where the product built them and whose middle is read off its baked model. The sub-step that makes that piece has to be signed off.\n\nThe way in is the hole\'s own line, out of the face. It starts with its tip 3 mm clear of the hole\'s mouth, and a screw turns at its thread\'s lead. A model that will not load is an error: nothing else is tried.',
    'Aucun appel n\u2019est fait : le trou que cette sous-étape remplit ({hole}) le dit déjà.\n\nLa position de repos de la quincaillerie est calculée à partir du trou — la pièce qui le porte, la face d\u2019entrée, sa place sur cette face, là où le bois commence et la profondeur du trou — et de la longueur de la quincaillerie, lue sur son modèle 3D. Un écrou cylindrique est tourné pour que son trou fileté regarde sa vis.\n\nElle est ensuite placée sur ce que la sous-étape reçoit : la pièce assemblée par les sous-étapes précédentes, dont les pièces sont là où le produit les a construites et dont le milieu est lu sur son modèle sauvegardé. La sous-étape qui produit cette pièce doit être validée.\n\nLe chemin d\u2019entrée est la ligne du trou, hors de la face. Elle part la pointe à 3 mm de l\u2019entrée du trou, et une vis tourne au pas de son filet. Un modèle qui ne se charge pas est une erreur : rien d\u2019autre n\u2019est essayé.',
  ],
  // A cover is placed by rule, in the sub-step that puts it on — see fromHole.
  'notice.assembly.placeCover': ['Place the cover', 'Placer le cache'],
  'notice.assembly.coverHow': [
    'No call is made: where a cover goes is already said by the joint it closes.\n\nA button goes on a hole the screw it caps enters by — the through hole its head is in. A pad goes under a foot of the part it is laid on, the feet read off that part\'s 3D model. The joint\'s covers are dealt to its sub-steps in order: buttons in the order of the screws they cap, pads in the order of the feet.\n\nThis sub-step\'s cover rests where it first touches the wood, measured by rays along its way in across its width, the way up its model was built. It is placed on the piece this sub-step receives, pushed straight on from 10 mm clear, and shot by the same rule as every worked-out movement.',
    'Aucun appel n\u2019est fait : la place d\u2019un cache est déjà dite par le joint qu\u2019il ferme.\n\nUn bouton va sur un trou par lequel entre la vis qu\u2019il couvre — le trou traversant où est sa tête. Un patin va sous un pied de la pièce sous laquelle il est posé, les pieds étant lus sur le modèle 3D de cette pièce. Les caches du joint sont distribués à ses sous-étapes dans l\u2019ordre : les boutons dans l\u2019ordre des vis qu\u2019ils couvrent, les patins dans l\u2019ordre des pieds.\n\nLe cache de cette sous-étape repose là où il touche le bois en premier, mesuré par des rayons le long de son trajet sur toute sa largeur, dans le sens où son modèle a été construit. Il est posé sur la pièce que cette sous-étape reçoit, poussé tout droit depuis 10 mm, et filmé par la même règle que tout mouvement calculé.',
  ],
  // A sign-off given while one of the parts had another 3D model — see fittingModels.
  'notice.assembly.sameAs': ['Same gesture as {label}, signed off together.', 'Même geste que {label}, validée en même temps.'],
  // A sign-off withdrawn because Phase 4 changed the gesture — see gestureMoved.
  'notice.assembly.gestureChanged': [
    'Phase 4 changed this sub-step. Watch it, then sign it off again.',
    'Phase 4 a changé cette sous-étape. Regardez-la, puis validez-la de nouveau.',
  ],
  // Fitting.prepOn: the washer prepared on the screw could not be placed.
  'notice.assembly.prepNotPlaced': [
    'The hardware prepared on it is not shown: {why}',
    'La quincaillerie préparée dessus n’est pas montrée : {why}',
  ],
  // Fitting.nextOn: the second hardware put into the same part could not be placed.
  // PrepOn.shot: nothing to frame (no unit stood).
  'notice.assembly.noShot': ['its shot could not be worked out', 'son cadrage n’a pas pu être calculé'],
  'notice.assembly.lastNotPlaced': [
    'The third hardware is not shown: {why}',
    'La troisième quincaillerie n’est pas montrée : {why}',
  ],
  'notice.assembly.nextNotPlaced': [
    'The second hardware is not shown: {why}',
    'La deuxième quincaillerie n’est pas montrée : {why}',
  ],
  'notice.assembly.modelChanged': [
    'Signed off on another 3D model of {parts}: watch it again, and sign it off again if it is still right.',
    'Validé sur un autre modèle 3D de {parts} : regardez-le à nouveau, et validez-le de nouveau s’il est toujours juste.',
  ],
  'notice.assembly.unscaled': [
    'No printed size for {parts}.',
    'Aucune dimension imprimée pour {parts}.',
  ],
  'notice.assembly.loadFailed': [
    'A part’s 3D model could not be loaded.',
    'Le modèle 3D d’une pièce n’a pas pu être chargé.',
  ],
  // THE PANEL A CLICK ON A PART OPENS. Its three numbers are the part's centre in the
  // assembly's frame, which is the frame the gizmo draws at the origin.
  //
  // Titled like the viewport corner's rows, down to the unit in brackets, because it is the
  // same grid asking the same two questions — of the PART rather than of the camera, which is
  // the one word that differs and therefore the one word that has to be there.
  'notice.assembly.coords': ['Part position (cm)', 'Position pièce (cm)'],
  'notice.assembly.hidePart': ['Take out of the picture', 'Retirer de l’image'],
  // One switch drawn on every card: the arrows measure everything on screen at once, so
  // every ruler has to be on for them to show.
  'notice.assembly.hideGizmo': ['Hide the origin gizmo', 'Masquer le repère d’origine'],
  'notice.assembly.showGizmo': ['Show the origin gizmo', 'Afficher le repère d’origine'],
  // WHICH SIDE THE SUB-STEP IS WATCHED FROM, beside how close. Both are answered by the call
  // now — see subStepFittingPrompt — and both are read in the viewport's own corner.
  'notice.assembly.camAngle': ['Camera angle', 'Angle de la cam\u00e9ra'],
  'notice.assembly.hideMeasures': ['Hide the measurements', 'Masquer les mesures'],
  // A box's own switch — the name on a part's card, or on one of its meshes.
  'notice.assembly.showBox': ['Show its box', 'Afficher sa boîte'],
  'notice.assembly.assembled': ['Assembled', 'Assembl\u00e9'],
  'notice.assembly.exploded': ['Exploded', '\u00c9clat\u00e9'],
  'notice.assembly.hideBox': ['Hide its box', 'Masquer sa boîte'],
  'notice.assembly.showMeasures': ['Show the measurements', 'Afficher les mesures'],
  // THE SIX WORDS ON THE FACES, which is the ruler's other half and now its own button. The
  // box both hang on is drawn while either is on, so the tooltips speak of the words alone.
  'notice.assembly.hideFaces': ['Hide the face names', 'Masquer les noms des faces'],
  'notice.assembly.showFaces': ['Show the face names', 'Afficher les noms des faces'],
  'notice.assembly.showPart': ['Put back in the picture', 'Remettre dans l’image'],
  // The part's own house, and it means what the viewport's does: back to the reference. That
  // reference is the origin the gizmo stands at, and all six numbers go there together.
  'notice.assembly.coordsOrigin': [
    'Put the part at the origin (0, 0, 0), unturned',
    'Placer la pièce à l’origine (0, 0, 0), sans rotation',
  ],
  'notice.assembly.viewAngles': ['Viewpoint', 'Point de vue'],
  'notice.assembly.viewSquare': ['Square-on view (front)', 'Vue de face, d’équerre'],
  'notice.assembly.orientation': ['Part orientation (°)', 'Orientation pièce (°)'],
  'notice.assembly.coordsClose': ['Close', 'Fermer'],
  'notice.assembly.noValidatedJoint': ['No sub-step yet.', 'Aucune sous-étape pour l’instant.'],
  'notice.assembly.noModel': [
    'No 3D model in the Parts DB for this part yet.',
    'Pas encore de modèle 3D dans la base de pièces pour cette pièce.',
  ],
  // THE TWO KINDS OF SUB-STEP had their own pair of strings here — "Fitting" and "Placing" —
  // which is the same distinction as notice.steps.family.*, in one more vocabulary. Phase 5
  // reads those now: the family is a fact about the SUB-STEP, answered in Phase 4 as one of
  // five kinds, and a phase that draws it does not get to name it a third way.
  'notice.assembly.noSlotParts': [
    'This sub-step lists no parts.',
    'Cette sous-étape ne liste aucune pièce.',
  ],
  // "Animation ready" was here, the badge the publish review drew on a screen whose fitting
  // was signed off. The review plays the clip now, which says the same thing by showing it.
  // THE CLIP'S OWN THREE CONTROLS — play, pause, and see it bigger. Their own keys rather
  // than the phase's transport labels beside them: the same words, but the phase's are about
  // a scrubber somebody is judging a fitting on, and these are about a picture being watched.
  'notice.clip.play': ['Play', 'Lire'],
  'notice.clip.pause': ['Pause', 'Pause'],
  'notice.clip.expand': ['See it bigger', 'Voir en grand'],
  'notice.clip.close': ['Close', 'Fermer'],
  'notice.clip.speed': ['Playing speed', 'Vitesse de lecture'],
  'notice.assembly.unvalidated': ['Not validated', 'Non validée'],
  'notice.assembly.validated': ['Validated', 'Validée'],
  // WHILE THE PART IS BEING SLID HOME. The contact places box against box and the surfaces
  // are found by measuring (see assemblySnap), which takes a moment and moves the picture when
  // it lands — so it is said rather than left to look like the viewer twitching.
  'notice.assembly.seating': [
    'Sliding the part in until it touches…',
    'La pièce est avancée jusqu’au contact…',
  ],
  'notice.assembly.start': ['Start', 'Début'],
  'notice.assembly.end': ['End', 'Fin'],
  // THE WHOLE ASSEMBLY PLAYED THROUGH (see AllClips).
  'notice.assembly.all': ['All', 'Tout'],
  'notice.assembly.allDownload': ['Download the video ({format})', 'Télécharger la vidéo ({format})'],
  'notice.assembly.allRecording': ['Recording… (stop)', 'Enregistrement… (arrêter)'],
  'notice.assembly.allExporting': ['Making the video… {n} % (stop)', 'Création de la vidéo… {n} % (arrêter)'],
  'notice.assembly.allFile': ['assembly', 'montage'],
  'notice.assembly.allGlb': ['Download as 3D (GLB)', 'Télécharger en 3D (GLB)'],
  'notice.assembly.allGlbMaking': ['Making the 3D file… {n} % (stop)', 'Création du fichier 3D… {n} % (arrêter)'],
  'notice.assembly.allNone': ['No sub-step has an animation yet.', 'Aucune sous-étape n’a encore d’animation.'],
  'notice.assembly.allSkipped': ['{n} sub-step(s) with no animation are left out.', '{n} sous-étape(s) sans animation sont omises.'],
  // "Back to the start of the step" was the rewind arrow's label. START is a button now,
  // and a button that says where it goes needs no second name for it.
  'notice.assembly.scrub': ['Position in the step', 'Position dans l’étape'],
  'notice.assembly.playFast': ['Playing speed (×0.5 to ×8)', 'Vitesse de lecture (×0,5 à ×8)'],
  // The views a shot's rule weighed, as pictures under the viewer (see ShotCandidates).
  'notice.assembly.candidates.title': ['Camera views weighed', 'Vues de caméra évaluées'],
  'notice.assembly.candidates.chosen': ['chosen', 'choisie'],
  'notice.assembly.candidates.onScreen': ['on screen', "à l'écran"],
  'notice.assembly.candidates.shot': ['Shot {n}/{of}', 'Plan {n}/{of}'],
  // What share of the thing the shot is of has a standing part between it and the camera, sampled
  // at the start of its way in and halfway (see hiddenFrom).
  'notice.assembly.candidates.hidden': ['{n} % of {what} hidden', '{n} % caché : {what}'],
  'notice.assembly.candidates.unmeasured': ['Hidden: not measured', 'Caché : non mesuré'],
  'notice.assembly.candidates.movement': ['The movement', 'Le mouvement'],
  'notice.assembly.candidates.prep': ['The hardware prepared on it', 'La quincaillerie préparée dessus'],
  'notice.assembly.candidates.next': ['The second hardware', 'La deuxième quincaillerie'],
  'notice.assembly.candidates.last': ['The third hardware', 'La troisième quincaillerie'],
  'notice.assembly.candidates.nextPrep': ['The hardware prepared on the second hardware', 'La quincaillerie préparée sur la deuxième'],
  // Under the viewport's one spinner: the meshes coming, or the parts being put in place.
  'notice.assembly.waitLoading': ['Loading the 3D models', 'Chargement des modèles 3D'],
  'notice.assembly.waitPlacing': ['Placing the parts', 'Mise en place des pièces'],
  // THE CLEARANCE READING under the transport — see ClashBar. Four words for where the two
  // surfaces stand at the moment on screen, and one verdict for the movement as a whole.
  'notice.clash.measuring': ['Measuring clearance…', 'Mesure des jeux…'],
  'notice.clash.clear': ['Clear', 'Dégagé'],
  'notice.clash.touching': ['Touching', 'En contact'],
  'notice.clash.overlap': ['Parts overlap', 'Pièces qui se traversent'],
  // The last millimetres of a threaded travel, where the fastener is inside a bore its mesh
  // does not have. Not measured, and said rather than left blank.
  'notice.clash.engaged': ['Thread engaged', 'Filetage engagé'],
  'notice.clash.apart': ['{mm} mm apart', '{mm} mm d’écart'],
  'notice.clash.deep': ['{mm} mm through', '{mm} mm de traversée'],
  'notice.clash.none': ['Nothing overlaps in this movement', 'Aucune traversée dans ce mouvement'],
  'notice.clash.worst': ['Deepest {mm} mm at {t}', 'Maximum {mm} mm à {t}'],
  'notice.clash.goto': ['Go there', 'Y aller'],
  // The two axes of that graph, each named once beside its own numbers: millimetres up the
  // side, the transport's own 0-to-1 along the bottom.
  'notice.clash.axisY': ['mm', 'mm'],
  'notice.clash.axisX': ['moment in the step', 'instant de l’étape'],
  'notice.clash.limit': ['limit', 'limite'],
  'notice.clash.graph': [
    'Clearance through the step — press a moment to stop the clip on it',
    'Le jeu au fil de l’étape — appuyer sur un instant y arrête le clip',
  ],
  'notice.clash.nothing': [
    'Nothing to check: this step moves one part and there is nothing for it to meet.',
    'Rien à vérifier : cette étape déplace une seule pièce, sans rien à rencontrer.',
  ],
  // THE ONE PRESS THAT CLEARS THE PICTURE — the boxes, the face names, the origin crosses and
  // the gizmo, out together. Named by what pressing it does, not by what it is showing now.
  // WHICH PART IS WHICH, over its name on the card. The movement's own vocabulary — what
  // stays, what travels, what turns it — beside the manual's name for the same thing.
  // The reload button, where the sub-step has an answer to be put back to. It says what it
  // discards, because it discards something somebody typed.
  'notice.assembly.reset': [
    'Draw it again, and put both parts back where the analysis placed them',
    'Redessiner et remettre les deux pièces là où l’analyse les avait placées',
  ],
  'notice.assembly.role.target': ['Target', 'Pièce fixe'],
  'notice.assembly.role.moving': ['Moving', 'Pièce mobile'],
  'notice.assembly.role.tool': ['Tool', 'Outil'],
  'notice.assembly.role.prep': ['Prepared', 'Préparée'],
  'notice.assembly.role.next': ['Then', 'Ensuite'],
  // The two halves of the marks switch (see MarksButton): the parts alone, or with their
  // measurements and marks.
  'notice.assembly.partsOnly': ['Parts only', 'Pièces seules'],
  'notice.assembly.withMarks': ['With marks', 'Avec repères'],
  'notice.run.delete': ['Delete this run', 'Supprimer ce run'],
  'notice.run.deleteN': ['Delete run {n}', 'Supprimer le run {n}'],
  'notice.run.deletePublished': [
    'The live page was published from this run.',
    'La page en ligne vient de ce run.',
  ],
  'notice.run.deleteFailed': [
    'That run could not be deleted.',
    'Ce run n\'a pas pu être supprimé.',
  ],
  'notice.run.waiting': ['Waiting on previous steps', 'En attente des étapes précédentes'],
  'notice.run.held': ['Paused before this step', 'En pause avant cette étape'],
  'notice.listing.setView': [
    'Which side this picture shows — click to correct it',
    'Quel côté cette photo montre — cliquez pour corriger',
  ],
  'notice.cat.group.manual': ['Manual', 'Notice'],
  'notice.cat.group.infos': ['Infos', 'Infos'],
  'notice.cat.group.sections': ['Sections', 'Sections'],
  'notice.3d.matApplyStandard': ['Apply to the hardware & tools', 'Appliquer à la quincaillerie et aux outils'],
  'notice.defaultMaterials.add': ['Add a standard material', 'Ajouter un matériau standard'],
  'notice.defaultMaterials.remove': ['Remove', 'Supprimer'],
  'notice.video.templateLabel': ['Prompt & Context', 'Prompt & Contexte'],
  'notice.publish.title': ['Public tutorial', 'Tutoriel public'],
  // The 3D pipeline's version of the same block. It waits for a mesh because a mesh is the
  // last thing that pipeline actually makes — asking it for a video would be asking it for
  // the pipeline it replaced.
  'notice.publish.needModel': [
    'Reconstruct at least one part in 3D before publishing.',
    'Reconstruisez au moins une pièce en 3D avant de publier.',
  ],
  'notice.publish.needClip': [
    'Validate at least one sub-step in Phase 6 before publishing.',
    'Validez au moins une sous-étape en phase 6 avant de publier.',
  ],
  'notice.publish.publish': ['Publish tutorial', 'Publier le tutoriel'],
  'notice.publish.unpublish': ['Unpublish', 'Dépublier'],
  'notice.publish.republish': ['Update', 'Mettre à jour'],
  'notice.publish.copy': ['Copy link', 'Copier le lien'],
  'notice.publish.copied': ['Copied!', 'Copié !'],
  'notice.publish.downloadQr': ['Download QR (SVG)', 'Télécharger le QR (SVG)'],
  'notice.publish.error': [
    'Something went wrong. Please try again.',
    'Une erreur est survenue. Veuillez réessayer.',
  ],
  'notice.publish.success': ['Published page updated.', 'Page publiée mise à jour.'],
  'notice.memory.tab': ['App analytics', 'App analytics'],
  'notice.memory.title': ['Memory asked of the device, per screen', 'Mémoire demandée à l\u2019appareil, par écran'],
  'notice.memory.loadFailed': ['The figures could not be read. Reload to try again.', 'Les mesures n\u2019ont pas pu être lues. Rechargez pour réessayer.'],
  'notice.memory.explain': [
    'One pass of one device, screen by screen in the order it happened: the app\u2019s phases and the tutorial\u2019s screens, with the memory peak of each.',
    'Une passe d\u2019un appareil, écran par écran dans l\u2019ordre où elle s\u2019est faite : les phases de l\u2019application et les écrans du tutoriel, avec le pic de mémoire de chacun.',
  ],
  'notice.memory.howCounted': [
    'Each 3D view keeps its own copy of the geometry and of the textures, and the page keeps one original of the geometry, shared by its views. The total adds the three. Beside it, what the page keeps besides: every mesh decoded since it opened, and the files downloaded in advance. Those two columns only grow.',
    'Chaque vue 3D garde sa propre copie de la géométrie et des textures, et la page garde un original de la géométrie, partagé par ses vues. Le total additionne les trois. À côté, ce que la page garde en plus : tous les maillages décodés depuis son ouverture, et les fichiers téléchargés d\u2019avance. Ces deux colonnes ne font que monter.',
  ],
  'notice.memory.pickDevice': ['Choose a device', 'Choisir un appareil'],
  'notice.memory.noDevice': ['No device measured yet', 'Aucun appareil mesuré pour l\u2019instant'],
  'notice.memory.namePlaceholder': ['Name this device', 'Nommer cet appareil'],
  'notice.memory.installed': ['installed app', 'app installée'],
  'notice.memory.pickRun': ['Choose a pass', 'Choisir une passe'],
  'notice.memory.screens': ['screens', 'écrans'],
  'notice.memory.deaths': ['pages killed', 'pages tuées'],
  'notice.memory.time': ['Time', 'Heure'],
  'notice.memory.pageCol': ['Page', 'Page'],
  'notice.memory.pageApp': ['App', 'Application'],
  'notice.memory.pageTutorial': ['Tutorial', 'Tutoriel'],
  'notice.memory.screen': ['Screen', 'Écran'],
  'notice.memory.views': ['3D views at the peak', 'Vues 3D au pic'],
  'notice.memory.total': ['Total (MB)', 'Total (MB)'],
  'notice.memory.geometry': ['Geometry, copies in the 3D views (MB)', 'Géométrie, copies des vues 3D (MB)'],
  'notice.memory.page': ['Geometry, the page\u2019s original (MB)', 'Géométrie, original de la page (MB)'],
  'notice.memory.textures': ['Textures in the 3D views (MB)', 'Textures des vues 3D (MB)'],
  'notice.memory.kept': ['Meshes decoded in the page, shown or not (MB)', 'Maillages décodés gardés par la page, affichés ou non (MB)'],
  'notice.memory.files': ['Downloaded files the page keeps (MB)', 'Fichiers téléchargés gardés par la page (MB)'],
  'notice.memory.created': ['3D views created since the page opened', 'Vues 3D créées depuis l\u2019ouverture'],
  'notice.memory.awaiting': ['Released, not yet destroyed by the browser', 'Libérées, pas encore détruites par le navigateur'],
  'notice.memory.awaitingMb': ['Their drawing surfaces (MB)', 'Leurs surfaces de dessin (MB)'],
  'notice.memory.lost': ['3D views lost', 'Vues 3D perdues'],
  'notice.memory.killed': ['Page killed', 'Page tuée'],
  'notice.memory.killedHere': ['Killed here', 'Tuée ici'],
  'notice.memory.newPage': ['New page', 'Nouvelle page'],
  'notice.memory.newPageAfterKill': ['New page, reloaded after the one above was killed', 'Nouvelle page, rechargée après la mort de celle du dessus'],
  'notice.memory.splash': ['Home (colourways)', 'Accueil (coloris)'],
  'notice.memory.tools': ['Tools', 'Outils'],
  'notice.memory.parts': ['Parts', 'Pièces'],
  'notice.memory.done': ['Finished', 'Terminé'],
  'notice.memory.step': ['Step', 'Étape'],
  'notice.public.loading': ['Loading…', 'Chargement…'],
  'notice.public.next': ['Next', 'Suivant'],
  // The pair of "Next", so it is named against it: one sub-step earlier, not out of the
  // tutorial. "Back" reads like a way out of the page, which this button never is.
  'notice.public.back': ['Previous', 'Précédent'],
  'notice.public.notFound': [
    'This tutorial is not available.',
    "Ce tutoriel n'est pas disponible.",
  ],
  'notice.public.notFoundHint': [
    'It may have been unpublished, or the link is incorrect.',
    'Il a peut-être été dépublié, ou le lien est incorrect.',
  ],
  'notice.public.empty': [
    'No videos have been published for this tutorial yet.',
    "Aucune vidéo n'a encore été publiée pour ce tutoriel.",
  ],
  'notice.public.noVideo': ['Video coming soon', 'Vidéo à venir'],
  'notice.public.language': ['Language', 'Langue'],
  'notice.public.detected': ['(detected)', '(détecté)'],
  'notice.public.ready': ['Ready!', 'Prêt\u00a0!'],
  'notice.public.play': ['Play', 'Lire'],
  'notice.public.steps': ['steps', 'étapes'],
  'notice.public.minutes': ['minutes', 'minutes'],
  'notice.public.toolsTitle': ['Your tools', 'Vos outils'],
  'notice.public.toolsLead': ['For this manual you will need:', 'Pour cette notice vous aurez besoin de :'],
  'notice.public.supplied': ['supplied', 'fourni'],
  'notice.public.notSupplied': ['not supplied', 'non fourni'],
  'notice.public.partsTitle': ['Your parts', 'Vos pièces'],
  'notice.public.partsLead': ['{n} items', '{n} éléments'],
  'notice.public.chooseVariant': ['Choose your model', 'Choisissez votre modèle'],
  'notice.public.seconds': ['seconds', 'secondes'],
  'notice.public.finish': ['Finish', 'Terminer'],
  // The two small presses at the ends of the row under the picture.
  // The screen after the last gesture: the product standing assembled, and no instruction.
  'notice.public.doneTitle': ['Assembly complete', 'Montage terminé'],
  'notice.public.doneText': ['Your product is assembled.', 'Votre produit est monté.'],
  // The same picture's tile in the assembly phase, on a line of its own under the steps (named
  // by notice.assembly.end) — and its tooltip while there is no such picture yet.
  'notice.assembly.endNotYet': [
    'The assembled product is shown here once the last sub-step is signed off.',
    'Le produit monté s’affiche ici une fois la dernière sous-étape validée.',
  ],
  'notice.public.congrats': ['Well done!', 'Bravo !'],
  'notice.public.timeLabel': ['Time', 'Temps'],
  'notice.public.home': ['Back to home', "Retourner à l'accueil"],
  'notice.steps.reconTitle': ['Parts reconciliation', 'Vérification des pièces'],
  'notice.steps.reconOk': [
    'Every supplied part is used exactly as many times as provided.',
    'Chaque pièce fournie est utilisée exactement autant de fois que fournie.',
  ],
  'notice.steps.reconBad': [
    'part(s) do not total their supplied quantity.',
    'pièce(s) ne totalisent pas leur quantité fournie.',
  ],
  // THE SUB-STEP CLAIM, which belongs to the phase that draws sub-steps. The two above are
  // about the step totals against the supplied counts, which is the phase before it.
  'notice.steps.reconSubOk': [
    'Every step’s parts are shared out among its sub-steps exactly.',
    'Les pièces de chaque étape sont réparties exactement entre ses sous-étapes.',
  ],
  'notice.steps.reconSubBad': [
    'part(s) do not add up across their step’s sub-steps.',
    'pièce(s) ne totalisent pas leur quantité sur les sous-étapes de leur étape.',
  ],
  // WHILE THE RUN IS ON: no verdict, and a word per step about where it is. The table is
  // being rewritten underneath, so what it says about itself is "working", not a tick.
  'notice.steps.reconRunning': [
    'Reading each step’s sub-steps…',
    'Lecture des sous-étapes de chaque étape…',
  ],
  'notice.steps.subRunning': ['reading this step…', 'lecture de cette étape…'],
  'notice.steps.subQueued': ['waiting its turn', 'en attente de son tour'],
  // One line per disagreement, under the count: the step, the part, and the two numbers that
  // do not meet. The step and the part are as far as the check can point — the shortfall
  // belongs to the whole set of that step's sub-steps, not to any one of them.
  'notice.steps.reconSubBadLine': [
    '{got} across its sub-steps, {want} in the step.',
    '{got} sur ses sous-étapes, {want} dans l’étape.',
  ],
  'notice.steps.reconSubPending': [
    '{n} step(s) not analysed yet, and not counted here.',
    '{n} étape(s) pas encore analysée(s), et non comptée(s) ici.',
  ],
  // One row where there were two, at opposite ends of a table several screens tall. The
  // question is whether the two match, and that is a comparison nobody makes between two
  // numbers they cannot see at once.
  // What a sub-step's holes say, drawn one line per hole in the Phase 4 review.
  // The heading over the sub-step's own sentence, in Phase 4 and Phase 5 alike.
  'notice.steps.instructions': ['Instructions', 'Instructions'],
  // THE NAVIGATOR'S FIRST PLACE, which is not a sub-step: all of them at once, in a table.
  // The first position in the walk: the whole manual as one object, its parts under it.
  'notice.steps.treeAll': ['Tree view (complete)', 'Vue arborescente (complète)'],
  'notice.steps.tree': ['Tree view', 'Vue arborescente'],
  'notice.steps.allSteps': ['All steps', 'Toutes les étapes'],
  'notice.steps.treeStep': ['Tree view (step {n})', 'Vue arborescente (étape {n})'],
  'notice.steps.treeZoom': ['Open the tree full width', 'Ouvrir l’arbre en pleine largeur'],
  'notice.steps.treeSave': ['Save the tree as an image', 'Enregistrer l’arbre en image'],
  'notice.steps.treeZoomIn': ['Zoom in', 'Zoomer'],
  'notice.steps.treeZoomOut': ['Zoom out', 'Dézoomer'],
  'notice.steps.treeNone': [
    'Nothing assembled yet — the sub-steps have to say what each gesture leaves.',
    'Rien d’assemblé pour l’instant — les sous-étapes doivent dire ce que chaque geste laisse.',
  ],
  // A finished run's duration on the button that made it — "took", so it cannot be read as
  // an estimate of what the next press will cost.
  'notice.steps.tookTime': ['(took {t})', '(a pris {t})'],
  'notice.steps.allSubs': ['All sub-steps', 'Toutes les sous-étapes'],
  // Its two columns. "Says" rather than "Instruction": the sentence is Phase 4's own writing,
  // and the manual's printed instruction is a different line shown a screen away.
  'notice.steps.colSub': ['Sub-step', 'Sous-étape'],
  'notice.steps.colSays': ['What it says to do', 'Ce qu’elle dit de faire'],
  // AND WHICH OF THE FIVE IT IS, between the sentence and the parts — the claim those two are
  // checked against. One word, because the cells under it are two tokens wide and the sentence
  // column is the one that should keep the width.
  'notice.steps.colKind': ['Kind', 'Type'],
  // AND WHAT IT LEAVES, last in the row. Named for the piece rather than for the field — a
  // reader sees "prepared screw" under this heading, not a value — so "Output" and not
  // "Result", which reads as a verdict on the gesture.
  'notice.steps.colOutput': ['Output', 'Résultat'],
  // On a violet chip in a later gesture's parts: this is not the loose part any more, it is
  // what an earlier sub-step made of it.
  'notice.steps.subFromEarlier': [
    'Assembled by an earlier sub-step',
    'Assemblé par une sous-étape précédente',
  ],
  // A gesture taking something the manual has not made, or has already built into
  // something else. The two columns disagree and the app cannot tell which is right.
  // A gesture doing to several units at once what a person does to one at a time. The parts
  // are right; the cut is not, and a cycle is what the cut should have been.
  'notice.steps.subAtOnce': [
    'Several handled in one gesture: a gesture takes one of each thing it names.',
    'Plusieurs à la fois : un geste prend une unité de chaque chose qu’il nomme.',
  ],
  'notice.steps.subMissing': [
    'Nothing on the table to take: this piece has not been made yet, or was used up earlier.',
    'Rien à prendre sur la table : cette pièce n’est pas encore assemblée, ou a déjà été utilisée.',
  ],
  // AND THE BANNER OVER EACH RUN OF THEM. What repeats is the run rather than any one row —
  // three gestures done to each of two side rails is one pair of rails, not three doublings —
  // so the cycle is announced once above the rows it governs and has no column. A run that is
  // nobody's cycle is announced in the same words, because "once" is an answer to the same
  // question and a reader should not have to notice an absence to get it.
  // PHASE 5'S HEADING over the row of ticked chips, where the three groups are one row and
  // the panel has no columns to name them with. The sub-step TABLE splits them into three
  // columns and names each — the three keys below.
  'notice.steps.partsTools': ['Parts & tools needed', 'Pièces & outils nécessaires'],
  // THE THREE GROUPS OF WHAT A GESTURE HAS IN HAND (PART_GROUPS), as column headings. The
  // words are the ones the five kinds are written in — "component part", "hardware part",
  // "tool" — because the kind column two places to the left states its rule by counting them,
  // and a rule about component parts is unreadable over a column called something else.
  // WHAT IS STILL ON THE TABLE after a gesture — the last column, and the only one that is
  // about the state between two rows rather than about the row it sits in.
  // WHICH END OF THE FITTING A PART IS, abbreviated because it rides inside a chip's label
  // beside the part's own name and its letter — the words in full would double the chip.
  'notice.steps.role.added': ['(add.)', '(aj.)'],
  'notice.steps.role.receiving': ['(rec.)', '(réc.)'],
  'notice.steps.role.prepared': ['(prep.)', '(prép.)'],
  'notice.steps.colAvailable': ['Available parts', 'Pièces disponibles'],
  'notice.steps.colComponents': ['Component parts', 'Pièces'],
  'notice.steps.colHardware': ['Hardware parts', 'Visserie'],
  'notice.steps.colTools': ['Tools needed', 'Outils nécessaires'],
  // THE FIVE KINDS OF GESTURE, named for the reader. The list itself — what each kind takes
  // and what it leaves — is in subStepKinds, in English, because that copy is written into
  // the prompt; these are the same five for the two languages on screen, and they are the
  // whole of the field's UI — a column of its own in the sub-step table, and a chip in Phase 5.
  //
  // Each names the gesture in full, and four of the five open with their family's own word, so
  // a reader who wants only "does this fasten anything" has it off the first word. The fifth
  // says "Screwing", which is that answer said better than the family word would say it — the
  // colour carries the family for all five anyway (see subStepKindTone). These ARE the label
  // now: they used to hide in a title attribute behind "COMBINING 5". And SHORT, because the
  // KIND column is one narrow cell beside a sentence: "Combining a component part & a hardware
  // part" set in it is six lines of stacked words, which is a paragraph where a label was
  // wanted. The nouns carry the vocabulary — component, hardware — and every other word of
  // those phrases was said twice.
  //
  // The bare family words stay for the sub-step nobody asked the question of — Phase 5 can
  // still tell the two apart off a movement's ids, and that much is worth saying.
  'notice.steps.family.placing': ['Placing', 'Positionnement'],
  'notice.steps.family.combining': ['Combining', 'Assemblage'],
  // The window a chip opens: what the piece is made of, one level down.
  'notice.steps.madeOfIn': ['Assembled in sub-step {n}, from:', 'Assemblé à la sous-étape {n}, à partir de :'],
  'notice.steps.madeOfBase': [
    'This is a base part (depth = 0). No smaller components.',
    'Pièce de base (profondeur = 0). Aucun composant plus petit.',
  ],
  'notice.steps.madeOfOpen': ['What is this made of?', 'De quoi est-ce fait ?'],
  // FOUR, RENUMBERED. "Placing one part" was the 1 and is gone — a gesture that joins
  // nothing draws as a lone part in empty space, which is not a thing anybody can watch or
  // sign off. The others moved up rather than leaving a hole at the top; see subStepKinds,
  // and the migration that moved the stored answers with them.
  'notice.steps.kind.1': ['Placing two parts', 'Placement de deux pièces'],
  'notice.steps.kind.2': ['Combining two hardwares', 'Assemblage de deux visseries'],
  'notice.steps.kind.3': ['Combining component & hardware', 'Assemblage pièce & visserie'],
  'notice.steps.reconUsedSupplied': ['Used / Supplied', 'Utilisées / Fournies'],
  'notice.steps.reconDescription': ['Description', 'Description'],
  'notice.steps.reconPrinted': ['Printed text', 'Texte imprimé'],
  'notice.steps.toolLegend': [
    'a tool that step calls for — used, never used up, so it is not totalled.',
    "un outil que l'étape demande — utilisé, jamais consommé, donc pas totalisé.",
  ],
  'notice.steps.subHandled': [
    'Handled here, used up by another sub-step',
    'Manipulée ici, consommée par une autre sous-étape',
  ],
  'notice.steps.subHandledLegend': [
    'handled by that action without being used up — it is fixed in another sub-step.',
    "manipulée par ce geste sans être consommée — elle est fixée dans une autre sous-étape.",
  ],
  'notice.steps.reconSkipped': [
    'part(s) have no supplied quantity and were not checked.',
    'pièce(s) sans quantité fournie, non vérifiées.',
  ],
  'notice.steps.reconUnknownQty': [
    'Some steps use a part without a stated quantity, so totals may be approximate.',
    'Certaines étapes utilisent une pièce sans quantité indiquée ; les totaux peuvent être approximatifs.',
  ],
  'notice.err.rendering': [
    'The page is still rendering. Try again in a moment.',
    'La page est encore en cours de rendu. Réessaie dans un instant.',
  ],
  'notice.err.generic': [
    'Something went wrong. Please try again.',
    'Une erreur est survenue. Réessaie.',
  ],
  // WHAT A FAILED CALL SAYS WHEN THE SERVER SENT NO WORDS OF ITS OWN. It used to be
  // axios's: "Request failed with status code 502", a sentence about HTTP under a button
  // about a manual. The code is kept — it is what tells a server that is down from one that
  // refused — and the rest says what to do.
  'notice.err.noAnswer': [
    'The server did not answer at all. Check the connection and try again.',
    'Le serveur n’a pas répondu du tout. Vérifie la connexion et réessaie.',
  ],
  'notice.err.gateway': [
    'The server did not answer this call ({code}). It is probably restarting, or the call outran the proxy. Try again in a moment.',
    'Le serveur n’a pas répondu à cet appel ({code}). Il redémarre probablement, ou l’appel a dépassé le délai du proxy. Réessaie dans un instant.',
  ],
  'notice.err.status': [
    'The server refused this call ({code}).',
    'Le serveur a refusé cet appel ({code}).',
  ],
  // WHICH CALL FAILED, wrapped round the why. The message appears under a phase that runs
  // several different things, and "the server is restarting" says nothing about whether the
  // list, one part or the sizes is the thing to press again.
  'notice.parts.listFailed': [
    'The supplied parts could not be listed. {why}',
    'Le matériel fourni n’a pas pu être listé. {why}',
  ],
  'notice.parts.oneFailed': [
    'That part could not be read again. {why}',
    'Cette pièce n’a pas pu être relue. {why}',
  ],
  'notice.cat.searchNotice': ['Read manual', 'Lire la notice'],
  'notice.cat.searchListing': ['Read listings', 'Lire les annonces'],
  'notice.cat.runAllLinks': ['All links', 'Tous les liens'],
  'notice.cat.model': ['Model', 'Modèle'],
  'notice.cat.total': ['Total', 'Total'],
  'notice.cat.cost': ['Total API cost', 'Coût API total'],
  'notice.cat.time': ['Avg. time', 'Temps moyen'],
  'notice.cat.calls': ['Total calls', "Nombre total d'appels"],
  'notice.cat.tokens': ['Total tokens', 'Tokens totaux'],
  'notice.cat.tokensUnits': ['(in / out / thinking)', '(entrée / sortie / raisonnement)'],
  'notice.cat.thinking': ['Thinking tokens', 'Tokens de raisonnement'],
  'notice.cat.noReasoning': [
    'No reasoning available for this page.',
    'Aucun raisonnement disponible pour cette page.',
  ],
  'notice.cat.reasoningTitle': ['Reasoning', 'Raisonnement'],
  // How long the call behind this panel took, at the top of it. Its own key rather than
  // Phase 4's "(took {t})" beside the run button: that one is a parenthesis after a
  // label, this one is a line of its own.
  'notice.cat.tookTime': ['Took {t}', 'A pris {t}'],
  'notice.cat.rawOutputTitle': ['Raw output', 'Sortie brute'],
  'notice.cat.rawUnreadable': [
    'The model stopped mid-answer: what came back is not readable, so nothing was taken from it.',
    "Le modèle s'est arrêté en cours de réponse : ce qui est revenu n'est pas lisible, rien n'en a été retenu.",
  ],

  // THE SANDBOX'S SAVES — what the reader looked like, one screen per phase.
  'notice.saves.title': ['Saves', 'Sauvegardes'],
  'notice.saves.intro': [
    'A save is one picture per phase of the Notices tab, taken top to bottom, with the '
    + 'commit the sandbox was standing on. It is how a design idea is compared with the '
    + 'one before it. Saves survive a sandbox reset.',
    'Une sauvegarde est une image par phase de l\'onglet Notices, prise de haut en bas, '
    + 'avec le commit sur lequel se trouvait le bac à sable. C\'est ainsi qu\'une idée de '
    + 'design se compare à la précédente. Les sauvegardes survivent à une remise à zéro.',
  ],
  'notice.saves.take': ['Save current version', 'Sauvegarder la version actuelle'],
  'notice.saves.naming': ['What is this version? (optional)', 'Quelle est cette version ? (facultatif)'],
  'notice.saves.noDoc': [
    'Open a manual in the Notices tab first — a save is a picture of one manual\'s phases.',
    'Ouvrez d\'abord une notice dans l\'onglet Notices — une sauvegarde est une image des '
    + 'phases d\'une notice.',
  ],
  'notice.saves.none': ['No saves yet.', 'Aucune sauvegarde.'],
  'notice.saves.screens': ['{n} screens', '{n} écrans'],
  'notice.saves.partial': ['{n} of {m} screens', '{n} écrans sur {m}'],
  'notice.saves.open': ['Open', 'Ouvrir'],
  'notice.saves.hide': ['Hide', 'Masquer'],
  'notice.saves.delete': ['Delete', 'Supprimer'],
  'notice.saves.rename': ['Rename', 'Renommer'],
  'notice.saves.confirmDelete': [
    'Delete this save and its screens?', 'Supprimer cette sauvegarde et ses écrans ?',
  ],
  'notice.saves.walking': ['Saving screen {n} of {m}: {phase}', 'Écran {n} sur {m} : {phase}'],
  'notice.saves.walkDone': ['Saved.', 'Sauvegardé.'],
  'notice.saves.stop': ['Stop', 'Arrêter'],
  'notice.saves.walkFailed': ['That save could not be finished: {err}', 'Sauvegarde inachevée : {err}'],
  'notice.saves.onCommit': ['on {c}', 'sur {c}'],
  'notice.cat.sectionEnd': ['end', 'fin'],
  'notice.cat.sectionStart': ['start', 'début'],
  'notice.cat.sectionOngoing': ['in progress', 'en cours'],
  'notice.cat.priceIn': ['Input', 'Entrée'],
  'notice.cat.priceOut': ['Output', 'Sortie'],
  'notice.cat.modelId': ['Model id', 'ID du modèle'],
  'notice.cat.stop': ['Stop', 'Arrêter'],
  'notice.stop.asked': ['Stopping…', 'Arrêt demandé…'],
  'notice.cat.inFlight': ['in progress', 'en cours'],
  'notice.cat.run': ['Run', 'Run'],
  'notice.cat.prevRun': ['Previous run', 'Run précédent'],
  'notice.cat.nextRun': ['Next run', 'Run suivant'],
  'notice.cat.failed': ['classification(s) failed', 'classification(s) échouée(s)'],
  'notice.cat2.noRun': [
    'Run the classification first (Step 1).',
    "Lance d'abord la classification (Étape 1).",
  ],
  'notice.parts.run': ['List the supplied parts', 'Lister le matériel'],
  'notice.parts.runAll': ['List all parts', 'Lister toutes les pièces'],
  // THE STRIP'S TABS FOR ONE KIND OF PART — see PartsPhase's showKind.
  'notice.parts.kindComponents': ['Components', 'Composants'],
  'notice.parts.kindHardware': ['Hardware & covers', 'Quincaillerie et caches'],
  'notice.parts.kindTools': ['Tools', 'Outils'],
  'notice.parts.runOne': ['List {part}', 'Lister {part}'],
  'notice.parts.thisPart': ['this part', 'cette pièce'],
  'notice.parts.oneNotFound': [
    'That part was not found on its page this time. Re-reading '
        + 'every part would find it, but it would also rewrite the others.',
    "Cette pièce n'a pas été retrouvée sur sa page cette fois. "
        + 'Relire toutes les pièces la retrouverait, mais réécrirait aussi les autres.',
  ],
  'notice.parts.suppliedTitle': ['Supplied parts', 'Matériel fourni'],
  'notice.parts.stepsNeeds': [
    'Needs the supplied parts and the assembly steps first.',
    "Il faut d\u2019abord le matériel fourni et les étapes d\u2019assemblage.",
  ],
  'notice.parts.stepsTitle': ['Steps overview', 'Aperçu des étapes'],
  'notice.parts.instrTitle': ['What each step says', 'Ce que dit chaque étape'],
  'notice.hardwareTable.badge': ['Joints table', 'Table des joints'],
  'notice.parts.callShare': ['Sharing out the parts & tools', 'Répartition des pièces et des outils'],
  'notice.parts.stepRow': ['Step', 'Étape'],
  'notice.parts.pass.first': ['1st inference', '1re inférence'],
  'notice.parts.pass.second': ['2nd inference', '2e inférence'],
  'notice.parts.pass.off': ['Boxes hidden', 'Repères masqués'],
  'notice.parts.bag': ['Bag', 'Sachet'],
  'notice.parts.letter': ['Letter', 'Lettre'],
  'notice.parts.ref': ['Ref. code', 'Code réf.'],
  'notice.parts.name': ['Name', 'Nom'],
  'notice.parts.rename': ['Click to rename', 'Cliquer pour renommer'],
  // NAMED FOR WHOSE ANSWER IT IS. This row is the size the MANUAL gives — read off its
  // dimension drawings, or estimated from a photograph against a part that was measured — and
  // it is not the only size a part has: the 3D model measures its own, they are allowed to
  // differ, and the gap between them is a reading worth keeping (see partModelSize). Called
  // just "Size" it read as the part's one size, and the mesh's figures were merged into it.
  // A screw's point (see ScrewTip): pointed, it makes its own hole and no part is bored for it.
  'notice.parts.tip': ['Point', 'Pointe'],
  'notice.parts.tipPointed': ['Pointed', 'Pointue'],
  'notice.parts.tipBlunt': ['Flat', 'Plate'],
  'notice.parts.tipUnknown': ['Not known', 'Inconnue'],
  'notice.parts.size': ['Size read/estimated', 'Taille lue/estimée'],
  'notice.listing.dimensioned': ['Sizes', 'Cotes'],
  'notice.listing.dimensionedTip': [
    'The model read measurements on this picture.',
    'Le modèle a lu des cotes sur cette image.',
  ],
  'notice.parts.dimsNoDrawing': [
    'No dimension drawing was found in the listing. Research the product first.',
    "Aucun schéma coté trouvé dans la fiche produit. Lancez d'abord la recherche produit.",
  ],
  'notice.parts.dimsPrompt': ['What the model is asked', 'Ce qui est demandé au modèle'],
  // The extraction's own prompt — the call that reads the supplied-parts section.
  // The three row buttons whose call had no badge until now. One label each, and each says
  // what its own call is FOR — "Prompt" alone would be three identical badges on one table.
  'notice.parts.redoPrompt': [
    'What the model is asked to re-locate',
    'Ce que le modèle doit re-localiser',
  ],
  // What each preview's own picture IS. These prompts all say "THIS image" and showed none,
  // which is a preview contradicting its only load-bearing sentence.
  // The second of the two drawing rows: the part's own portrait above, the part as the
  // steps draw it here.
  // The four calls one press of this phase makes, named in the button while each runs. One
  // word each: the button already carries a label, a count and a clock.
  'notice.parts.stageList': ['parts', 'pièces'],
  'notice.parts.stageSizes': ['sizes', 'tailles'],
  'notice.parts.stageCrops': ['crops', 'découpes'],
  'notice.parts.stageManual': ['manual crops', 'découpes notice'],
  // And each call's name as the caps title over its badge's menu — four identical (i) icons in
  // a row say nothing about which call is which until one is opened.
  'notice.parts.badgeList': ['Parts', 'Pièces'],
  'notice.parts.badgeSize': ['Size', 'Taille'],
  // THE TWO HALVES OF THE PARTS LIST, and the titles say which is which. The photograph call
  // can only find what is left on the outside of an assembled product, which is the components;
  // the hardware is drawn in the manual instead, and has its own call (see hardwareCropsRun).
  'notice.parts.badgeCrops': ['Crops (photos)', 'Découpes (photos)'],
  'notice.parts.badgeHardwareCrops': ['Crops (manual)', 'Découpes (notice)'],
  'notice.parts.hardwareFind': ['What is drawn', 'Ce qui est dessiné'],
  'notice.parts.hardwareMatch': ['Which part each one is', 'Quelle pièce est laquelle'],
  'notice.parts.hardwareFindPrompt': [
    'Boxing every part drawn in an assembly step',
    "Encadrement de chaque pièce dessinée dans une étape",
  ],
  'notice.parts.hardwarePrompt': [
    'Naming each boxed piece of an assembly step',
    "Identification de chaque pièce encadrée d'une étape",
  ],
  'notice.parts.hardwareFindAnswer': [
    'What this step drew',
    'Ce que cette étape a dessiné',
  ],
  'notice.parts.hardwareAnswer': [
    'What each rectangle turned out to be',
    'Ce que chaque rectangle s\'est révélé être',
  ],
  'notice.parts.badgeSchema': ['Schema', 'Schéma'],
  'notice.parts.badgeEstimate': ['Estimate', 'Estimation'],
  // The masks badge's prompt panel, and the caption under its second picture.
  'notice.parts.masksPrompt': ['What the tracer is asked', 'Ce qui est demandé au traceur'],
  'notice.parts.masksHint': ['The whole photograph, the part boxed', 'La photo entière, la pièce encadrée'],
  'notice.parts.redoPicOne': [
    'Page {n} of the manual, whole — one of the pages this search reads',
    'Page {n} de la notice, entière — une des pages que cette recherche lit',
  ],
  'notice.parts.redoPicAll': [
    'Page {n} with every current box drawn and numbered — the tags above are these numbers',
    'Page {n} avec chaque boîte actuelle tracée et numérotée — les tags ci-dessus sont ces numéros',
  ],
  // No longer "the first step's": the panel steps through them with ‹ › and the arrow keys,
  // and the heading would be naming a step that is not on screen. Which step IS on screen is
  // written in the stepper itself, next to the arrows that changed it.
  // The heading over one photograph's answer, where several were read in one press.
  'notice.parts.listingShot': ['Photograph {n}', 'Photographie {n}'],
  'notice.parts.listPrompt': [
    'What the model is asked to list',
    'Ce que le modèle doit lister',
  ],
  // The two kinds of page a parts call carries, named where they are drawn: the section it
  // reads, and the manual it reads it inside. One label each, since the panel shows both
  // groups and a tile with no name is a page number nobody can place.
  'notice.parts.promptPageRead': [
    'Supplied-parts page {n}, masked to its section — the page this call reads',
    'Page matériel fourni {n}, masquée hors de sa section — la page que cet appel lit',
  ],
  'notice.parts.promptPageContext': [
    'Page {n} of the manual, sent as context',
    'Page {n} de la notice, envoyée comme contexte',
  ],
  // The section's own tile says what it IS before it says which page it was cut from — the
  // words the call itself labels that image with. Its page number alone made it a second
  // "PAGE 1" beside the context render of the same page.
  'notice.parts.promptSectionTag': ['Supplied parts', 'Matériel fourni'],
  // And Phase 3's two, named the way each call captions them. The repartition's tiles carry
  // the step and the rank of the drawing within it, because that pair is the only thing the
  // model is given to tell one step's diagram from the next one's; the transcription's carry
  // a page number, because a page is what it is sent and the step is named in the words.
  // Under a step crop opened large. A tool's picture is the one that cannot say what it is
  // doing on its own — the same screwdriver twice — so its partners in that step are written
  // beside it, from the repartition, with no call made.
  // Phase 3's tool-sizing row: a ratio worked out from the drawings, not a call.
  'notice.parts.toolSizeRow': ['Tool sizing', 'Taille des outils'],
  'notice.parts.toolSizeNoRef': [
    'Not drawn against a measured part — Estimate sizes it from its standard instead',
    'Pas dessiné contre une pièce mesurée — Estimation le dimensionne d’après sa norme',
  ],
  'notice.parts.toolSizeIntro': [
    '{n} reading(s), each one drawing of this tool measured against one drawing of a part it works on there. No call was made: the boxes are the step search’s and the millimetres are Phase 2’s.',
    '{n} lecture(s), chacune un schéma de cet outil mesuré contre un schéma d’une pièce qu’il utilise. Aucun appel : les repères viennent de la recherche dans les étapes et les millimètres de la phase 2.',
  ],
  'notice.parts.toolSizePerUnit': ['/page width', '/largeur de page'],
  'notice.parts.toolSizeTool': ['This tool:', 'Cet outil :'],
  'notice.parts.toolSizeMedian': [
    'Median of the readings: {mm} mm long.',
    'Médiane des lectures : {mm} mm de long.',
  ],
  'notice.parts.toolSizeSpread': [
    'The readings disagree by {n}×. A manual that draws its hardware oversized breaks the one thing this assumes — that a drawing is to one scale.',
    'Les lectures divergent d’un facteur {n}. Une notice qui grossit sa visserie brise la seule hypothèse d’ici : qu’un schéma est à une seule échelle.',
  ],
  'notice.parts.toolSizeSaveOne': [
    'Save {mm} mm length for {part}',
    'Enregistrer la longueur {mm} mm pour {part}',
  ],
  // WHICH AXIS the saved length becomes on the mesh — the longest one, which is what
  // mmPerUnit anchors against. Said on the button, because "17 mm" alone does not tell you
  // whether the model is about to be 17 mm long or 17 mm thick.
  'notice.parts.toolSizeAsAxis': [' (as {axis})', ' (comme {axis})'],
  'notice.parts.axisWidth': ['width', 'largeur'],
  'notice.parts.axisDepth': ['depth', 'profondeur'],
  'notice.parts.axisHeight': ['height', 'hauteur'],
  'notice.parts.toolSizeSavedOne': ['Saved — the model is at that length now', 'Enregistré — le modèle est à cette longueur'],
  'notice.parts.toolSizeNote': [
    'Scaled from {n} drawing(s), against {parts}.',
    'Mis à l’échelle depuis {n} schéma(s), contre {parts}.',
  ],
  'notice.parts.cropUsedNone': [
    'This step adds no supplied part.',
    'Cette étape n’ajoute aucune pièce fournie.',
  ],
  // The search looked at THIS drawing and found the tool working on nothing in it — a
  // header, a hand holding it beside the assembly. Not the same as not having asked.
  'notice.parts.cropTouchesNone': [
    'Drawn on its own here — not shown working on a part.',
    'Dessiné seul ici — pas montré en train de travailler sur une pièce.',
  ],
  // And the crop that predates the question: what is listed is the step's parts, which is
  // a weaker claim, so it says so and names the button that would settle it.
  'notice.parts.promptStepBand': [
    'Step {n}, diagram {k} — the band this call reads',
    'Étape {n}, schéma {k} — la bande que cet appel lit',
  ],
  'notice.parts.promptStepPage': [
    'Page {n} of the manual, whole — the step is named in the prompt',
    'Page {n} de la notice, entière — l’étape est nommée dans le prompt',
  ],
  'notice.parts.dimsThinking': [
    'What the model thought, and what it replied',
    "Le raisonnement du modèle, et sa réponse brute",
  ],
  'notice.parts.description': ['Description', 'Description'],
  'notice.parts.qty': ['Quantity', 'Quantité'],
  'notice.parts.type': ['Type', 'Type'],
  'notice.parts.category': ['Category', 'Catégorie'],
  'notice.parts.symmetry': ['Symmetry', 'Symétrie'],
  'notice.parts.sym.none': ['None', 'Aucune'],
  'notice.parts.sym.identical': ['Identical', 'Identiques'],
  'notice.parts.sym.left-right': ['Left/Right', 'Gauche/Droite'],
  'notice.parts.symmetryPick': ['Change the symmetry', 'Changer la symétrie'],
  'notice.parts.part': ['Part', 'Pièce'],
  'notice.parts.tool': ['Tool', 'Outil'],
  'notice.parts.piece': ['Schema', 'Schéma'],
  'notice.parts.inDb': ['Found in database', 'Trouvé en base'],
  'notice.parts.addAllToDb': ['Add all ({n})', 'Tout ajouter ({n})'],
  'notice.parts.addAllTitle': [
    'Add every part not yet in the parts database, with this manual\u2019s names, categories and drawings. A part already there under the same name and category gains this drawing instead of a second row.',
    'Ajouter à la base toutes les pièces qui n\u2019y sont pas encore, avec les noms, catégories et croquis de cette notice. Une pièce déjà présente sous le même nom et la même catégorie reçoit ce croquis au lieu d\u2019une seconde fiche.',
  ],
  'notice.parts.addAllFailed': ['{n} not saved', '{n} non enregistrée(s)'],
  'notice.parts.yes': ['Yes', 'Oui'],
  'notice.parts.seeInDb': ['See this part in the database', 'Voir cette pièce dans la base'],
  'notice.parts.no': ['No', 'Non'],
  'notice.step3.brand': ['Brand', 'Marque'],
  'notice.info.productName': ['Product name', 'Nom du produit'],
  'notice.info.time': ['Estimated time', 'Temps estimé'],
  'notice.info.people': ['People', 'Personnes'],
  'notice.info.maxWeight': ['Max weight', 'Poids maximum'],
  'notice.info.category': ['Category', 'Catégorie'],
  'notice.info.hero': ['Picture', 'Image'],
  'notice.info.site': ['Website', 'Site web'],
  'notice.info.queries': ['Search phrases', 'Recherches'],
  // ONE COLUMN for the two halves of one answer, and a column naming the variant they
  // belong to. A material and its finish describe one surface, and which submodel that
  // surface belongs to is what everything downstream is scoped by.
  'notice.info.submodel': ['Submodel', 'Sous-modèle'],
  'notice.info.materialColour': ['Material & colour', 'Matériau et couleur'],
  'notice.info.edit': ['Click to edit', 'Cliquez pour modifier'],
  'notice.info.pickPrompt': [
    'The manual and the listing disagree. Pick the answer to keep — the next phases wait on it.',
    'La notice et l\'annonce ne disent pas la même chose. Choisissez la réponse à garder — les phases suivantes l\'attendent.',
  ],
  'notice.source.pdf': ['Manual', 'Notice'],
  'notice.source.listing': ['Listing', 'Annonce'],
  // The reader could not load this manual's answers: it is showing defaults and saving
  // nothing, which is worth saying plainly — a run launched here is paid for and lost.
  'notice.state.unsaved': [
    "This manual's answers could not be loaded. Nothing you do here will be saved — including a run.",
    "Les réponses de cette notice n'ont pas pu être chargées. Rien de ce que vous faites ici ne sera enregistré — un lancement compris.",
  ],
  'notice.state.retry': ['Load again', 'Recharger'],
  'notice.state.stillDown': ['Still unreachable.', 'Toujours injoignable.'],
  'notice.listing.label': ['Web listing', 'Annonce en ligne'],
  // The Listings table's first column header (see ListingsTable).
  'notice.listing.link': ['Link', 'Lien'],
  'notice.listing.section': ['Web listings', 'Annonces en ligne'],
  'notice.listing.removeLink': ['Remove this link', 'Retirer ce lien'],
  'notice.listing.placeholder': [
    'Paste the product page link',
    'Collez le lien de la page produit',
  ],
  'notice.listing.addVariant': ['Add a submodel link', 'Ajouter un lien de sous-modèle'],
  // Looking the links up from the manual instead of pasting them.
  'notice.listing.find': ['Find the links', 'Trouver les liens'],
  'notice.listing.addFound': ['Add this link', 'Ajouter ce lien'],
  // One badge per call the search made: what the manual said, then what the pictures said.
  'notice.listing.callMatch': ['Matching the pictures', 'Comparaison des images'],
  'notice.listing.callRead': ['Reading the page and its pictures', 'Lecture de la page et de ses images'],
  'notice.listing.searchError.refused': ['The shop refused the search', 'La boutique a refusé la recherche'],
  'notice.listing.searchError.unreachable': ["The shop's search did not answer", "La recherche de la boutique n'a pas répondu"],
  'notice.listing.searchError.notShopify': [
    "This shop's search cannot be read (not a Shopify shop)",
    'La recherche de cette boutique ne peut pas être lue (pas une boutique Shopify)',
  ],
  'notice.listing.searchError.siteDown': ['The website did not answer', "Le site web n'a pas répondu"],
  'notice.listing.searchError.noQueries': ['No search phrase to type', 'Aucune recherche à saisir'],
  'notice.listing.productDrawing': ["The manual's drawing", 'Le dessin de la notice'],
  'notice.cat.badgeManual': ['Manual', 'Notice'],
  'notice.cat.badgeListings': ['Listings', 'Annonces'],
  'notice.listing.noneFound': [
    'No listing was found for this manual.',
    'Aucune annonce trouvée pour cette notice.',
  ],
  'notice.listing.searchFailed': [
    'The search for the listings failed.',
    'La recherche des annonces a échoué.',
  ],
  'notice.listing.noInfo': [
    'The manual names no website. Read the manual first, or paste the links.',
    "La notice ne donne aucun site web. Lisez d'abord la notice, ou collez les liens.",
  ],
  'notice.listing.noPages': [
    'The manual read found no picture of the finished product, so there is nothing to compare.',
    "La lecture de la notice n'a trouvé aucune image du produit fini, donc rien à comparer.",
  ],
  'notice.listing.failed': ['The listing could not be read.', 'L\'annonce n\'a pas pu être lue.'],
  'notice.listing.noImages': [
    'No image was found in the listing. Re-read this link from the Listings badge.',
    "Aucune image trouvée dans l'annonce. Relisez ce lien depuis le badge Annonces.",
  ],
  'notice.listing.open': [
    'Open the listing in a new tab',
    'Ouvrir l\'annonce dans un nouvel onglet',
  ],
  'notice.listing.gallery': ['Listing gallery', 'Galerie de l\'annonce'],
  'notice.listing.splash': ['Splash', 'Image de couverture'],
  'notice.listing.resetHero': ["Back to the model's pick", 'Revenir au choix du modèle'],
  'notice.listing.researching': ['Searching…', 'Recherche…'],
  'notice.listing.imgFailed': ['Blocked', 'Bloquée'],
  'notice.listing.imgBlocked': [
    'This picture would not load in the browser — the merchant may be blocking hotlinking.',
    "Cette image n'a pas pu se charger dans le navigateur — le marchand bloque peut-être le hotlink.",
  ],
  'notice.listing.imgBlockedBanner': [
    "listing picture(s) would not load — the merchant may be blocking hotlinking (they were fetched fine server-side).",
    "image(s) de l'annonce n'ont pas pu se charger — le marchand bloque peut-être le hotlink (elles ont bien été récupérées côté serveur).",
  ],
  'notice.listing.fromDb': ['From the database.', 'Depuis la base de données.'],
  'notice.listing.splashFailed': [
    'This cover picture could not be loaded. Pick another one below.',
    "Cette image de couverture n'a pas pu être chargée. Choisissez-en une autre ci-dessous.",
  ],
  'notice.gallery.prev': ['Previous picture', 'Image précédente'],
  'notice.gallery.next': ['Next picture', 'Image suivante'],
  'notice.step3.detect': ['Detect', 'Détecter'],
  // NO NUMBER IN THE COPY, and it names the button rather than a place to go. It read
  // "Extract the supplied parts first (Step 2)", which was wrong twice over: the parts list
  // is this phase's own first stage, not an earlier one, and the button that runs it is the
  // one directly above this line. The number was left over from a pipeline where the
  // extraction was step 2 — the same drift the phase-description keys have a note about.
  //
  // {b} is that button's own label, so the sentence cannot come to name a button that says
  // something else.
  'notice.step3.noResults': ['No images found.', 'Aucune image trouvée.'],
  'notice.step3.unsearchable': [
    'Nothing to search with: the manual gives this part neither a reference code nor a name, and the search needs one of the two. Its drawing is shown above.',
    "Rien à chercher : la notice ne donne à cette pièce ni code référence ni nom, or la recherche a besoin de l'un des deux. Son dessin est affiché ci-dessus.",
  ],
  'notice.step3.filtering': ['Filtering real photos...', 'Filtrage des vraies photos...'],
  'notice.step3.kept': ['Kept', 'Gardées'],
  'notice.step3.discarded': ['Discarded', 'Écartées'],
  'notice.step3.listing': ['Listing', 'Annonce'],
  'notice.step3.imageLink': ['Image link', "Lien de l'image"],
  'notice.step3.generateAll': ['Generate images', 'Générer images'],
  'notice.step3.regenerate': ['Regenerate', 'Régénérer'],
  // The row between the manual's drawing and the generated photograph: where each part
  // actually is in the listing's pictures of the assembled product. Most parts are not in
  // them at all — the furniture is built, so the fasteners are inside it.
  'notice.parts.listingCrops': ['LISTING CROPS', 'DÉTAILS ANNONCE'],
  'notice.parts.manualCrops': ['MANUAL CROPS', 'DÉTAILS NOTICE'],
  // The few of those crops GPT-6 Astra keeps per part: different sides, the largest boxes.
  'notice.parts.toggleCrop': [
    'Click to strike this detection through, or to bring it back',
    'Cliquer pour barrer cette détection, ou la rétablir',
  ],
  'notice.parts.listingPrompt': [
    'What is asked of each photograph',
    'La question posée à chaque photo',
  ],
  // The other half of that call, named the same way: the question is asked of every
  // photograph, and the answer belongs to the ONE you have open — which is why this is
  // read from the zoom and not from the row.
  'notice.parts.listingAnswer': [
    'What came back for this photograph',
    'Ce que cette photo a donné',
  ],
  // THE TWO CALLS BEHIND ONE PICTURE, named on the badges that read them back. Two words
  // rather than sentences: they label icons sitting side by side, and what they have to do
  // is tell those icons apart — the panels behind them explain themselves.
  'notice.parts.boxesTitle': ['Boxes', 'Cadres'],
  // THE TWO RECTANGLES ONE ANSWER PRODUCES. "Padded" is what the table stores and what
  // every crop is cut from — the model's box with a margin added on the way in. "Raw" is
  // the box as it was sent, read back out of the reply. Named for what they ARE and not
  // for what they do, because the pair is a comparison rather than a setting.
  'notice.parts.boxesPadded': ['Padded', 'Marge'],
  'notice.parts.boxesRaw': ['Raw', 'Brut'],
  'notice.parts.masksTitle': ['Masks', 'Masques'],
  // How many calls the panel is a list of, and by which model. The count IS the fact: the
  // shapes on the photograph behind it came back one at a time, not together.
  // A shape from before this panel existed: the call was made, its polygons were kept and
  // its words were not. Said plainly, because "no reasoning" would be a claim about the
  // model rather than about what this browser wrote down.
  'notice.parts.masksNoNote': [
    'Traced before answers were kept here — run Find again to record one.',
    'Tracé avant que les réponses ne soient conservées ici — relancer Chercher pour en enregistrer une.',
  ],
  'notice.parts.masksCount': ['{n} call(s) · {model}', '{n} appel(s) · {model}'],
  // The same, where no model is named (the demo).
  'notice.parts.masksCountBare': ['{n} call(s)', '{n} appel(s)'],
  // WHICH instance, where a part was found more than once on one photograph. Its own line
  // over each crop: the calls were separate and their answers can disagree, which is the
  // one thing a stack of pictures under a single heading would hide.
  'notice.parts.masksNth': ['Instance {n} of {of}', 'Occurrence {n} sur {of}'],
  // ONE SHAPE OF ONE PART, under the zoomed photograph — pressing it takes that shape off the
  // picture. Numbered from one, as the reply names them.
  'notice.parts.polygonNth': ['Polygon {n}', 'Polygone {n}'],
  'notice.3d.width': ['width', 'largeur'],
  'notice.3d.depth': ['depth', 'profondeur'],
  'notice.3d.height': ['height', 'hauteur'],
  // THE TWO ENDS OF A WIDTH AND A DEPTH, where the drawing printed both: on the box's bottom
  // edge and on its top edge, each over what that end was supposed to be.
  'notice.3d.bottomWidth': ['bottom width', 'largeur en bas'],
  'notice.3d.topWidth': ['top width', 'largeur en haut'],
  'notice.3d.bottomDepth': ['bottom depth', 'profondeur en bas'],
  'notice.3d.topDepth': ['top depth', 'profondeur en haut'],
  // What the mesh measures along the three lines through a clicked point.
  // The piece of matter the point is in, or the whole line through the part.
  // A QUESTION MARK, not the word. It sits after "wanted depth:" where a number goes, and
  // it is read at a glance beside two lines that DO have numbers — "unknown" is a word to
  // read where "?" is a shape to notice, and the row is asking rather than reporting.
  'notice.3d.unknown': ['?', '?'],
  // WANTED is what the part is SUPPOSED to be — the size record, whatever the manual and
  // the drawing between them established. The line above it is what the mesh on screen
  // actually measures, and the gap between the two is the whole reason both are drawn.
  //
  // THE AXIS IS NOT REPEATED ON IT. It was "wanted depth: 398 mm" sitting directly under
  // "depth: 398 mm" — the same word twice, one line apart, on a label drawn over the part it
  // is measuring. The second line is the second claim about the FIRST line's axis; that is
  // what being under it means, and saying so again buys nothing and costs the width.
  //
  // Which is also why this is one string where it was three. The three existed because
  // French puts the adjective after the noun — "profondeur voulue" — and with the noun gone
  // there is nothing left for the axis to disagree with.
  'notice.3d.wanted': ['wanted', 'voulu'],
  'notice.3d.actualWidth': ['3D width', 'largeur 3D'],
  'notice.3d.actualDepth': ['3D depth', 'profondeur 3D'],
  'notice.3d.actualHeight': ['3D height', 'hauteur 3D'],
  // PAINTING A CONVERTED CAD MESH. Its geometry is exact and its material is a
  // placeholder — OpenCASCADE writes a metallic default, so a wood dowel arrives as a
  // shiny aluminium rod. The colour is read off one of the part's own photographs.
  'notice.3d.colour': ['Colour', 'Couleur'],
  // The colour the patches measure, beside the one the model judged.
  // The materials table's two sections, named as the two badges over it are.
  'notice.3d.listingMaterials': ['Product materials', 'Matériaux du produit'],
  'notice.3d.defaultMaterials': ['Standard materials', 'Matériaux standard'],
  // The Parts column's menu: tick the parts made of this material.
  'notice.3d.materialPartsPick': ['Which parts are made of this', 'Quelles pièces en sont faites'],
  // NAMED MATERIALS, for the parts no photograph shows. A felt pad is under the foot: it is
  // in no picture of the assembled chair, so there is nothing to sample and the menu would
  // otherwise be empty for the one part that cannot do without it.
  'notice.3d.material': ['Material', 'Matériau'],
  // THE TWO NUMBERS A MATERIAL IS BESIDES ITS COLOUR, shown as percentages: both are 0-to-1
  // factors in glTF, and they separate these materials on screen far more than their colours
  // do — a grey disc at 75% roughness is a plastic washer, the same grey at 100% is felt.
  'notice.3d.roughness': ['roughness', 'rugosité'],
  'notice.3d.metallic': ['metallic', 'métallique'],
  // Painted a fibrous material onto a mesh with no texture coordinates. The colour landed;
  // the fibre had nowhere to go. Says the fix rather than the cause — every mesh this can
  // happen to was shaped from measurements, and re-shaping it is one press away.
  // SHAPING A PART FROM ITS MEASUREMENTS — the third way a part gets a mesh. For the parts
  // a handful of numbers describes exactly: a button, a cap, a plug, a felt pad.
  //
  // "Turn it" was the word while every one of them was turned on a lathe. A felt pad is
  // punched out of a sheet, and a label naming the machine is a label that has to be
  // widened every time the list gains a shape made a different way. "Shape" is what the
  // route does regardless of how the real part was made.
  //
  'notice.3d.latheEdit': ['new measurements', 'nouvelles mesures'],
  // DRILLING A HOLE, which is the modification every reconstruction wants: a photograph shows
  // a bored end as two dark discs and the reconstruction hands back a solid. Said as a place
  // on a face — the app's one way of naming a place on a part, and the same one Phase 5 asks a
  // model for when two parts meet.
  'notice.3d.drill': ['Drill a hole', 'Percer un trou'],
  // READING THE HOLES, beside boring one. Every label here is about what the mesh already
  // has, said in the same words the drill form asks in — a face, a place on it, a width and
  // a depth — so the list and the form are one vocabulary.
  // WHAT THE MANUAL SAYS IS BORED IN THIS PART, beside what the mesh has got. Phase 4 read
  // it off the diagrams — every fastener entering a part is a hole — and this is that answer
  // filed under the part, not a second reading of the mesh. "Found" and not "detected": the
  // button next to it detects, and these were found by reading.
  // Disabled, and said rather than hidden: "phase 4 has not been asked" and "it was asked and
  // this part is bored nowhere" are different facts, and a button that vanished says neither.
  // 1..N down the table. The hole ids are H101, H201, H301 — minted per step, so a bore says
  // which step found it before it says anything else — which is the right name and the wrong
  // thing to count with: "H303" at the bottom of ten rows reads as three hundred and three.
  // The button under that table. It cuts every hole the manual says is in this part, in one
  // press — which is the whole point of having read them: a reconstruction comes back solid,
  // and putting fourteen bores in by hand through the drill form is an afternoon.
  'notice.3d.drillSaid': ['Drill all', 'Tout percer'],
  'notice.3d.drillSaidHint': [
    'Bore every hole listed above into this mesh, one after another',
    'Percer dans ce maillage tous les trous listés ci-dessus, l’un après l’autre',
  ],
  // Said BEFORE the button is pressed. The count on the button is rarely the number of rows —
  // a hole with no width, or a blind one whose depth neither the drawing nor its fastener
  // gives, cannot be cut at all. A number nobody can account for is worse than a smaller one
  // that is explained.
  // Refused on the right hand of a pair: there is one mesh, it IS the left-hand part, and the
  // right one is that file drawn reflected. Every hole is read in the left unit's frame (see
  // StepHole.side), so cutting while the reflection is up would put each bore where its mirror
  // belongs.
  'notice.3d.drillSaidHand': [
    'Switch to Left to drill: the mesh is the left-hand part, and the right one is its mirror',
    'Passez à Gauche pour percer : le maillage est la pièce gauche, la droite en est le miroir',
  ],
  // WHAT WILL NOT BE CUT, AND WHY, said before the button is pressed and one clause per
  // reason. It was a single "{n} not cut: same bore twice, or no width or depth" — which on a
  // part whose third axis nobody has measured read "10 not cut" and sent the reader looking
  // for a fault in the holes, when every one of them was fine and the PART had no size to
  // place them against. The reasons are not the same kind of thing and are not fixed in the
  // same place; summing them hid the only one that mattered.
  // THE MESH and not the part. A hole is placed against the box the drill cuts into, which is
  // the mesh's own, so what is missing when this fires is the mesh's measurement — and naming
  // the part instead would send a reader to the size record, which is neither where the
  // problem is nor where it is fixed.
  // ONE LINE PER REASON, EACH NAMING ITS ROWS. A tally — "3 with no depth" — says three of
  // these are wrong and leaves the reader to re-derive the rule down fourteen rows to find
  // out which. The # column is the one address these rows have that can be said in a
  // sentence, and this is what it is for.
  // The whole table, waiting on one fetch: the shape of a bore is decided by what goes in it,
  // and what that IS comes from the category ontology. Said once rather than against every row.
  'notice.3d.skipOntology': [
    'Waiting for the parts ontology, which is what says the shape of each hole',
    'En attente de l’ontologie des pièces, qui donne la forme de chaque trou',
  ],
  'notice.3d.skipPlace': [
    'Nowhere to go — this mesh has no measured size',
    'Sans emplacement — ce maillage n’a pas de taille mesurée',
  ],
  'notice.3d.skipWood': ['Off the wood, as Drilling holes measured it', 'Hors du bois, d’après le perçage des trous'],
  'notice.3d.skipWidth': ['No width', 'Sans largeur'],
  'notice.3d.skipDepth': ['No depth', 'Sans profondeur'],
  // NOT A FAULT, unlike the three around it, and it is the commonest line here by far: the
  // holes already bored into this mesh. It is in the same list because the list exists to
  // account for the difference between the number of rows and the number the button will cut,
  // and "nothing left to do on that one" is the biggest part of that difference.
  'notice.3d.skipDone': ['Already drilled', 'Déjà percé'],
  'notice.3d.skipTwice': ['The same bore twice', 'Le même trou en double'],
  'notice.3d.skipOne': ['hole {n}', 'trou {n}'],
  'notice.3d.skipMany': ['holes {n}', 'trous {n}'],
  // The last comma becomes a word: a list of numbers ending in two commas is one you have to
  // count to know you have reached the end of.
  'notice.3d.skipAnd': ['and', 'et'],
  // The gesture that claimed this bore — "1AF", the address the sub-step navigator walks. Its
  // own column rather than a second line under the hole's id: those are two different kinds of
  // address, one this app's and one the manual's, and stacked they read as one in two parts.
  // THE STEP ON ITS OWN, beside the sub-step that carries it in its text. Two columns for what
  // looks like one address, because they are read at different scales: which of four steps a
  // bore belongs to is the shape of the work, and "1AF" is where to go and argue with it.
  // WHAT SHAPE THE BORE IS, under the fastener that decides it. Three words for three
  // different cuts — see _HOLE_STYLES in mesh_repair_blender.py, which makes them — and not a
  // vocabulary of drilling: a value that made no difference to the mesh would be a word in a
  // table nobody could check against the picture.
  //
  // "Pointed" and not "plain", which is what the code calls it: the code's word says what the
  // bore has NOT got, and a reader looking at a row wants to know what it HAS. On a through
  // hole there is no point and no floor — both ends are open — so it says so instead.
  // ONE ROW'S OWN DRILL. The button under the table cuts all of them, which is what you want
  // the first time and not what you want the second: a list read against a part is a list you
  // disagree with in one place, and cutting the thirteen that are right meant either the whole
  // batch or typing the one out in the drill form by hand.
  // "Drill hole" and not "Drill": the column is called Drill and a button repeating its
  // heading says nothing the heading did not, where the noun says WHAT this one press cuts —
  // this hole, not the fourteen above it.
  'notice.3d.saidColDrill': ['Drill', 'Percer'],
  'notice.3d.drillOne': ['Drill', 'Percer'],
  // THE HARDWARE IN ITS HOLE, slid in on the part — see holeFit.
  'notice.3d.fitShow': ['Show hardware', 'Montrer la quincaillerie'],
  'notice.3d.fitHint': [
    'Slide the part this hole takes into it, with the board see-through',
    'Glisser la pièce que reçoit ce trou, le panneau en transparence',
  ],
  'notice.3d.fitNoPlace': ['This hole has no place on the part yet', "Ce trou n'a pas encore de place sur la pièce"],
  'notice.3d.fitNoMesh': [
    'No 3D model in the Parts DB for the part this hole takes',
    'Pas de modèle 3D en base pour la pièce que reçoit ce trou',
  ],
  'notice.3d.fitNoDepth': ['This blind hole has no depth', "Ce trou borgne n'a pas de profondeur"],
  'notice.3d.drillOneHint': [
    'Bore this one hole into the mesh',
    'Percer ce seul trou dans le maillage',
  ],
  // Greyed out, with the reason on it. The lines under the batch button say the same thing by
  // row number; this says it where a reader who has followed one line across is already
  // looking, and without making them carry a number back to a sentence.
  // The row's own button, once its hole is in the part. It reads "Drilled" rather than staying
  // "Drill hole" and greyed: a disabled button under the name of what it would do reads as a
  // fault — no width, the wrong hand — and this is the opposite, a row with nothing left owing.
  'notice.3d.drillOneDone': ['Drilled', 'Percé'],
  'notice.3d.drillOneDoneHint': [
    'This hole is already bored into this mesh — undo it or fill it to cut it again',
    'Ce trou est déjà percé dans ce maillage — annulez-le ou bouchez-le pour le repercer',
  ],
  'notice.3d.drillOneNo': [
    'Cannot be cut: no place, no width, or a blind hole with no depth',
    'Impossible à percer : sans emplacement, sans largeur, ou trou borgne sans profondeur',
  ],
  // WHOSE HOLES THE TABLE IS SHOWING, on a part that comes in two hands. Said once, over the
  // whole table, and not per row.
  //
  // It was per row, as the word Phase 4 answered: which unit the MANUAL bores each hole in. On
  // a manual whose diagrams all draw the left rail — which is most of them — that came out as
  // "Left" under all eight ids while the toggle over the viewer stood on Right, and the two
  // words meant different things. The row's meant "the drawing showed the left one"; the
  // toggle's means "you are looking at the right one". Read together they say the table is
  // about the other rail, which is the one thing that is not true of it.
  //
  // There is ONE set of bores for the pair. The mesh is the left-hand part, every hole is
  // written in its frame, and the right-hand part is that same file drawn reflected — so both
  // hands show the same rows, and it is the drill and not the table that cares which is up.
  //
  // TWO SENTENCES AND NOT ONE WITH A WORD SWAPPED: standing on the right hand is exactly when
  // a reader needs telling that what is in front of them is the mirror, and standing on the
  // left is when they need telling that the other rail is covered too. Different news.
  'notice.3d.holesPairLeft': [
    'One set of bores for both hands, read on the left-hand part — this mesh. The right-hand '
      + 'part takes the same ones, mirrored.',
    'Un seul jeu de perçages pour les deux pièces, relevé sur la pièce gauche — ce maillage. '
      + 'La pièce droite reçoit les mêmes, en miroir.',
  ],
  'notice.3d.holesPairRight': [
    'One set of bores for both hands, read on the LEFT-hand part. The right-hand part shown '
      + 'here takes these same ones, mirrored across its width.',
    'Un seul jeu de perçages pour les deux pièces, relevé sur la pièce GAUCHE. La pièce droite '
      + 'affichée ici reçoit les mêmes, en miroir sur sa largeur.',
  ],
  // A BORE NOBODY COULD PLACE. The model answered it against the assembled piece the step is
  // building rather than against the plank inside it, and that piece holds two planks — so
  // which one is drilled cannot be worked out from what was said (see placeInPart, which
  // resolves the pieces holding only one and keeps the rest).
  //
  // It used to be deleted at the moment it arrived. The bore was found, paid for and thrown
  // away, and the only trace was a gap in the hole numbers, which reads as nothing at all.
  'notice.holes.noPart': ['Not placed in a component part', 'Non rattaché à une pièce composant'],
  // AND THE SAME NEWS AS A NUMBER, over the whole manual, on the phase — because the row above
  // is only seen by somebody already standing on the gesture that answered it. One sentence for
  // any count, so "1 bores" cannot happen.
  // A BORE ANSWERED FOR HARDWARE THAT MAKES NONE. Its own sentence and not the "no width" one
  // it would otherwise land in: both leave the row uncuttable, and only one of them is fixed
  // by filling in a measurement.
  'notice.holes.noBoreCount': [
    'Bores answered for hardware that makes none: {n}. A cover, a washer or a pad goes over '
      + 'or around a fastener — shown in the gestures table, and not drilled.',
    'Perçages attribués à de la visserie qui n’en fait pas : {n}. Un cache, une rondelle ou '
      + 'un patin se pose sur ou autour d’une fixation — visible dans le tableau des gestes, '
      + 'et non percé.',
  ],
  'notice.holes.noPartCount': [
    'Bores not placed in a component part: {n}. Each was answered against an assembled piece '
      + '— shown in the gestures table, and not drilled.',
    'Perçages non rattachés à une pièce composant : {n}. Chacun a été indiqué sur un '
      + 'sous-ensemble — visible dans le tableau des gestes, et non percé.',
  ],
  // AND THE PARTS NO HOLE COULD BE ASKED ABOUT. Beside the two above, and worded as a
  // different kind of fact: those are answers that could not be used, this is a question that
  // could not be put. Names the parts rather than counting them — one is the whole fix, and
  // "2 parts" would leave you opening fourteen to find which.
  'notice.holes.unmeasured': [
    'No 3D model measured for {parts}, so no hole can be placed in it: any bore a step makes '
      + 'there was left out of the answer. Open it in the parts phase to measure it.',
    'Aucun modèle 3D mesuré pour {parts} : aucun perçage ne peut y être situé, et ceux que les '
      + 'étapes y font ont été omis. Ouvrez-la dans la phase des pièces pour la mesurer.',
  ],
  // THE SWITCH OVER A WHOLE HOLE TABLE — the said one and the measured one both, since it is
  // the same sentence about the same gesture. Two words for two states of one button — with
  // everything already on the part the only thing left to offer is taking it off — and the
  // count beside it is the rows it will actually mark, not the rows in the table.
  'notice.3d.holesPickAll': ['Select all', 'Tout sélectionner'],
  'notice.3d.holesPickNone': ['Deselect all', 'Tout désélectionner'],
  'notice.3d.holes': ['Detect holes', 'Détecter les trous'],
  'notice.3d.holesNoScale': [
    'The part has no measured size, so there are no millimetres to report holes in',
    "La pièce n'a pas de taille mesurée : aucun millimètre dans lequel rapporter les trous",
  ],
  // AND READ IT AGAIN, from inside the reading. Every edit made in this panel changes the
  // mesh the list describes, so the list is worth re-taking without leaving it.
  // THE FOUR QUESTIONS drill_hole ASKS, as column headings, in its order: how wide, which
  // face, whereabouts on it, how deep. Every number in that table is ambiguous on its own —
  // "12.2 × 231.2 mm" says nothing about which way round it is read.
  // AND THE ONE COLUMN THAT IS NOT A MEASUREMENT: putting the wood back where a hole is.
  // A reconstruction's holes are approximately where the real ones are and approximately as
  // wide, which is the worst of both — so they are filled and the drawing's are bored.
  // THREE PRESSES AND NOT ONE. Filling used to be a single button with thirty steps behind
  // it, and a bad result said nothing about which of them was bad. Each of these is a thing
  // that can be looked at before the next is pressed, and the counts each one answers with are
  // what say whether it did what it claims.
  // WHAT EACH PRESS ANSWERED, in the line under the table. Counts and nothing else: they are
  // what tells you the step did what it says — two mouths and not one, a sweep that found the
  // tube.
  // AND WHERE THAT MULTIPLE WAS NOT MEASURED. It is normally read off the bore's own wall —
  // the width at which the whole tube falls inside the capsule — and a bore with no wall to
  // find gets the fixed one instead, which is a guess and says so.
  // WHAT THE CAPSULE MISSED, if anything. It is a straight rod between the bore's two ends, so
  // a bore that bends can leave a piece behind — said, rather than hunted down and deleted.
  // AND STEP THREE'S. How many wounds were closed and what that cost in triangles; the bands
  // are the density decision made visible — all ones is a plain fan, which on a rim this fine
  // means the reading of the local triangle size came back far too big.
  // WHY STEP THREE CANNOT BE PRESSED. It closes the borders step two wrote down, and a row
  // whose last answer is not a cut is not carrying any.
  // WHEN THE PANEL'S STATE WOULD NOT SAVE. The table on screen is still the answer; what is
  // lost is the copy the run keeps, which is what a reload comes back to and where step three
  // finds the borders it closes.
  // THE THREE STEPS ON EVERY ROW, PRESSED FOR YOU. Not the single fill button this was broken
  // up from: each step still runs in its own right and still answers with its own counts in
  // its own row, so afterwards the table reads exactly as it would have by hand.
  'notice.3d.fillAll': ['Cut & fill all', 'Tout couper et boucher'],
  // A PART WITH NO PICTURE ON IT. Each cap takes one colour off the wood round its own mouth,
  // and there is none to take.
  // The same switch on step two's answer, where what it shows is the borders the cut left —
  // a hue per ring, since they are closed one at a time.
  // AND ITS LABEL, because that switch is no longer the answer it stands beside. Step two
  // reports what it removed AND carries the capsule it removed it with, so the answer is the
  // ball's switch and the borders needed one of their own.
  // What the depth beside it MEANS: how far a blind hole goes, or how much material a
  // through one crosses.
  //
  // AND THE SAID TABLE'S DEPTH COLUMN NOW SAYS IT TOO — "Blind (25)", "Through" — off these
  // same two words rather than a second pair of its own. The two tables are read one after the
  // other about one bore, and a hole the mesh reading calls Blind should not be a bare number
  // in the reading above it. Not notice.holes.through, which is lower-case because it rides
  // inside a sentence: "Ø 6 mm · through".
  // Tapped or not, which is what decides the fastener rather than the drill: a threaded
  // hole takes the screw itself, a smooth one lets it pass to a nut on the far side.
  // WHICH WAY A MIRROR PAIR'S FACE LOOKS, under the face name in the said table. Only the two
  // faces the mirror moves get one — see pairFaceSide, which holds the rule. Bracketed there
  // because it is a reading of the face above it and not a second column's worth of answer.
  // What one reads in the history: whether it closed. "Closed the surface" on an attempt
  // that ended with a rim it refused to touch says the opposite of what happened.
  'notice.3d.closeEdit': ['closed the surface', 'surface fermée'],
  'notice.3d.closeEditShort': ['tidied the surface', 'surface nettoyée'],
  // THE NAMES OF THE BANDS OF CONTROLS under the mesh — see Band, which
  // draws them in capitals. Sentence case here and not shouted in the string: the capitals
  // are a typographic choice about a label, and a translation that carries them cannot be
  // used anywhere they are wrong.
  'notice.3d.bandShape': ['Size & orientation', 'Taille & orientation'],
  // AND THE PICTURES OVER THE VIEWER, which is the same device naming a group of things
  // rather than a group of controls — see CropStrip.
  'notice.3d.bandReference': ['Reference images', 'Images de référence'],
  // THE PART'S OWN THREE WORDS, short enough to sit in front of a number. Initials and not
  // the words themselves: a cell reading "width 50 · height 2 · depth 47" is a sentence
  // where a measurement should be, and three of them to a row is a wall of text.
  'notice.3d.axisW': ['w', 'l'],
  'notice.3d.axisH': ['h', 'h'],
  'notice.3d.axisD': ['d', 'p'],
  'notice.3d.keepEditOne': ['piece deleted', 'morceau supprimé'],
  'notice.3d.keepEditMany': ['pieces deleted', 'morceaux supprimés'],
  // The face's own ruler, drawn from the mesh's measured box: what makes "45" an answer
  // rather than a number.
  // TYPED AS A SHARE OF THE EDGE and stored in millimetres: the two edges of a face are
  // different lengths and neither is on screen, so a millimetre typed here means something
  // different on every face — where halfway is 50 wherever you are.
  // What one reads in the history: WHAT WAS DONE first, then where, then how big — since a
  // part takes several and "drill" on all of them names none of them.
  //
  // THE DEED BEFORE ITS MEASUREMENTS. These read "{mm} mm through the {face}", which opens on
  // a number: a rail of them is a column of digits, and the thing a reader scans for is the
  // one entry where a hole was FILLED rather than bored. Which kind of edit it was is what
  // tells those apart, so it goes first and the numbers qualify it.
  //
  // AND THE FACE IS NAMED AS THE MESH NAMES IT — "Bottom face", capitalised, the same word the
  // viewer draws on that side of the box (see faceKey). It was lowercased to sit inside a
  // sentence; it is a field now, and a field should carry the name a reader can go and look at.
  'notice.3d.drillEditThrough': [
    'hole drilled, {face} face, {mm} mm through',
    'trou percé, face {face}, {mm} mm traversant',
  ],
  'notice.3d.drillEditBlind': [
    'hole drilled, {face} face, {mm} mm × {depth} mm deep',
    'trou percé, face {face}, {mm} mm × {depth} mm de profondeur',
  ],
  // AND A FILL, named like the drill it undoes, so a part whose holes were filled and
  // re-bored reads as the pair of entries it is.
  // A FILL LEAVES TWO ENTRIES NOW, because it is two presses: the ball came out, and then the
  // wound was closed. Both name the hole they are about, in the words the drill names one it
  // made — a part whose reconstructed holes were filled and re-bored has all three on one rail.
  // The old single entry stays: rails written before this exist and still have to read.
  'notice.3d.fillEdit': [
    'hole filled, {face} face, {mm} mm',
    'trou bouché, face {face}, {mm} mm',
  ],
  // ONE PRESS OF CUT & FILL ALL, which is one entry however many holes it went through. Named
  // by the count rather than by a face, because there is no one face: it is the whole table.
  'notice.3d.drillAllEdit': [
    'drilled {n} hole(s)',
    '{n} trou(s) percé(s)',
  ],
  'notice.3d.fillAllEdit': [
    'cut & filled {n} hole(s)',
    '{n} trou(s) coup\u00e9(s) et bouch\u00e9(s)',
  ],
  'notice.3d.fillCutEdit': [
    'hole cut out, {face} face, {mm} mm',
    'trou découpé, face {face}, {mm} mm',
  ],
  // KEPT THOUGH THE STEP IS GONE. Meshes edited before it was removed still carry `fill_close`
  // rows in their history, and a key with no string renders as the key.
  'notice.3d.fillCloseEdit': [
    'wound closed, {face} face, {mm} mm',
    'plaie refermée, face {face}, {mm} mm',
  ],
  'notice.3d.fillSlot': ['Fill the end slot', 'Combler la fente'],
  'notice.3d.cutCross': ['Cut a cross recess', 'Creuser une empreinte cruciforme'],
  // The example names a barrel nut's cross, which is the case this was built for and the
  // wrong hint under a chair leg. Kept generic here; the part-specific half is which
  // repairs are offered at all, and that is decided by category (see MeshControls).
  'notice.3d.askWhatsWrong': ['Say what is wrong with this mesh, and where.', 'Dites ce qui ne va pas sur ce maillage, et o\u00f9.'],
  'notice.3d.askRepair': ['Modify mesh', 'Modifier le maillage'],
  'notice.3d.askTitle': ['What is wrong with this mesh', 'Ce qui ne va pas sur ce maillage'],
  // WHICH WAY ROUND THE PART GOES. Not "orient the mesh", which reads as a thing being
  // done to a file: what is being settled is a fact about the furniture, and the word for
  // it is the one somebody would use about the chair.
  // ONE WORD. "Determine orientation" said what pressing it DOES, which is what the two items
  // inside the menu are for — and at nineteen characters it took a row of eight controls onto
  // a second line by itself.
  'notice.3d.orient': ['Orientation', 'Orientation'],
  'notice.3d.orientHint': [
    'Match this mesh against the photographs of the assembled product, and turn it so its Front, Back and Top are the finished item’s.',
    "Comparer ce maillage aux photos du produit assemblé, et le tourner pour que ses faces Avant, Arrière et Dessus soient celles de l'article fini.",
  ],
  // THE SAME PRESS ASKED ABOUT A DIFFERENT KIND OF THING. A screw belongs to no one product,
  // so there are no photographs in its question and the hint above would be describing a call
  // that is not made. What it is matched against is nothing: it is read off its own six views,
  // stood on the side it rests on and turned so the side that does the work faces front.
  'notice.3d.orientHintSolo': [
    'Read the six views of this part and turn it so it stands the way it is used — resting on its underside, with the side that does the work to the front. No photographs: hardware and tools belong to no one product.',
    "Lire les six vues de cette pièce et la tourner pour qu'elle se tienne comme on l'utilise — posée sur sa face inférieure, le côté qui travaille vers l'avant. Aucune photo : la visserie et les outils n'appartiennent à aucun produit en particulier.",
  ],
  'notice.3d.orientPrompt': ['Orientation question', "Question d'orientation"],
  // WHY THE MODEL'S HALF IS GREYED: the question is this mesh against photographs of the
  // assembled thing WITH THIS PART ON THEM, and none of the classified pictures has it.
  // Names the phase that can fix it, since the crops can be re-run for one part.
  'notice.3d.orientNoShots': [
    'No classified photograph of the assembled product has this part on it, so there is nothing to match the mesh against. Find its crops in the parts phase first — or turn it by hand.',
    "Aucune photo classée du produit assemblé ne contient cette pièce : rien à quoi comparer le maillage. Cherchez d'abord ses détourages dans la phase des pièces — ou tournez-la à la main.",
  ],
  // Under a photograph the question is NOT asked with — opened large, where the others say
  // which picture of the product they are. It stays in the strip because that is the only
  // place it can be put back.
  'notice.3d.orientLeftOut': [
    'Left out of this question — press its name to put it back',
    'Exclue de cette question — appuyez sur son nom pour la remettre',
  ],
  // The one control in the viewer that is about the FETCH rather than about the mesh: ask
  // for the file again when it came back broken, instead of reloading the page for it.
  'notice.3d.reload': ['Load this 3D model again', 'Recharger ce modèle 3D'],
  'notice.3d.orientThinking': ['Why it is that way round', 'Pourquoi ce sens'],
  // THE ANSWER, in the vocabulary the question was asked in: two of the six lettered views
  // of the mesh, and the side of the finished product each one shows. The letters are the
  // ones written under the pictures in the prompt beside this.
  //
  // Nothing about how the file was labelled before. The model is shown A to F precisely so
  // that it cannot know the mesh's own names for those sides, and a line phrased against
  // those names read as though it had been told the orientation in advance.
  'notice.3d.orientAnswerTitle': ['Answer', 'Réponse'],
  'notice.3d.orientAnswerLine': ['view {v}: {s}', 'vue {v} : {s}'],
  // THE SIX ANSWERS CAN NOW DISAGREE WITH EACH OTHER, where two perpendicular faces could
  // not: the turn applied is the one that explains the most of them, and the rest are drawn
  // in red. These say what red means and why one of them is red.
  'notice.3d.orientMisfitHint': [
    'In red: answers the applied turn disagrees with. Press one to see why.',
    'En rouge : les réponses que la rotation appliquée contredit. Appuyez dessus pour savoir pourquoi.',
  ],
  'notice.3d.orientUnsettledHint': [
    'These answers do not say which way round the part goes, so nothing was turned.',
    "Ces réponses ne disent pas dans quel sens va la pièce : rien n'a été tourné.",
  ],
  'notice.3d.orientWhyOpen': ['Why this one does not fit', 'Pourquoi celle-ci ne colle pas'],
  // WHAT THE MODEL SAID ABOUT THIS FACE, written under the app's own word for it on the
  // part's own bounding box. Two rows, one plate: "Front / says: back" is a contradiction you
  // can see while turning the part, where the same two facts in a panel below the viewport
  // are two facts to hold in the head. Short, because it is drawn on a picture.
  'notice.3d.orientFaceSays': ['says:', 'dit :'],
  // Pressing one that agrees is a fair question too — "what did it say about this side" —
  // and this is the answer to it.
  'notice.3d.orientFitsTitle': ['View {v} agrees', 'La vue {v} concorde'],
  'notice.3d.orientWhyFits': [
    'It was answered {said}, and the turn that was applied makes it exactly that.',
    'Réponse donnée : {said}, et la rotation appliquée en fait précisément cela.',
  ],
  'notice.3d.orientWhyTitle': ['View {v} does not fit', 'La vue {v} ne colle pas'],
  // The three reasons an answer can be the odd one out, in the order they are worth reading:
  // what else claims this side, what its opposite view says, and what the applied turn makes
  // of it. All three are arithmetic on the six answers — see OrientViews, which argues why
  // they are computed here rather than asked of the model that produced the contradiction.
  'notice.3d.orientWhyDuplicate': [
    'View {others} says {side} as well. Six views show six different sides, so one of them is wrong.',
    'La vue {others} dit également {side}. Six vues montrent six côtés différents : l’une des deux se trompe.',
  ],
  'notice.3d.orientWhyOpposite': [
    'Views {v} and {twin} look along opposite directions, so what they show must be opposite sides — but {twin} says {twinSaid}, where the opposite of this answer is {opposite}.',
    'Les vues {v} et {twin} regardent dans des directions opposées : ce qu’elles montrent doit donc être opposé — or {twin} dit {twinSaid}, alors que l’opposé de cette réponse est {opposite}.',
  ],
  'notice.3d.orientWhyApplied': [
    'It was answered {said}; the turn that fits the other views makes it the {applied}.',
    'Réponse donnée : {said} ; la rotation qui convient aux autres vues en fait {applied}.',
  ],
  'notice.3d.orientWhyUnsettled': [
    'Nothing was turned: {n} different orientations explain these six answers equally well, so they settle nothing.',
    'Rien n’a été tourné : {n} orientations différentes expliquent aussi bien ces six réponses, qui ne tranchent donc rien.',
  ],
  // WHERE THE WAIT WENT, the two halves measured where they happen. The question everybody
  // asks about a button that takes a minute, and the one nobody could answer from outside.
  // Under a render opened large: which mesh it is a view of, and which side. Neither is
  // readable off the picture — a leg down its own length is the same brown shape from four
  // of the six angles, and a part with three reconstructions has three of these sets.
  'notice.3d.viewOfMesh': [
    'View {t} · mesh #{n} · seen from the {side}',
    'Vue {t} · maillage #{n} · vu de {side}',
  ],
  'notice.3d.orientTimeTitle': ['Time', 'Durée'],
  'notice.3d.orientTimeRenders': ['renders', 'rendus'],
  'notice.3d.orientFailed': [
    'Which way round this mesh goes could not be decided.',
    "Impossible de déterminer dans quel sens va ce maillage.",
  ],
  // On the history rail, naming what MOVED rather than that a turn happened: {f} is the
  // face the mesh used to call the one that really faces the front.
  'notice.3d.orientEdit': ['turned, front was {f}', 'tourné, l’avant était {f}'],
  'notice.3d.orientEditPlain': ['turned the right way round', 'remis dans le bon sens'],
  'notice.3d.orientEditHand': ['turned by hand', 'tourné à la main'],
  // The same line for a hand turn that named its faces — which is every one of them since the
  // dialog stopped asking for axes. The older entries, which recorded an axis and an angle and
  // nothing about faces, keep the line above.
  'notice.3d.orientEditHandFace': [
    'turned by hand, front was {f}',
    'tourné à la main, l’avant était {f}',
  ],
  // THE TWO WAYS THAT QUESTION GETS ANSWERED, on the menu the button opens. Put it to the
  // model, or turn the part yourself — which is what somebody already looking at a leg lying
  // on its side wants, without the minute of inference and the paragraph about why.
  'notice.3d.orientAsk': ['Ask the model', 'Interroger le modèle'],
  'notice.3d.orientByHand': ['Change orientation', 'Changer l’orientation'],
  'notice.3d.turnTitle': ['Turn the mesh', 'Tourner le maillage'],
  // Says the two things the rows cannot: that the name on the left is the one the FILE gives
  // that side today, and why there are two rows rather than one — the second is not extra
  // detail, it is what makes the first answerable.
  'notice.3d.turnHint': [
    'Name a face as the file has it, and the side of the product it really is. Two settle the other four.',
    'Nommez une face telle que le fichier la désigne, et le côté du produit qu’elle est vraiment. Deux suffisent à fixer les quatre autres.',
  ],
  'notice.3d.turnPick': ['Choose…', 'Choisir…'],
  'notice.3d.turnFace': ['The face as the file has it', 'La face telle que le fichier la désigne'],
  'notice.3d.turnBecomes': ['The side it really is', 'Le côté qu’elle est vraiment'],
  'notice.3d.turnApply': ['Turn the mesh', 'Tourner le maillage'],
  'notice.3d.turnClose': ['Close', 'Fermer'],
  'notice.3d.turnFailed': [
    'The mesh could not be turned.',
    'Impossible de tourner le maillage.',
  ],
  // Said above the box when the mesh has already been transformed: the next question
  // is usually about the last one, and its code goes into the prompt.
  'notice.3d.followingOn': ['Following on from', '\u00c0 la suite de'],
  // The other kind of next question: about ONE past transformation rather than about
  // the mesh in general. A press, not an inference from how a sentence is worded.
  'notice.3d.refine': ['Build on this', 'Partir de \u00e7a'],
  'notice.3d.refineHint': [
    'Ask for a change that corrects or extends this one. The code it ran goes with the question.',
    'Demander une modification qui corrige ou prolonge celle-ci. Le code ex\u00e9cut\u00e9 accompagne la question.',
  ],
  'notice.3d.refining': ['Building on', 'En partant de'],
  'notice.3d.askDrawings': ['Drawings to send with it', 'Schémas à envoyer avec'],
  // The finished thing the part is a piece of — offered for a component and never for
  // hardware. See askPictures.ts.
  'notice.3d.askProduct': ['The whole item', "L'article entier"],
  // The half of the call that is not chosen here: the renders of the mesh, its
  // measurements, and the prompt they go in. Shown on demand — it costs a render.
  'notice.3d.askShown': ['What the model will be shown', 'Ce que le mod\u00e8le verra'],
  // The mesh's own three renders, in the row of pictures with the drawings. Always
  // sent — there is no proposal about a mesh nobody let the model look at.
  'notice.3d.askRenders': ['This mesh, from three angles', 'Ce maillage, sous trois angles'],
  'notice.3d.askRendersFailed': [
    'This mesh could not be rendered, so the question goes without it.',
    'Ce maillage n\u2019a pas pu \u00eatre rendu, la question part donc sans lui.',
  ],
  'notice.3d.askRendersHint': [
    'Rendered from the mesh as it stands, and always sent.',
    'Rendus du maillage tel qu\u2019il est, toujours envoy\u00e9s.',
  ],
  'notice.3d.askNoDrawings': [
    'This part has no drawing in the database, so there is nothing to compare against.',
    'Cette pièce n’a aucun schéma dans la base, il n’y a donc rien à comparer.',
  ],
  'notice.3d.askMaxPictures': [
    'That is as many pictures of this kind as one question carries.',
    'C’est le maximum d’images de ce type pour une question.',
  ],
  'notice.3d.adviceFailed': ['The repair could not be worked out.', 'La correction n\u2019a pas pu \u00eatre d\u00e9duite.'],
  'notice.3d.applyProposal': ['Apply this', 'Appliquer'],
  // A proposal that has already been run stays pressable: the same code twice cuts
  // twice, which is a real thing to want and a real thing to do by accident.
  'notice.3d.applyAgain': ['Apply again', 'Appliquer \u00e0 nouveau'],
  'notice.3d.wasApplied': ['applied', 'appliqu\u00e9e'],
  'notice.3d.proposalMore': ['See the code', 'Voir le code'],
  'notice.3d.proposalLess': ['Hide the code', 'Masquer le code'],
  'notice.3d.forgetProposal': [
    'Forget this proposal',
    'Oublier cette proposition',
  ],
  'notice.3d.noProposal': ['None of the available repairs matches that.', 'Aucune des corrections disponibles ne correspond.'],
  'notice.3d.noDrawing': ['This part has no manual drawing, so nothing was compared against one.', 'Cette pi\u00e8ce n\u2019a aucun sch\u00e9ma, la comparaison s\u2019est donc faite sans.'],
  'notice.3d.sawThis': ['What it looked at', 'Ce qu\u2019il a regard\u00e9'],
  'notice.3d.confidence': ['confidence', 'confiance'],
  // What the edit stack calls a colour that kept the grain. A word and not a sentence: it hangs off the
  // material's name on a rail where every entry is already three words long.
  'notice.3d.colourGrainMark': ['grain kept', 'grain gardé'],
  'notice.3d.confHigh': ['high', '\u00e9lev\u00e9e'],
  'notice.3d.confMedium': ['medium', 'moyenne'],
  'notice.3d.confLow': ['low', 'faible'],
  // A box dragged over the history catches several changes, undone by one press.
  'notice.3d.revertMany': ['Undo {n} changes', 'Annuler {n} modifications'],
  'notice.3d.pickClear': ['Clear selection', 'Effacer la sélection'],
  'notice.3d.revert': ['Undo', 'Annuler'],
  'notice.3d.revertHint': ['Put the mesh back as it was before this change.', 'Rétablir le maillage tel qu’il était avant cette modification.'],
  'notice.3d.revertFailed': ['That change could not be undone.', 'Cette modification n’a pas pu être annulée.'],
  'notice.3d.deleteModel': ['Delete this 3D model', 'Supprimer ce modèle 3D'],
  // A run that made no mesh, in the viewer's place and on its tile (see FailedRunPanel).
  'notice.3d.deleteRun': ['Delete this run', 'Supprimer ce run'],
  'notice.3d.runFailedTitle': ['Run #{n} failed', 'Échec du run n°{n}'],
  'notice.3d.runFailedNoModel': ['No 3D model was made.', 'Aucun modèle 3D n\'a été créé.'],
  'notice.3d.runFailedFull': ['Show the full error', 'Voir l\'erreur complète'],
  'notice.3d.runFailedCall': ['Prompt & script', 'Prompt et script'],
  'notice.3d.runFailedTile': ['#{n} failed', 'n°{n} échoué'],
  'notice.3d.deleteVersion': ['Delete this version', 'Supprimer cette version'],
  'notice.3d.scaling': ['Scaling', 'Mise à l\'échelle'],
  'notice.3d.scaleOn': ['Scale on', 'Mettre à l\'échelle sur'],
  'notice.3d.scaleNoDim': [
    'No measurement was read for this axis, so there is nothing to scale it to.',
    'Aucune mesure n\'a été lue pour cet axe, il n\'y a donc rien sur quoi le caler.',
  ],
  'notice.3d.rescaleFailed': [
    'The mesh could not be rescaled.',
    'Le maillage n\'a pas pu être redimensionné.',
  ],
  'notice.addon.button': ['Blender add-on', 'Extension Blender'],
  'notice.formFactor.mobile': ['Mobile', 'Mobile'],
  'notice.formFactor.desktop': ['Desktop', 'Desktop'],
  'notice.cad.title': ['Upload a CAD file', 'Importer un fichier CAO'],
  'notice.addon.files': ['Blender files for this manual', 'Fichiers Blender de cette notice'],
  'notice.addon.noFiles': [
    'No Blender file yet. Install the add-on, pick this manual in its panel, and press the new-file button to create one.',
    "Aucun fichier Blender pour l'instant. Installez l'extension, choisissez cette notice dans son panneau, puis appuyez sur le bouton nouveau fichier.",
  ],
  // The Blender files could not be listed: a failure, not "no file yet" (see BlendFileList).
  'notice.addon.filesFailed': [
    'The Blender files could not be loaded. Still trying…',
    'Les fichiers Blender n’ont pas pu être chargés. Nouvel essai en cours…',
  ],
  'notice.addon.title': ['NoticeIA add-on for Blender', 'Extension NoticeIA pour Blender'],
  'notice.addon.step1': ['Download the add-on', "Téléchargez l'extension"],
  'notice.addon.step2': ['Install it in Blender', 'Installez-la dans Blender'],
  'notice.addon.step2Path': [
    'Edit > Preferences > Add-ons > the v menu > Install from Disk, then pick the file you just downloaded and tick it on.',
    "Édition > Préférences > Add-ons > le menu v > Installer depuis un fichier, puis choisissez le fichier téléchargé et cochez-le.",
  ],
  'notice.addon.step3': ['Connect it to this account', 'Reliez-la à ce compte'],
  'notice.addon.keyGenerate': ['Generate a connection key', 'Générer une clé de connexion'],
  'notice.addon.keyReplace': ['Generate a new one', 'En générer une nouvelle'],
  'notice.addon.keyRevoke': ['Revoke', 'Révoquer'],
  'notice.addon.keyOnce': [
    'Copy it now. It is shown once, and generating another one replaces this. Paste it into the add-on preferences in Blender and this page turns green by itself.',
    "Copiez-la maintenant. Elle n'est affichée qu'une fois, et en générer une autre remplace celle-ci. Collez-la dans les préférences de l'extension dans Blender et cette page passe au vert toute seule.",
  ],
  'notice.addon.keyInstalled': [
    'A key is installed, ending in',
    'Une clé est installée, terminant par',
  ],
  'notice.addon.keyPending': [
    'A key exists but no Blender has used it yet. Check the add-on is ticked on in Preferences > Add-ons, then paste the key into its preferences. This page turns green on its own within a few seconds. If you no longer have the key, generate a new one.',
    "Une clé existe mais aucun Blender ne s'en est encore servi. Vérifiez que l'extension est bien cochée dans Préférences > Add-ons, puis collez la clé dans ses préférences. Cette page passe au vert toute seule en quelques secondes. Si vous n'avez plus la clé, générez-en une nouvelle.",
  ],
  'notice.addon.connected': ['Blender is connected', 'Blender est connecté'],
  'notice.addon.connectedWhen': ['Last reached NoticeIA', 'Dernier contact avec NoticeIA'],
  'notice.addon.installedVersion': ['add-on', 'extension'],
  'notice.addon.outdated': [
    'A newer add-on is available:',
    'Une version plus récente est disponible :',
  ],
  'notice.addon.outdatedHow': [
    'Download it and install it over the old one. Your key stays valid.',
    "Téléchargez-la et installez-la par-dessus l'ancienne. Votre clé reste valable.",
  ],
  'notice.addon.another': ['Set up another machine', 'Configurer une autre machine'],
  'notice.addon.keyFailed': [
    'That key could not be changed.',
    "Cette clé n'a pas pu être modifiée.",
  ],
  'notice.addon.copy': ['Copy', 'Copier'],
  'notice.addon.copied': ['Copied', 'Copiée'],
  'notice.addon.copyFailed': [
    'The browser would not give access to the clipboard. Select the key and copy it by hand.',
    "Le navigateur n'a pas donné accès au presse-papiers. Sélectionnez la clé et copiez-la à la main.",
  ],
  'notice.addon.then': [
    'Then, in Blender: paste the key into the add-on preferences, press N in the 3D viewport and open the NoticeIA tab.',
    "Ensuite, dans Blender : collez la clé dans les préférences de l'extension, appuyez sur N dans la vue 3D et ouvrez l'onglet NoticeIA.",
  ],
  'notice.3d.angle': ['Angle asked for:', 'Angle demandé :'],
  'notice.mode.llm': ['Gemini Omni', 'Gemini Omni'],
  // Its name where no model is named (the demo).
  'notice.mode.llmDemo': ['AI image', 'Image IA'],
  // Phase 1's classification row, where the model it is named after is not named (the demo).
  // "The call" where the model making it is not named (the demo).
  'notice.3d.orientTimeCall': ['The call', 'L’appel'],
  'notice.mode.models': ['Blender 3D', 'Blender 3D'],
  // CORRECTING A LAMP OUT OF THE MESH'S OWN TEXTURE. Said as what it does to the picture —
  // "reflection" is the word somebody uses looking at the seat, where "specular highlight"
  // and "delighting" are words for the thing that causes it.
  // SELECTING A WHOLE FACE OF THE PART, rather than a piece of the picture. For the side
  // a single-image reconstruction never saw and had to invent, which comes back at the
  // wrong level and is scattered across the atlas in pieces no rectangle can enclose.
  // ONE AT A TIME, and the list is for comparing them. Correcting two together levelled the
  // UNION's average onto the target, which put the two faces either side of it — asked for
  // 116, the panel produced a 113 and a 118 and reported both.
  // WHAT THE LEVELLING AIMS AT: everything the selection does not claim. "The rest" and not
  // "the part", because the chosen faces are taken out of it — choose two and the target
  // moves, which is a thing to be able to watch rather than to be told.
  // WHAT THE PANEL IS DOING, once a face is selected. The headings were written for the
  // rectangles and went on saying "mark the reflections" over a face mask — so the one
  // question left was which of two corrections the Apply button was about to run.
  // WHAT THE PRESS WOULD DO, ON THE PRESS. It was a sentence above the buttons explaining
  // that the grain is untouched and only the overall level moves — true, and read once. The
  // two things worth knowing at the moment of pressing are which colour and which faces, and
  // those belong on the button: it names its own effect, and the paragraph above it went.
  'notice.3d.levelEdit': ['a face evened out', 'une face égalisée'],
  // What the edit rail calls one, singular and counted.
  'notice.3d.retouchEdit': ['a reflection', 'un reflet'],
  'notice.3d.retouchEditMany': ['reflections', 'reflets'],
  // TWO PRESSES. "Apply" keeps the correction and leaves the panel open for the next one;
  // "Save" writes the picture they arrived at into the mesh and closes. The old label said
  // "apply to the mesh" for what is now the second of those.
  // The plain label, kept for the moment before there is a zone or a face to name — where
  // the button is disabled anyway. Named presses are the two below and levelApplyTo above.
  // A RECTANGLE IS NAMED TOO: the red badge on the picture and the row that sets its
  // strength both number it, so the press can say which ones it is about. Only the zones at
  // a strength above zero are counted — a zone left at zero is a rectangle being kept, not a
  // correction being made.
  // ("{n} kept, not saved yet" lived here. The rail under the pictures shows every one of
  // them as a dot, and the Undo button beside Save names the last — so the count was a third
  // telling of what two controls already say, in a panel that is short of height.)
  // ONE ENTRY FOR A SITTING, named by what went into it.
  'notice.3d.textureEdit': ['corrections to the picture', 'corrections de l’image'],
  'notice.3d.levelEditMany': ['faces evened out', 'faces égalisées'],
  'notice.3d.generate': ['Generate the 3D model', 'Générer le modèle 3D'],
  'notice.3d.settingsRow': ['Pictures & settings', 'Images et réglages'],
  // Phase 2's selected listing crops, sent after the product's photographs — see productPartCrops.
  'notice.3d.includeCrops': ['Include parts crops', 'Inclure les découpes des pièces'],
  'notice.3d.includeCropsNone': [
    'No selected crops to send: run the Selection in Phase 2 first.',
    "Aucune découpe retenue à envoyer : lancez d'abord la Sélection en phase 2.",
  ],
  // While the run is in flight: on the button with the seconds counting after it, and in
  // the empty viewport where the mesh will land. One string for both, because they are one
  // fact said in the two places somebody is looking at while they wait.
  'notice.3d.generating': ['Generating the 3D model…', 'Génération du modèle 3D…'],
  'notice.3d.repainting': ['Applying the materials…', 'Application des matériaux…'],
  // THE SAME FACT UNDER A 96-PIXEL TILE, where the sentence above does not fit — see
  // MeshRunStrip's PendingThumb. The strip's other labels are "#95", so this one is a word
  // where they are a number, and that is the whole of what it has to say.
  'notice.3d.generatingShort': ['making…', 'en cours…'],
  // AND THE SAME ROW UNDER A MODEL THAT READS EVERY PICTURE. A reconstructor's endpoint
  // takes one image, so its photographs are alternatives; a model being shown photographs
  // has no such limit, and the ones after the first are what say the things the first
  // cannot — the back of a leg, how deep a seat is. The order is the order they were
  // pressed, and the number on each tile is the number the prompt calls it by.
  'notice.3d.pickInputs': [
    'Click the photographs to model from, in the order they should be read. The first is the one measured against.',
    'Cliquez les photos à modéliser, dans l’ordre où elles doivent être lues. La première est celle qui sert de référence.',
  ],
  'notice.3d.noPhotos': [
    'No part photo is in the database for this submodel yet. Add them in Phase 2, with the switch under each generated image.',
    "Aucune photo de pièce n'est dans la base pour ce sous-modèle. Ajoutez-les en phase 2, avec l'interrupteur sous chaque image générée.",
  ],
  'notice.3d.noKey': [
    'No OpenAI key is configured on the server.',
    "Aucune clé OpenAI n'est configurée sur le serveur.",
  ],
  // WHICH ENGINE MAKES THE PRODUCT'S MESH — the dropdown over Phase 5's generate button.
  'notice.3d.engine': ['How to make the model', 'Comment fabriquer le modèle'],
  // The first dot on the history: the mesh as it arrived, before anything was done
  // to it. Everything after it is something done TO it.
  'notice.3d.origin': ['initial version', 'version initiale'],
  // The ringed dot on the history: where the mesh actually is. Every Apply lands here.
  'notice.3d.currentNode': [
    'the mesh as it stands; a change applies here',
    'le maillage tel qu’il est ; une modification s’applique ici',
  ],
  // Pressing a dot on the trunk shows the mesh as it stood there. It is a LOOK and not an
  // undo: the copy was taken when the next change overwrote it, so it has been on disk all
  // along and fetching it moves nothing.
  // THE TWO THINGS A DOT OFFERS, in the little menu pressing one opens. Verbs, because each
  // is something about to happen to the mesh — and short ones, because the menu hangs under
  // a dot in a row of them and must not cover its neighbours.
  'notice.3d.dotSee': ['See', 'Voir'],
  'notice.3d.dotHide': ['Hide', 'Cacher'],
  'notice.3d.dotRedo': ['Redo', 'Refaire'],
  'notice.3d.dotDelete': ['Delete', 'Supprimer'],
  'notice.3d.dotClose': ['close this menu', 'fermer ce menu'],
  // Shown under the history while the viewer is on an earlier state, because every control
  // below it still acts on the mesh as it STANDS.
  //
  // Only once there is a NUMBER to say. At the origin there is none — "as it was made" was a
  // sentence restating what the dot at the far left of the rail already says, in the row that
  // exists to carry the way back out of it.
  'notice.3d.viewingAfter': [
    'You are looking at the mesh after {n} change(s).',
    'Vous regardez le maillage après {n} modification(s).',
  ],
  'notice.3d.viewingBack': ['back to the current mesh', 'revenir au maillage actuel'],
  // THE SAME TWO SENTENCES FOR THE RETOUCH PANEL'S OWN RAIL, where what you are looking back
  // at is a PICTURE this sitting has not saved yet — not the mesh, which has not moved.
  // Close enough to share the strings and wrong enough that sharing them would misname the
  // thing on screen, which is the one job a sentence like this has.
  // A transformation WRITTEN for one mesh, rather than chosen off the list of two.
  // It is applied, undoable, and can be kept — see mesh_script.py for what one may
  // contain and MeshControls for where the keeping is offered.
  'notice.3d.transformation': ['transformation', 'transformation'],
  'notice.3d.scriptFailed': [
    'That transformation could not be applied.',
    'Cette transformation n\u2019a pas pu \u00eatre appliqu\u00e9e.',
  ],
  // The images packed inside the .glb — the colour a reconstruction is painted with,
  // and whatever else was baked beside it. ".glb" is in the label on purpose: it says
  // these are not the part's photographs, which are a gallery three panels up.
  // EDIT, not see. The button opens the correction workbench now, and nobody opens a
  // reconstruction's atlas to look at it — it is a flayed square in which nothing is
  // recognisable, and the only reason to have one on screen is to change it.
  'notice.3d.failed': ['This mesh could not be made.', 'Ce maillage n\u2019a pas pu \u00eatre cr\u00e9\u00e9.'],
  // Stop pressed on Find holes: the call already out is finishing, and is kept.
  // Phase 6-3: the holes by Find holes group, or by part.
  'notice.joints.back': ['Back to the joints', 'Retour aux assemblages'],
  'notice.assembly.groupComponents': ['Components', 'Composants'],
  'notice.assembly.groupHardware': ['Hardware', 'Quincaillerie'],
  'notice.assembly.groupCovers': ['Covers', 'Caches'],
  'notice.joints.pending': ['pending', 'en attente'],
  'notice.joints.toCheck': ['{n} to check', '{n} à vérifier'],
  'notice.joints.expected': ['Holes', 'Trous'],
  'notice.joints.foundCol': ['Status', 'État'],
  'notice.joints.position': ['Position', 'Position'],
  'notice.joints.measured': ['Measured', 'Mesuré'],
  'notice.joints.notPlaced': ['not placed', 'non placé'],
  'notice.joints.untimed': ['no sub-step says which side it is on, so it is not timed', 'aucune sous-étape ne dit de quel côté il se trouve : il n’est pas minuté'],
  'notice.joints.depthDeep': ['{mm} mm deep', 'profond de {mm} mm'],
  'notice.joints.foundOf': ['found {n} of {m}', '{n} trouvé(s) sur {m}'],
  'notice.joints.foundThrough': ['found through', 'trouvé traversant'],
  'notice.joints.foundBlind': ['found blind', 'trouvé borgne'],
  'notice.joints.noReach': ['does not reach {part}', 'n’atteint pas {part}'],
  'notice.joints.drilling': ['drilling', 'perçage'],
  'notice.joints.rollingBack': ['rolling back', 'retour en arrière'],
  'notice.joints.drillNote': ['{part} — drilling: {what}', '{part} — perçage : {what}'],
  'notice.joints.waiting': ['waiting for Component holes to be open', 'en attente de l’ouverture de Trous des pièces'],
  'notice.joints.runAll': ['Every joint', 'Tous les assemblages'],
  'notice.joints.runOne': ['Run 1 joint', 'Lancer 1 assemblage'],
  'notice.joints.runMany': ['Run {n} joints', 'Lancer {n} assemblages'],
  'notice.joints.assembled': ['Assembled product', 'Produit assemblé'],
  'notice.joints.cover': ['Cover', 'Cache'],
  'notice.joints.coverExpected': ['Expected', 'Attendus'],
  'notice.joints.coverPlaced': ['Placed', 'Placés'],
  'notice.joints.coverUnplaced': ['not placed yet', 'pas encore placés'],
  'notice.joints.coverInto': ['{mm} mm into {part}', 'entre de {mm} mm dans {part}'],
  'notice.joints.noLines': ['The joints table lists no holes for this joint.', 'La table des joints ne liste aucun trou pour cet assemblage.'],
  'notice.joints.extra': ['Found, and not in the joints table:', 'Trouvés, et absents de la table des joints :'],
  'notice.joints.none': ['No drilling sub-step yet: analyse the steps first.', 'Aucune sous-étape de perçage : analysez d’abord les étapes.'],
  'notice.joints.noHoles': ['Drilling holes has placed nothing for this joint yet.', 'Le perçage des trous n’a encore rien placé pour cet assemblage.'],
  'notice.joints.noModels': ['None of these parts has a model cut from the whole product.', 'Aucune de ces pièces n’a de modèle découpé dans le produit entier.'],
  'notice.joints.openPart': ['Open this part, to drill it', 'Ouvrir cette pièce, pour la percer'],
  'notice.holes.stopping': ['Stopping after this call', 'Arrêt après cet appel'],
  'notice.3d.grained': ['Wood laid on', 'Bois appliqué'],
  'notice.3d.piecesEdit': ['Pieces on their materials', 'Pièces sur leurs matériaux'],
  'notice.3d.pass': ['pass {n}/{of}', 'passe {n}/{of}'],
  // A pass's versions: the pass itself, then each rebuild of it at other sizes.
  'notice.3d.version': ['version {n}/{of}', 'version {n}/{of}'],
  'notice.3d.passes': ['{n} passes', '{n} passes'],
  // THE PASS DROPDOWN, over the generate button. "{n} passes" above is a run's LABEL —
  // what it turned out to be — and these are the choice made before it runs, which is why
  // one of them is singular: "1 passes" is the sort of thing a label built by replacement
  // says when nobody looked.
  'notice.3d.passOne': ['1 pass', '1 passe'],
  'notice.3d.passMany': ['{n} passes', '{n} passes'],
  'notice.3d.passCount': ['How many passes', 'Nombre de passes'],
  // How hard the written model thinks — OpenAI's reasoning effort, one label per level.
  'notice.3d.effortLabel': ['How hard it thinks', 'Effort de réflexion'],
  'notice.3d.effort.none': ['No reasoning', 'Sans raisonnement'],
  'notice.3d.effort.low': ['Low reasoning', 'Raisonnement faible'],
  'notice.3d.effort.medium': ['Medium reasoning', 'Raisonnement moyen'],
  'notice.3d.effort.high': ['High reasoning', 'Raisonnement élevé'],
  'notice.3d.effort.xhigh': ['Extra-high reasoning', 'Raisonnement très élevé'],
  'notice.3d.effort.max': ['Max reasoning', 'Raisonnement maximal'],
  // Where a written run is, under its button: the pass, and the stage of it.
  'notice.3d.progressPass': ['Pass {n} of {of}', 'Passe {n} sur {of}'],
  'notice.3d.passTotal': ['{t} in total', '{t} au total'],
  'notice.3d.stage.queued': ['Waiting for OpenAI', 'En attente d\u2019OpenAI'],
  'notice.3d.stage.thinking': ['Thinking and writing the script', 'R\u00e9flexion et \u00e9criture du script'],
  'notice.3d.stage.building': ['Building in Blender', 'Construction dans Blender'],
  'notice.3d.stage.saving': ['Saving', 'Enregistrement'],
  'notice.3d.stage.fixing': ['Fixing the script Blender stopped on', 'Correction du script arrêté par Blender'],
  // A try at making a pass run, on the run's badge (see FixPanel).
  'notice.3d.writtenFixRow': ['Pass {p}: fix {n}', 'Passe {p} : correction {n}'],
  'notice.3d.writtenFixError': ['Blender stopped on', 'Blender s\'est arrêté sur'],
  'notice.3d.writtenFixScript': ['The script it was running', 'Le script en cours'],
  'notice.3d.writtenFixThought': ['Asked to fix it, the model thought', 'Pour le corriger, le modèle a pensé'],
  // The dimensions a written script opens with, and rebuilding at changed ones.
  // A written run's build, measured and held against the drawing, with nothing found.
  'notice.3d.checksPassed': ['Size and shape checks passed', 'V\u00e9rifications de taille et de forme r\u00e9ussies'],
  'notice.3d.dimsTitle': ['Dimensions', 'Dimensions'],
  // Where two parts of a written product fill the same space — see ProductOverlaps.
  // A whole product modelled elsewhere, brought in from a file — see ProductImport.
  'notice.import.badge': ['Import 3D model', 'Importer un mod\u00e8le 3D'],
  'notice.import.intro': ['Accepted formats: .glb, .gltf, .obj, .stl or .fbx, up to 100 MB.', 'Formats accept\u00e9s : .glb, .gltf, .obj, .stl ou .fbx, jusqu\u2019\u00e0 100 Mo.'],
  'notice.import.newPart': ['New part', 'Nouvelle pi\u00e8ce'],
  'notice.import.newPartName': ['Part name', 'Nom de la pi\u00e8ce'],
  'notice.import.wholeOption': ['The whole product', 'Le produit entier'],
  'notice.import.wholeSaid': ['Parts not separated: only the chosen object is imported.', 'Pi\u00e8ces non s\u00e9par\u00e9es : seul l\u2019objet choisi est import\u00e9.'],
  'notice.import.saveWhole': ['Import the whole product', 'Importer le produit entier'],
  'notice.import.wholesSaid': ['Parts not separated: each chosen object becomes a model of its own.', 'Pi\u00e8ces non s\u00e9par\u00e9es : chaque objet choisi devient un mod\u00e8le \u00e0 part.'],
  'notice.import.saveWholes': ['Import {n} whole products', 'Importer {n} produits entiers'],
  'notice.import.choose': ['Choose a file', 'Choisir un fichier'],
  'notice.import.another': ['Import another file', 'Importer un autre fichier'],
  'notice.import.reading': ['Reading {file}', 'Lecture de {file}'],
  'notice.import.failed': ['The file could not be imported.', 'Le fichier n\u2019a pas pu \u00eatre import\u00e9.'],
  'notice.import.whole': ['{file}, {size} overall', '{file}, {size} au total'],
  'notice.import.matched': ['{n} of {m} objects matched to a part', '{n} objets sur {m} associ\u00e9s \u00e0 une pi\u00e8ce'],
  'notice.import.object': ['Object in the file', 'Objet du fichier'],
  'notice.import.size': ['Size', 'Taille'],
  'notice.import.part': ['Part', 'Pi\u00e8ce'],
  'notice.import.none': ['Not a part', 'Pas une pi\u00e8ce'],
  'notice.import.save': ['Name the parts', 'Nommer les pi\u00e8ces'],
  'notice.overlaps.title': ['Collisions', 'Collisions'],
  'notice.overlaps.fix': ['Fix collisions', 'Corriger les collisions'],
  'notice.overlaps.fixing': ['Fixing collisions', 'Correction des collisions'],
  'notice.overlaps.fixFailed': ['The collisions could not be fixed.', 'Les collisions n\u2019ont pas pu \u00eatre corrig\u00e9es.'],
  'notice.overlaps.fixPrompt': ['What the Fix collisions call sends', 'Ce qu\u2019envoie l\u2019appel Corriger les collisions'],
  'notice.overlaps.render': ['Render', 'Rendu'],
  'notice.overlaps.stageFixing': ['rewriting', 'r\u00e9\u00e9criture'],
  'notice.overlaps.stageFitting': ['fitting', 'ajustement'],
  'notice.overlaps.stageChecking': ['checking', 'v\u00e9rification'],
  'notice.overlaps.none': ['No parts overlap', 'Aucune pi\u00e8ce ne se chevauche'],
  'notice.overlaps.foundOne': ['1 overlap between parts', '1 chevauchement entre pi\u00e8ces'],
  'notice.overlaps.found': ['{n} overlaps between parts', '{n} chevauchements entre pi\u00e8ces'],
  'notice.overlaps.unchecked': ['Not checked for overlaps', 'Chevauchements non v\u00e9rifi\u00e9s'],
  'notice.overlaps.checking': ['Checking the parts for overlaps\u2026', 'V\u00e9rification des chevauchements\u2026'],
  'notice.overlaps.parts': ['Parts', 'Pi\u00e8ces'],
  'notice.overlaps.depth': ['How deep', 'Profondeur'],
  'notice.overlaps.shared': ['Shared space (x × y × z)', 'Volume commun (x × y × z)'],
  'notice.3d.materialsTitle': ['Materials', 'Mat\u00e9riaux'],
  // The numbers a material was measured to have — see texture_stats.
  'notice.3d.texContrast': ['coarseness', 'grain'],
  'notice.3d.texChroma': ['colour spread', '\u00e9tendue de teinte'],
  'notice.3d.texAniso': ['directional', 'directionnel'],
  'notice.3d.texPeriod': ['Period', 'P\u00e9riode'],
  'notice.3d.texMap': ['Texture map', 'Carte de texture'],
  // How big one repeat of the texture map is on the real object.
  'notice.3d.texTileSize': ['Tile', 'Motif'],
  // The numbers the texture map is drawn from, in their own column beside it.
  'notice.3d.texValues': ['Texture values', 'Valeurs de texture'],
  'notice.3d.texGrain': ['grain', 'veinage'],
  'notice.3d.texMottle': ['mottle', 'moucheture'],
  'notice.3d.texKept': ['clean', 'net'],
  'notice.3d.noApplied': [
    'No materials applied to this submodel: its models stay grey. Apply a reading in Phase 3.',
    'Aucun mat\u00e9riau appliqu\u00e9 \u00e0 ce sous-mod\u00e8le : ses mod\u00e8les restent gris. Appliquez une lecture en phase 3.',
  ],
  'notice.3d.matShow': [
    'Show this reading\u2019s numbers', 'Afficher les valeurs de cette lecture',
  ],
  'notice.3d.matApply': [
    'Apply to the 3D models', 'Appliquer aux mod\u00e8les 3D',
  ],
  'notice.3d.matTook': ['took {t}', 'a pris {t}'],
  'notice.3d.matApplying': ['Applying…', 'Application…'],
  'notice.3d.matApplyAgain': [
    'Apply again', 'R\u00e9appliquer',
  ],
  'notice.3d.matIsApplied': [
    'This is what every 3D model of this colourway wears \u2014 the whole product and every component part.',
    'C\u2019est ce que portent tous les mod\u00e8les 3D de ce coloris \u2014 le produit entier et chaque pi\u00e8ce.',
  ],
  'notice.3d.matPatches': ['{n} patch(es)', '{n} plage(s)'],
  // A reading whose answer put no part on any material: the parts are left alone.
  'notice.3d.matRunCurrent': ['Selected submodel', 'Sous-modèle sélectionné'],
  'notice.3d.matRunAll': ['All submodels', 'Tous les sous-modèles'],
  'notice.3d.matRunPartsOnly': ['Component parts only', 'Pièces seules'],
  'notice.3d.matRunWithHardware': ['Parts, hardware & tools', 'Pièces, quincaillerie & outils'],
  'notice.3d.matSubsMissing': ['Not read (no photograph of a known side, or no material in the listing): {s}', 'Non lus (aucune photo d’une face connue, ou aucun matériau dans l’annonce) : {s}'],
  'notice.3d.matNoPhotos': ['No saved photo of a known side: {s}', 'Aucune photo enregistrée d’une face connue : {s}'],
  'notice.3d.matNoMaterials': ['No material in the listing: {s}', 'Aucun matériau dans l’annonce : {s}'],
  'notice.3d.matNoParts': [
    'The reading assigned no part to a material, so the parts were left as they were.',
    'La lecture n\u2019a attribu\u00e9 aucune pi\u00e8ce \u00e0 un mat\u00e9riau : les pi\u00e8ces sont rest\u00e9es telles quelles.',
  ],
  // The patches call's answer, in the materials badge's panel.
  'notice.3d.matPatchesTitle': ['What each material was measured off', 'Ce sur quoi chaque mati\u00e8re a \u00e9t\u00e9 mesur\u00e9e'],
  // Deleting one reading from the list. Never the applied one (see deleteReading).
  'notice.3d.matDelete': ['Delete this reading', 'Supprimer cette lecture'],
  'notice.3d.matDeleteGo': ['Delete', 'Supprimer'],
  'notice.3d.matDeleteCancel': ['Cancel', 'Annuler'],
  'notice.3d.matApplied': ['Applied to {n} 3D model(s){s}', 'Appliqu\u00e9 \u00e0 {n} mod\u00e8le(s) 3D{s}'],
  'notice.3d.matSkipped': [
    '{n} left alone: nothing in it is named by this reading',
    '{n} laiss\u00e9(s) de c\u00f4t\u00e9 : cette lecture n\u2019en nomme aucun mat\u00e9riau',
  ],
  'notice.3d.matApplyFailed': [
    'The models could not be repainted.', 'Les mod\u00e8les n\u2019ont pas pu \u00eatre repeints.',
  ],
  'notice.3d.matBadge': ['Components', 'Pi\u00e8ces'],
  'notice.3d.matCalls': ['Materials', 'Mat\u00e9riaux'],
  'notice.3d.matReading': [
    'Reading\u2026', 'Lecture en cours\u2026',
  ],
  'notice.3d.matFailed': [
    'The materials could not be read.', 'Les mat\u00e9riaux n\u2019ont pas pu \u00eatre lus.',
  ],
  'notice.3d.textureStale': [
    'Close-ups found since this reading. Read again to measure the textures.',
    'Gros plans trouv\u00e9s depuis cette lecture. Relisez pour mesurer les textures.',
  ],
  'notice.3d.textureNone': [
    'No part close-ups to measure textures on. Run the listing crops in Phase 2, then read again.',
    'Aucun gros plan de pi\u00e8ce pour mesurer les textures. Lancez les d\u00e9tails annonce en phase 2, puis relisez.',
  ],
  'notice.3d.materialParts': ['Parts', 'Pi\u00e8ces'],
  'notice.3d.materialNotGiven': ['not on the listing', 'absent de l\u2019annonce'],
  'notice.3d.materialPartOf': ['which part', 'quelle partie'],
  'notice.3d.materialOnNothing': ['on no part', 'sur aucune pi\u00e8ce'],
  'notice.3d.dimsRebuild': ['Rebuild at these sizes', 'Reconstruire \u00e0 ces dimensions'],
  'notice.3d.dimsRebuilding': ['Rebuilding\u2026', 'Reconstruction\u2026'],
  // WHERE AN OLD MODEL'S DIMENSIONS ARE MEASURED — a small call, saved as a new version.
  'notice.3d.linesAdd': ['Draw where each is measured', 'Tracer où chaque cote est mesurée'],
  'notice.3d.linesAdding': ['Drawing the measurements…', 'Tracé des cotes…'],
  'notice.3d.linesFailed': ['The measurement lines could not be added.', 'Les lignes de cote n’ont pas pu être ajoutées.'],
  'notice.3d.dimsFailed': ['The model could not be rebuilt.', 'Le mod\u00e8le n\u2019a pas pu \u00eatre reconstruit.'],
  'notice.3d.paintNoColour': [
    'No colour sampled for this material, so its meshes are left unpainted.',
    'Aucune couleur relevée pour ce matériau : ses maillages restent non peints.',
  ],
  'notice.3d.buildNoSurface': [
    'This material has no colour and surface yet, so the tool cannot be built with it — read the materials first:',
    'Ce matériau n\u2019a pas encore de couleur ni de surface : l\u2019outil ne peut pas être construit avec — lancez d\u2019abord la lecture des matériaux :',
  ],
  'notice.3d.paintNoMaterial': [
    'This part has no material, so its mesh cannot be given one.',
    'Cette pièce n\u2019a pas de matériau : impossible d\u2019en donner un à son maillage.',
  ],
  // ONE ROW PER PASS on the written badge's menu, each holding that call's question and its
  // answer — numbered where a run has several, plain where it made one.
  'notice.3d.writtenPassRow': [
    'Prompt, Reasoning & output (Pass {n})',
    'Prompt, raisonnement & réponse (Passe {n})',
  ],
  'notice.3d.writtenRebuiltRow': [
    'Script & output (Pass {p}, version {n}, rebuilt)',
    'Script & sortie (Passe {p}, version {n}, reconstruite)',
  ],
  'notice.3d.writtenShotPhoto': ['The photograph, again', 'La photo, à nouveau'],
  'notice.3d.writtenShotDraft': [
    'The first attempt, {v}',
    'Le premier essai, {v}',
  ],
  'notice.3d.writtenSecondLabel': [
    'Prompt & renders sent to correct the first attempt',
    'Prompt & rendus envoyés pour corriger le premier essai',
  ],
  'notice.3d.writtenNextCorrection': [
    'Prompt sent to correct each pass after the first',
    'Prompt envoyé pour corriger chaque passe après la première',
  ],
  'notice.3d.writtenPrompt': [
    'Prompt & photograph sent to model this',
    'Prompt & photo envoyés pour modéliser ceci',
  ],
  'notice.3d.writtenDifferences': [
    'What it found different from the photographs',
    'Ce qu’il a trouvé différent des photos',
  ],
  'notice.3d.writtenAnswer': [
    'What the model thought and wrote',
    'Ce que le modèle a pensé et écrit',
  ],
  'notice.3d.boxGenerate': ['3D model generation', 'Génération du modèle 3D'],
  'notice.3d.genScope.product': ['product', 'produit'],
  'notice.3d.genScope.hardware': ['hardware', 'quincaillerie'],
  'notice.3d.boxView': ['3D model', 'Modèle 3D'],
  'notice.3d.expand': ['Open the 3D model bigger', 'Agrandir le modèle 3D'],
  'notice.3d.downloadGlb': ['Download the GLB file', 'Télécharger le fichier GLB'],
  // AIMING THE VIEW BY PRESSING RATHER THAN BY DRAGGING.
  // WHERE IT IS AIMED, not where it stands — and the row is titled that way because the two
  // are a whole framing distance apart. Called "camera position", a reading of 0, 0, 0 looks
  // like a camera sitting inside the gizmo, when what it means is a camera looking straight
  // at it from wherever the zoom has put it.
  'notice.3d.viewPosRow': ['Looking at (cm)', 'Point visé (cm)'],
  'notice.3d.viewRotRow': ['Camera orientation (°)', 'Orientation caméra (°)'],
  'notice.3d.viewZoom': [
    'How far in, as a multiple of the home view',
    'Zoom, en multiple de la vue de référence',
  ],
  'notice.3d.viewHome': [
    'Back to the starting view',
    'Revenir à la vue de départ',
  ],
  // A MENU NOW, AND NOT A FLIP. Half a turn shows the back, which is one of the two things
  // worth pressing a button for; the other is a quarter turn, which is the next SIDE — and
  // that was a drag, aimed by hand, landing near the face rather than on it.
  //
  // On the letters alone, which is what a viewer with no readings under them shows. Where the
  // angle is printed it is typed into instead, and a number needs no tooltip to say that.
  'notice.3d.viewTurn': [
    'Turn about {axis}',
    'Tourner autour de {axis}',
  ],
  'notice.3d.noMesh': [
    'No 3D model for this part yet.',
    "Pas encore de modèle 3D pour cette pièce.",
  ],
  // The parts' runs could not be listed: a failure, not "no mesh" (see ThreeDStep's `jobs`).
  'notice.3d.jobsFailed': [
    'The parts’ 3D models could not be loaded. Reload the page to try again.',
    'Les modèles 3D des pièces n’ont pas pu être chargés. Rechargez la page pour réessayer.',
  ],
  // The empty frame is a way in, and a frame that does not say so is a frame nobody
  // presses. It is the same click as on a mesh that exists — which is where every
  // measurement in this phase is edited.
  'notice.3d.noMeshOpen': [
    'Open it to give it its measurements.',
    'Ouvrir pour saisir ses mesures.',
  ],
  'notice.3d.addToDb': ['Add to database', 'Ajouter à la base'],
  // A run that is not a reconstruction: the mesh of what a sub-step MAKES, baked out of
  // Phase 5's viewer once its movement was signed off.
  'notice.3d.inDb': ['In the database', 'Dans la base'],
  'notice.3d.dbFailed': [
    'The database could not be updated.',
    "La base n'a pas pu être mise à jour.",
  ],
  // UNDER THE PRODUCT'S DATABASE SWITCH, and only where the press did something the switch
  // itself cannot show: components taken back out, components named by no part of the list,
  // a mesh that could not be cut up. A press that worked says nothing. See _keep_pieces.
  'notice.3d.piecesDropped': [
    '{n} component(s) are no longer kept as their parts’ 3D models.',
    '{n} composant(s) ne sont plus conservés comme modèles 3D de leurs pièces.',
  ],
  'notice.3d.piecesSkipped': [
    'Named by no part of the list: {names}',
    'Ne correspondent à aucune pièce de la liste : {names}',
  ],
  'notice.3d.piecesFailed': [
    'Its components could not be cut out: {why}',
    "Ses composants n’ont pas pu être extraits : {why}",
  ],
  // ON A MESH CUT OUT OF A WHOLE PRODUCT'S RUN, in the part's own strip: why it carries a
  // number the part itself never ran.
  'notice.3d.fromProduct': ['from the whole product', 'du produit entier'],
  'notice.3d.fromSet': ['from the parts built together', 'des pièces construites ensemble'],
  'notice.step3.generateNew': ['Generate', 'Générer'],
  'notice.step3.promptUsed': ['Instructions given', 'Instructions données'],
  'notice.step3.promptNone': [
    'None — generated without instructions.',
    'Aucune — générée sans instructions.',
  ],
  'notice.step3.contextNone': ['No product photo was sent.', 'Aucune photo produit envoyée.'],
  'notice.step3.contextUnknown': [
    'Not recorded for this image.',
    'Non enregistré pour cette image.',
  ],
  'notice.step3.deleteImage': ['Delete', 'Supprimer'],
  'notice.step3.deleteConfirm': ['Delete this image?', 'Supprimer cette image ?'],
  'notice.step3.regenPrevious': ['Previous attempt', 'Image précédente'],
  'notice.step3.regenPlaceholder': [
    'What to fix / instructions (optional)…',
    'Ce qui ne va pas / instructions (optionnel)…',
  ],
  'notice.step3.genPlaceholder': [
    'Instructions for this image (optional)…',
    'Instructions pour cette image (optionnel)…',
  ],
  'notice.step3.regenIncludePrev': [
    'Include the previous image (to correct it)',
    "Inclure l'image précédente (pour la corriger)",
  ],
  'notice.step3.regenClearBox': ['Clear the zones', 'Effacer les zones'],
  // One of them, on the zone that is selected. The keystroke that has always done it
  // (Delete) stays; this is the version somebody can find without being told.
  'notice.step3.regenDropBox': ['Remove this zone', 'Supprimer cette zone'],
  'notice.step3.regenPlaceholderBox': [
    'The change to make in the box (e.g. add a hole)…',
    'Ce qu\'il faut changer dans la zone (ex : ajouter un trou)…',
  ],
  'notice.step3.angle': ['Angle', 'Angle'],
  'notice.step3.angleNone': ['None', 'Aucun'],
  'notice.step3.generated': ['Generated image', 'Image générée'],
  // The part selector's mark in the video pipeline, where a part is done once it has
  // been drawn. (In the 3D one the mark is about its model, and reads notice.3d.inDb.)
  // IN THE BOX, OR NOT. Almost always yes — the parts list is read off the page that says
  // what the carton holds — and the row exists for the case that page cannot describe: a
  // tool a step calls for and nobody packed. "Not supplied" is the news, so it is the one
  // that wears a colour.
  // EVERY OTHER DRAWING OF THE PART: the steps draw it again, from the angles the steps
  // needed, and the supplied-parts page only ever had one.
  'notice.parts.cropOff': ['Not this part — strike it out', 'Pas cette pièce — barrer'],
  'notice.parts.cropBack': ['Put it back', 'Rétablir'],
  'notice.parts.supplied': ['Supplied', 'Fourni'],
  'notice.parts.suppliedYes': ['Supplied', 'Fourni'],
  'notice.parts.suppliedNo': ['Not supplied', 'Non fourni'],
  'notice.parts.suppliedPick': [
    'In the box, or something the reader has to find themselves',
    'Dans le carton, ou à trouver soi-même',
  ],
  'notice.parts.drawn': ['Photographed', 'Photographiée'],
  'notice.parts.notDrawn': ['Not photographed yet', 'Pas encore photographiée'],
  'notice.step3.addToDb': ['Add to database', 'Ajouter à la base'],
  'notice.step3.addedToDb': ['Added to database', 'Ajouté à la base'],
  'notice.step3.context': ['Context images', 'Images de contexte'],
  'notice.step3.schemas': ['Drawings in the steps', 'Dessins dans les étapes'],
  'notice.step4.submodelFilter': ['Submodel', 'Sous-modèle'],
  'notice.step3.contextOn': [
    'Selected — click to remove from context',
    'Sélectionnée — cliquer pour retirer du contexte',
  ],
  'notice.step3.contextOff': [
    'Not used — click to add to context',
    'Non utilisée — cliquer pour ajouter au contexte',
  ],
  'notice.step3.promptTitle': ['Prompt sent to the generator', 'Prompt envoyé au générateur'],
  'notice.step3.promptWithProduct': [
    'When a finished-product photo is attached',
    'Quand une photo du produit fini est jointe',
  ],
  'notice.step3.promptNoProduct': ['When there is no product photo', 'Sans photo du produit'],
  'notice.step3.promptHardware': ['For hardware / a tool', 'Pour de la quincaillerie / un outil'],
  'notice.step3.promptContext': ['Per-part context appended', 'Contexte ajouté pour chaque pièce'],
  'notice.step3.promptContextNote': [
    'Each line below is added only when the part has that detail:',
    "Chaque ligne ci-dessous n'est ajoutée que si la pièce a ce détail :",
  ],
  'notice.step3.blockedOne': [
    'image blocked from unwanted sources.',
    "image bloquée d'une source indésirable.",
  ],
  'notice.step3.blockedMany': [
    'images blocked from unwanted sources.',
    'images bloquées de sources indésirables.',
  ],
  'notice.lib.new': ['New manual', 'Nouvelle notice'],
  // A folder with no manual, for a product somebody has a 3D model of (see createFolder).
  'notice.lib.newFolder': ['New folder', 'Nouveau dossier'],
  'notice.lib.folderBrand': ['Brand', 'Marque'],
  'notice.lib.folderName': ['Product name', 'Nom du produit'],
  'notice.lib.folderCreate': ['Create the folder', 'Cr\u00e9er le dossier'],
  'notice.lib.folderFailed': ['The folder could not be created.', 'Le dossier n\u2019a pas pu \u00eatre cr\u00e9\u00e9.'],
  'notice.lib.empty': [
    'No manuals yet. Start one with New manual, above.',
    'Aucune notice pour le moment. Commence avec Nouvelle notice, ci-dessus.',
  ],
  'notice.lib.remove': ['Remove', 'Supprimer'],
  'notice.lib.removeConfirm': [
    'Remove "{name}" from this device?',
    'Supprimer « {name} » de cet appareil ?',
  ],
};

// Which slot a language reads. The tuple order is [en, fr], stated once here rather than
// indexed by hand at three call sites.
const SLOT: Record<Language, 0 | 1> = { en: 0, fr: 1 };

const lookup = (lang: Language, key: string): string =>
  translations[key]?.[SLOT[lang]] || translations[key]?.[0] || key;

// Translate a key in a SPECIFIC language, independent of the current UI language —
// for text whose audience is fixed rather than the reader, e.g. the always-English
// prompt sent to the video generator.
export function translateIn(lang: Language, key: string): string {
  return lookup(lang, key);
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// THE BROWSER'S LANGUAGE, as a two-letter code ("fr", "es"), whether or not the page speaks it:
// the public menu marks it "(detected)" even on a language still greyed.
export const browserLanguage = (): string =>
  (navigator.language || navigator.languages?.[0] || '').slice(0, 2).toLowerCase();

// WHERE THE APP'S CHOICE IS KEPT. The public tutorial keeps none: it opens in the browser's
// language on every visit (Louis 2026-10-28), and its menu switches the language for that visit
// only. A kept choice had a reader who once tried English shown every page in English after it.
const persist = (lang: Language) => {
  if (!isPublicTutorialRoute()) localStorage.setItem('language', lang);
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Check URL param first (used when transferring between browsers)
    try {
      const params = new URLSearchParams(window.location.search);
      const urlLang = params.get('lang');
      if (urlLang === 'en' || urlLang === 'fr') {
        persist(urlLang);
        const url = new URL(window.location.href);
        url.searchParams.delete('lang');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        return urlLang;
      }
    } catch {}
    // The public tutorial page (QR target) follows the browser, whatever a past visit chose.
    const saved = isPublicTutorialRoute() ? null : localStorage.getItem('language');
    if (saved === 'en' || saved === 'fr') return saved;
    return browserLanguage() === 'fr' ? 'fr' : 'en';
  });

  // Keep <html lang> in sync so Google Sign-In (locale-aware) picks up our language.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    persist(lang);
  };

  const t = (key: string): string => lookup(language, key);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
